import {
  INTRADAY_DEFAULTS,
  type IntradayExitAction,
  POLICY_DEFAULTS,
  POLICY_LIMIT_NAMES,
  policyLimitKind,
} from "@atc/domain";
import {
  type Account,
  type AgentConfig,
  type AgentPerformance,
  type AgentRun,
  type AgentRunDetail,
  agentRunFinished,
  agentRunSettled,
  api,
  type DecidesOn,
  type ExitAtBroker,
  type Feed,
  type FeedSwitch,
  type Feeds,
  type IntradayConfig,
  type IntradayExit,
  type IntradayKind,
  type IntradayOptions,
  type LiveExitCopy,
  type MarketSummary,
  type Order,
  type Policy,
  type PolicyState,
  type RebalanceBookView,
  type RebalanceCompareSide,
  type RebalanceConfigView,
  type RebalanceDecisionView,
  type RebalanceFiltersView,
  type RebalanceLadderView,
  type RebalanceNext,
  type RebalanceStatsWire,
  type RebalanceTuneRequest,
  type SyncPlan,
  type SyncResult,
  type TuneRecordWire,
  type TuneRowWire,
  type TuneStatsWire,
} from "./api";
import { AGENT_STRATEGIES, CATALOG, type CommandName, MAX_REPLAY_SPEED } from "./catalog";
import { feedName, formatSpendUsd, formatUsd, pad, venueDateTime } from "./format";
import {
  type CommandDoc,
  type FlagSpec,
  optionsFor,
  parseFlags,
  renderHelpIndex,
  renderHelpPage,
  type ShellSets,
  suggest,
  TIME_IN_FORCE,
  usageOf,
} from "./spec";

/**
 * The backtest that reruns a tune's setting as the tune ran it: every flag the
 * grid held fixed, and nothing that is the default (M9/E11). Copying a command
 * that dropped `--at close` or `--market` tested a different rule.
 */
export function tuneBacktestLine(
  request: RebalanceTuneRequest,
  setting: { top: number; lookback: number; score: string; weight: string },
): string {
  const b = request.base;
  const flags: string[] = [
    `--top ${setting.top}`,
    `--lookback ${setting.lookback}`,
    `--score ${setting.score}`,
    `--weight ${setting.weight}`,
    `--every ${b.every}`,
  ];
  if (b.at && b.at !== "open") flags.push(`--at ${b.at}`);
  if (b.session !== undefined && b.session > 1) flags.push(`--session ${b.session}`);
  if (request.universe !== "default") flags.push(`--universe ${request.universe}`);
  if (request.exclude.length > 0) flags.push(`--exclude ${request.exclude.join(",")}`);
  if (b.stopPct !== undefined) flags.push(`--stop-pct ${b.stopPct}`);
  if (b.resizeBandPct !== undefined && b.resizeBandPct !== 20)
    flags.push(`--band ${b.resizeBandPct}`);
  if (b.investPct !== undefined && b.investPct !== 100) flags.push(`--invest-pct ${b.investPct}`);
  if (b.skipSpikePct !== undefined) flags.push(`--skip-spike ${b.skipSpikePct}`);
  if (b.market) flags.push(`--market ${b.market.on}:${b.market.sma}`);
  if (b.market && b.marketAction === "hold") flags.push("--market-action hold");
  if (request.capital !== undefined && request.capital !== null)
    flags.push(`--capital ${request.capital}`);
  if (request.cost_bps !== undefined && request.cost_bps !== 5)
    flags.push(`--cost-bps ${request.cost_bps}`);
  if (request.start) flags.push(`--start ${request.start}`);
  if (request.end) flags.push(`--end ${request.end}`);
  return `rebalance backtest ${flags.join(" ")}`;
}

/** A decimal money string in dollars and cents, "4000" as "4,000.00" — never more places. */
const centsOf = (decimal: string) => {
  const [whole = "0", frac = ""] = decimal.split(".");
  return formatUsd(`${whole}.${`${frac}00`.slice(0, 2)}`);
};

/**
 * The command set.
 *
 * Commands are pure-ish: they take parsed arguments and a small session
 * context, and return lines to print. Keeping them separate from the React
 * component means the grammar is testable without a DOM, and adding a command
 * in a later iteration touches this file only.
 */

export type Line = {
  kind: "out" | "err" | "note" | "echo";
  text: string;
  /**
   * A row of a fixed-width table, which must not wrap at any width.
   *
   * Marked at the source rather than inferred from leading spaces. Every table
   * here is space-padded text whose columns only line up while the row is one
   * line, so on a narrow screen the pane scrolls sideways instead (M4/E14).
   * Prose is left alone: a sentence that has to be swiped is worse than one
   * that wraps.
   */
  pre?: true;
  /**
   * A line a running command rewrites in place — `rebalance tune`'s progress.
   * The shell replaces the line carrying the same key rather than appending.
   */
  live?: string;
};

/**
 * `| undefined` is explicit on every field rather than relying on `?` alone.
 * Under `exactOptionalPropertyTypes`, `{ email?: string }` means "absent or a
 * string" and refuses an explicit `undefined` — but clearing a field on sign-out
 * is exactly what we need to express.
 */
export type Session = {
  email?: string | undefined;
  userId?: string | undefined;
  accountId?: string | undefined;
  accountName?: string | undefined;
  /** Set by `login` between sending a code and verifying it. */
  pendingEmail?: string | undefined;
  /**
   * Whether the terms of use are still outstanding for this user.
   *
   * The server's answer, from `whoami`, not a decision made here. The terminal
   * uses it to know when to ask and when to stay quiet; the block itself is the
   * API's, which refuses every other route until it is cleared.
   */
  agreementRequired?: boolean | undefined;
};

export type CommandContext = {
  session: Session;
  setSession: (patch: Partial<Session>) => void;
  /** Ask the caller to refresh the header. */
  refresh: () => void;
  /**
   * Ask the caller to re-read which feed is selected.
   *
   * Separate from `refresh`, which reads the account: the feed choice is behind
   * a session but not behind an account, and a trader chooses their data before
   * they open one — that is the order `M4/I12/T10` puts the steps in.
   */
  refreshFeed: () => void;
  /**
   * The live connection, reached through the shell rather than imported.
   *
   * A command that constructed its own socket would open a second one per tab,
   * and the tests would need a real WebSocket to run at all. Passing it in keeps
   * the grammar testable without a DOM, which is the property this file already
   * had and is worth keeping.
   */
  watch: (symbols: string[]) => void;
  unwatch: (symbols: string[]) => void;
  watching: () => string[];
  disconnect: () => void;
  /**
   * The chrome, reached the same way and for the same reason.
   *
   * A command that imported the setup registry would pull React components into
   * the grammar, and `setups/registry.ts` would stop being the only place a
   * setup is named — adding a third one would touch this file too, which is
   * exactly what M3/I2 exists to prevent. `set*` returns false for an id the
   * shell does not know, so the command can say so rather than silently doing
   * nothing.
   */
  setups: () => readonly { id: string; title: string; current: boolean }[];
  setSetup: (id: string) => boolean;
  themes: () => readonly { id: string; title: string; current: boolean }[];
  setTheme: (id: string) => boolean;
  /**
   * Replay playback, owned by the shell for the same reason the socket is: a
   * command that held its own timer would keep running after the line that
   * started it returned, and nothing would be able to stop it.
   *
   * `stopReplay` returns whether anything was actually playing, so the command
   * can say "nothing was replaying" rather than claim to have stopped something.
   */
  startReplay: (spec: { date: string; speed: number; symbols: string[] }) => void;
  stopReplay: () => boolean;
  /**
   * The guided first run, owned by the shell for the reason the socket is: it
   * has to react to things that happen without a command — an order filling on
   * an alarm moves you to the last step, and no command ran.
   */
  /**
   * The guided first run, asked for rather than volunteered.
   *
   * `running` and `setTour` are gone: the tour used to speak on its own as the
   * derived state advanced, so it needed switching off. It does not any more,
   * and a stored preference for a thing that never happens is a setting nobody
   * can observe the effect of.
   */
  tour: () => { lines: Line[]; finished: boolean };
  /**
   * One line the running command rewrites in place, for something that takes
   * minutes — `rebalance tune` (ADR-0056). Absent where nothing can show it
   * (the tests), and a command then says how to follow it rather than wait.
   */
  progress?: (text: string) => void;
  /** Aborted when the trader presses Escape: stop waiting, not stop the work. */
  signal?: AbortSignal;
  /**
   * The shell's command history, newest first, and the way to empty it
   * (`M9/E7`). Held by the shell for the reason the socket is.
   */
  history?: () => readonly string[];
  clearHistory?: () => void;
};

/**
 * A question the command wants answered before it can finish.
 *
 * The terminal could print and it could read a whole line, and it had no way to
 * *ask* — so every command that needed input took it as an argument, which is
 * why signing in meant typing an email at a prompt that gave no sign it wanted
 * one and then retyping the command with a code appended (M4/E13).
 *
 * `onAnswer` returns another `CommandResult`, so a prompt can lead to a prompt
 * and the flow is written as ordinary code rather than as a state machine the
 * shell has to interpret. The shell owns only two things: routing the next line
 * here instead of to the grammar, and keeping the answer out of history — a
 * command's argument belongs in ArrowUp, an emailed code does not.
 */
export type Prompt = {
  /** What to show in place of the usual prompt: `email:`, `code:`. */
  label: string;
  onAnswer: (answer: string) => Promise<CommandResult>;
};

export type CommandResult = { lines: Line[]; prompt?: Prompt };

const out = (text: string): Line => ({ kind: "out", text });
const err = (text: string): Line => ({ kind: "err", text });
const note = (text: string): Line => ({ kind: "note", text });
/** A line about REAL money (`ADR-0050`), in the error colour so it is never skimmed past. */
const warn = (text: string): Line => ({ kind: "err", text });

/** The same three, for a line whose columns matter. */
const row = (text: string): Line => ({ kind: "out", text, pre: true });
const rowNote = (text: string): Line => ({ kind: "note", text, pre: true });
const rowErr = (text: string): Line => ({ kind: "err", text, pre: true });

/**
 * The line to print when the system worked and said no.
 *
 * **`reason` first, and this was a bug for three milestones.** `api.ts` builds
 * `error` from `body.message ?? body.error`, and the orders route's `422` sends
 * neither — it sends `{ error: "rejected", reason, code }`. So every refusal the
 * OMS has ever made printed the bare word `rejected` while the sentence
 * explaining it sat unused on the same object. "you have 100,000 and this costs
 * 240,000" became "rejected", and so did "your limit is 1,000.00 per order".
 *
 * `api.ts` states the rule this restores in its own comment: prefer `reason` in
 * the line you print, keep `error` for branching. Found by typing `buy 100
 * AAPL` at the deployed terminal in `M5/I1/T7` — every unit test and every
 * deployed API check was green, because none of them look at what a person
 * reads.
 */
const refusal = (res: { error: string; reason?: string }): Line => err(res.reason ?? res.error);

type Handler = (args: string[], ctx: CommandContext) => Promise<CommandResult>;

/** Time in force — the words `buy` and `sell` take after a price. `spec.ts` holds the list. */
const TIF = TIME_IN_FORCE;

/**
 * The limits `policy set` accepts.
 *
 * They are the API's own field names rather than friendlier aliases. An alias
 * would be a second vocabulary to keep in step with the server's, and the
 * refusal a trader gets for a wrong one — `unknown field` — names the real set.
 * The list and each limit's kind are `@atc/domain`'s, which the route reads too.
 */
const LIMIT_NAMES = POLICY_LIMIT_NAMES;

/**
 * What runs a command.
 *
 * Everything else about it — its forms, arguments, flags, examples, traps and
 * what Tab offers — is its declaration in `catalog.ts`
 * (`DESIGN-cli-command-spec`). `COMMANDS` satisfies a record over the
 * catalogue's names, so a command without a declaration, or a declaration
 * without a command, does not compile.
 */
type CommandSpec = { run: Handler };

const COMMANDS = {
  dca: {
    run: (args, ctx) => intradayCommand("dca", args, ctx),
  },
  band: {
    run: (args, ctx) => intradayCommand("band", args, ctx),
  },
  shares: {
    run: (args, ctx) => intradayCommand("shares", args, ctx),
  },
  gap: {
    run: (args, ctx) => gapCommand(args, ctx),
  },
  sync: {
    run: (args, ctx) => syncCommand(args, ctx),
  },
  help: {
    run: async (args, ctx) => {
      const wanted = args[0];
      if (wanted) return helpFor(wanted);
      // The first run's checklist leads the index until every step is done —
      // what `start` printed, in the one place a newcomer already looks.
      const tour = ctx.tour();
      return {
        lines: [
          ...(tour.finished ? [] : [note("your first run"), ...tour.lines, note("")]),
          ...renderHelpIndex(DOCS),
        ],
      };
    },
  },

  login: {
    run: async (args, ctx) => {
      const email = args[0];
      // Bare `login` asks. The two argument forms are unchanged — a flow that
      // only worked interactively would break the one thing this terminal is
      // for, which is typing a whole instruction on one line.
      if (!email) {
        return {
          lines: [note("signing in — the code arrives by email")],
          prompt: { label: "email:", onAnswer: (answer) => sendCode(answer.trim(), ctx) },
        };
      }

      const code = args[1];
      if (code) return verifyCode(email, code, ctx);
      return sendCode(email, ctx);
    },
  },

  whoami: {
    run: async (_args, ctx) => {
      const res = await api.whoami();
      if (!res.ok) {
        ctx.setSession({ email: undefined, userId: undefined });
        return { lines: [err("not signed in — run `login <email>`")] };
      }
      ctx.setSession({ email: res.data.email, userId: res.data.user_id });
      return {
        lines: [out(res.data.email), note(res.data.user_id)],
      };
    },
  },

  delegate: {
    run: async (args, _ctx) => {
      const parsed = parseFlags(args, CATALOG.delegate.flags ?? [], "delegate");
      if ("error" in parsed) return { lines: [err(parsed.error)] };
      const [action, ...rest] = parsed.positional;
      if (action === "list") {
        const listed = await api.listDelegations();
        if (!listed.ok) return { lines: [err(`error: ${listed.error}`)] };
        return {
          lines: listed.data.delegations.length
            ? listed.data.delegations.map((d) =>
                out(
                  `${d.id.slice(0, 8)}  ${d.scope}  expires ${d.expires_at}${d.revoked_at ? "  revoked" : ""}`,
                ),
              )
            : [note("no delegations")],
        };
      }
      if (action === "revoke" && rest.length === 1) {
        const revoked = await api.revokeDelegation(rest[0] as string);
        return revoked.ok
          ? { lines: [out(`revoked ${revoked.data.revoked} delegation(s)`)] }
          : { lines: [err(`error: ${revoked.error}`)] };
      }
      if (action === "grant") {
        const expires = parsed.values["--expires"];
        const names = rest;
        if (!names.length)
          return { lines: [err("usage: delegate grant all|<account>... [--expires HH:MM]")] };
        let accounts: "all" | string[];
        if (names[0] === "all" && names.length === 1) accounts = "all";
        else {
          const listed = await api.listAccounts();
          if (!listed.ok) return { lines: [err(`error: ${listed.error}`)] };
          accounts = listed.data.accounts
            .filter((account) =>
              names.some((name) => account.name.toLowerCase() === name.toLowerCase()),
            )
            .map((account) => account.id);
          if (accounts.length !== names.length)
            return { lines: [err("one or more named accounts do not exist")] };
        }
        const granted = await api.grantDelegation(accounts, expires);
        return granted.ok
          ? {
              lines: [
                out(`delegation ${granted.data.id.slice(0, 8)} expires ${granted.data.expires_at}`),
                note(`token (shown once): ${granted.data.token}`),
              ],
            }
          : { lines: [err(`error: ${granted.error}`)] };
      }
      return { lines: [err("usage: delegate grant|list|revoke")] };
    },
  },

  accounts: {
    run: async (args, _ctx) => {
      if (args[0] === "closed" && args.length === 1) return closedAccountList();
      if (args.length > 0) return { lines: [err("usage: accounts [closed]")] };
      const list = await api.listAccounts();
      if (!list.ok)
        return {
          lines: [
            err(
              list.status === 401 ? "not signed in — run `login <email>`" : `error: ${list.error}`,
            ),
          ],
        };
      if (list.data.accounts.length === 0)
        return { lines: [note("no paper accounts — run `account create <name>`")] };
      const width = Math.max(...list.data.accounts.map((a) => a.name.length)) + 2;
      const feedWidth = Math.max(...list.data.accounts.map((a) => a.feed.length)) + 2;
      return {
        lines: list.data.accounts.map((a) =>
          out(
            `${a.id === list.data.selected_account_id ? "*" : " "} ${pad(a.name, width)}${
              a.mirror ? `${pad(a.feed, feedWidth)}mirrors to ${a.mirror}` : a.feed
            }`,
          ),
        ),
      };
    },
  },

  account: {
    run: async (args, ctx) => {
      // The words are checked before anything is asked of the server.
      const parsed = parseFlags(args, CATALOG.account.flags, "account");
      if ("error" in parsed) return { lines: [err(parsed.error)] };
      if (parsed.values["--feed"] && parsed.positional[0] !== "create")
        return { lines: [err("--feed is for `account create <name> --feed <provider>`")] };
      if (parsed.values["--cash"] && parsed.positional[0] !== "create")
        return { lines: [err("--cash is for `account create <name> --cash <dollars>`")] };
      const list = await api.listAccounts();
      if (!list.ok)
        return {
          lines: [
            err(
              list.status === 401 ? "not signed in — run `login <email>`" : `error: ${list.error}`,
            ),
          ],
        };
      const [action, ...rest] = parsed.positional;
      if (!action) {
        const id = list.data.selected_account_id;
        if (!id)
          return {
            lines: [note("no account selected — run `accounts` or `account create <name>`")],
          };
        const account = await api.getAccount(id);
        return account.ok
          ? { lines: renderAccount(account.data) }
          : { lines: [err(`error: ${account.error}`)] };
      }
      if (action === "create") {
        const name = rest[0];
        if (!name || rest.length !== 1)
          return {
            lines: [err("usage: account create <name> [--feed <provider>] [--cash <dollars>]")],
          };
        const cash = parsed.values["--cash"];
        const created = await api.openAccount(name, parsed.values["--feed"], cash);
        if (!created.ok) return { lines: [err(`could not open account: ${created.error}`)] };
        ctx.setSession({ accountId: created.data.id, accountName: created.data.name });
        ctx.refresh();
        ctx.refreshFeed();
        return {
          lines: [
            note(
              created.data.created
                ? `opened ${created.data.name}${cash ? ` with $${centsOf(cash)}` : ""}, priced by ${created.data.feed}`
                : `${created.data.name} is already open, priced by ${created.data.feed}${cash ? " — --cash does not change an existing account" : ""}`,
            ),
          ],
        };
      }
      if (action === "mirror") return accountMirror(rest, list.data, ctx);
      // `account live` alone still selects an account NAMED live. With a verb,
      // or with no such account, it is the retired live mirror (ADR-0059), and
      // says where the live account went.
      if (
        action === "live" &&
        (rest.length > 0 || !list.data.accounts.some((a) => a.name.toLowerCase() === "live"))
      )
        return {
          lines: [
            note(
              "the live mirror is retired: a live account is now an account's one venue — `account mirror connect alpaca live`, then `sync`",
            ),
          ],
        };
      if (action === "delete") return accountDelete(rest, list.data, ctx);
      if (action === "close") {
        const name = rest[0];
        const target = list.data.accounts.find(
          (account) => account.name.toLowerCase() === name?.toLowerCase(),
        );
        if (!target || rest.length !== 1) return { lines: [err("usage: account close <name>")] };
        const closed = await api.closeAccount(target.id);
        if (!closed.ok)
          return { lines: [err(`could not close account: ${closed.reason ?? closed.error}`)] };
        if (target.id === list.data.selected_account_id)
          ctx.setSession({ accountId: undefined, accountName: undefined });
        ctx.refresh();
        ctx.refreshFeed();
        return { lines: [out(`closed ${target.name}`)] };
      }
      if (action === "rename") {
        const [oldName, newName] = rest;
        const target = list.data.accounts.find(
          (a) => a.name.toLowerCase() === oldName?.toLowerCase(),
        );
        if (!target || !newName || rest.length !== 2)
          return { lines: [err("usage: account rename <old> <new>")] };
        const renamed = await api.renameAccount(target.id, newName);
        if (!renamed.ok) return { lines: [err(`could not rename account: ${renamed.error}`)] };
        ctx.refresh();
        return { lines: [out(`renamed ${oldName} to ${renamed.data.name}`)] };
      }
      if (rest.length !== 0)
        return { lines: [err("usage: account [<name>|create <name>|rename <old> <new>]")] };
      const target = list.data.accounts.find((a) => a.name.toLowerCase() === action.toLowerCase());
      if (!target)
        return {
          lines: [err(`no account named ${action} — run \`account create ${action}\` to open it`)],
        };
      const selected = await api.selectAccount(target.id);
      if (!selected.ok) return { lines: [err(`could not select account: ${selected.error}`)] };
      ctx.setSession({ accountId: target.id, accountName: target.name });
      ctx.refresh();
      // The header's feed badge is the selected account's, and that just changed.
      ctx.refreshFeed();
      const account = await api.getAccount(target.id);
      return account.ok
        ? { lines: renderAccount(account.data) }
        : { lines: [err(`error: ${account.error}`)] };
    },
  },

  quote: {
    run: async (args) => {
      if (args.length === 0) return { lines: [err("usage: quote <symbol>")] };

      const res = await api.quotes(args);
      if (!res.ok) return { lines: [err(`error: ${res.error}`)] };

      const lines: Line[] = [
        rowNote(`  ${pad("symbol", 8)}${pad("last", 12)}${pad("bid", 12)}${pad("ask", 12)}`),
        ...res.data.quotes.map((q) =>
          row(
            `  ${pad(q.symbol, 8)}${pad(formatUsd(q.last), 12)}${pad(formatUsd(q.bid), 12)}${pad(formatUsd(q.ask), 12)}`,
          ),
        ),
      ];
      for (const u of res.data.unknown) lines.push(rowErr(`  ${pad(u, 8)}unknown symbol`));
      return { lines };
    },
  },

  buy: {
    run: (args, ctx) => trade("buy", args, ctx),
  },

  sell: {
    run: (args, ctx) => trade("sell", args, ctx),
  },

  cancel: {
    run: async (args, ctx) => {
      const wanted = args[0];
      if (!wanted) return { lines: [err("usage: cancel <id>")] };

      const accountId = await resolveAccount(ctx);
      if (typeof accountId !== "string") return accountId;

      // `orders` prints an 8-character prefix, so that is what a user types
      // back. Resolving it against the ids the API returned keeps the terminal
      // from inventing an identifier the server never issued.
      const open = await api.orders(accountId, 200, "open");
      if (!open.ok) return { lines: [err(`error: ${open.error}`)] };

      const matches = open.data.orders.filter((o) => o.id.startsWith(wanted.toLowerCase()));
      if (matches.length === 0) {
        return { lines: [err(`no open order starts with ${wanted}`), note("try `orders open`")] };
      }
      if (matches.length > 1) {
        return {
          lines: [
            err(`${matches.length} open orders start with ${wanted}`),
            ...matches.map((o) => note(`  ${o.id}`)),
          ],
        };
      }

      const target = matches[0] as Order;
      const res = await api.cancelOrder(accountId, target.id);
      if (!res.ok) return { lines: [refusal(res)] };

      ctx.refresh();
      return {
        lines: [
          out(`✓ ${target.id.slice(0, 8)} canceled — ${describeOrder(target)}`),
          note("the cash or shares it was holding are free again"),
        ],
      };
    },
  },

  positions: {
    run: async (_args, ctx) => {
      const accountId = await resolveAccount(ctx);
      if (typeof accountId !== "string") return accountId;

      const res = await api.positions(accountId);
      if (!res.ok) return { lines: [err(`error: ${res.error}`)] };
      if (res.data.positions.length === 0) {
        return {
          lines: [
            note("no open positions"),
            // Not silence: an account with resting orders is not an idle one,
            // and cash that is committed but unspent is the easiest thing in
            // this system to be surprised by.
            ...(res.data.open_orders > 0
              ? [
                  note(
                    `${res.data.open_orders} resting order${res.data.open_orders === 1 ? "" : "s"} holding ${formatUsd(res.data.reserved_cash)}`,
                  ),
                ]
              : []),
          ],
        };
      }

      return {
        lines: [
          rowNote(
            `  ${pad("symbol", 8)}${pad("qty", 14)}${pad("avg", 12)}${pad("mark", 12)}${pad("value", 14)}P&L`,
          ),
          ...res.data.positions.map((p) => {
            // Unrealized P&L is market value minus cost basis. It is computed
            // here from two numbers the API returned, not invented: without a
            // mark there is nothing to compare cost against, so it stays blank
            // rather than showing zero.
            const pnl =
              p.market_value === undefined
                ? ""
                : signed(subtractDecimals(p.market_value, p.cost_basis));
            return row(
              `  ${pad(p.symbol, 8)}${pad(trimQty(p.qty), 14)}${pad(formatUsd(p.average_price), 12)}${pad(
                p.mark ? formatUsd(p.mark) : "—",
                12,
              )}${pad(p.market_value ? formatUsd(p.market_value) : "—", 14)}${pnl}`,
            );
          }),
          note(""),
          row(`  ${pad("market value", 16)}${formatUsd(res.data.market_value)}`),
          row(`  ${pad("cash", 16)}${formatUsd(res.data.cash)}`),
          // Only when they differ. "You have 100,000 but can spend 2,000" is
          // something a trader must never have to work out for themselves, and
          // a line that always reads "reserved 0.00" is one nobody looks at.
          ...(res.data.reserved_cash && res.data.reserved_cash !== "0.0000"
            ? [
                rowNote(`  ${pad("reserved", 16)}${formatUsd(res.data.reserved_cash)}`),
                row(`  ${pad("spendable", 16)}${formatUsd(res.data.available_cash)}`),
              ]
            : []),
          row(`  ${pad("equity", 16)}${formatUsd(res.data.equity)}`),
        ],
      };
    },
  },

  orders: {
    run: async (args, ctx) => {
      const accountId = await resolveAccount(ctx);
      if (typeof accountId !== "string") return accountId;

      const openOnly = args[0]?.toLowerCase() === "open";
      const countArg = openOnly ? args[1] : args[0];
      const limit = Math.min(Math.max(Number(countArg ?? 20) || 20, 1), 200);

      const res = await api.orders(accountId, limit, openOnly ? "open" : undefined);
      if (!res.ok) return { lines: [err(`error: ${res.error}`)] };
      if (res.data.orders.length === 0) {
        return { lines: [note(openOnly ? "nothing resting" : "no orders yet")] };
      }
      // The alpaca column appears once any order listed was mirrored (M7/I11).
      const mirrored = res.data.orders.some((o) => o.mirror);

      return {
        lines: [
          rowNote(
            `  ${pad("id", 10)}${pad("side", 6)}${pad("qty", 10)}${pad("symbol", 8)}${pad("order", 22)}${pad("status", 11)}${pad("price", 12)}${mirrored ? "alpaca" : ""}`,
          ),
          ...res.data.orders.map((o) => {
            const price = o.avg_fill_price ? formatUsd(o.avg_fill_price) : "—";
            const line = `  ${pad(orderTag(o), 10)}${pad(o.side, 6)}${pad(trimQty(o.qty), 10)}${pad(
              o.symbol,
              8,
            )}${pad(describeTerms(o), 22)}${pad(o.status, 11)}${mirrored ? `${pad(price, 12)}${mirrorCell(o)}` : price}`;
            if (o.status === "rejected") return rowErr(line);
            // A resting order is the one thing on this screen that can still
            // change on its own, so it is the one thing highlighted.
            return o.status === "new" ? rowNote(line) : row(line);
          }),
          ...res.data.orders
            .filter((o) => o.reject_reason)
            .map((o) => rowNote(`  ${orderTag(o)}  ${o.reject_reason}`)),
          ...mirrorRefusalLines(res.data.orders),
          // `M5/E10`. Which run placed it is the question anybody actually
          // asks, and it does not fit in the grid — the id column has exactly
          // one spare character, which is where the marker goes. The run ids
          // go underneath, next to the same marked id, so the two read as one
          // row rather than as a second table.
          ...res.data.orders
            .filter((o) => o.submitted_by === "agent")
            .map((o) =>
              rowNote(
                `  ${orderTag(o)}  placed by an agent${
                  o.agent_run_id ? ` · run ${o.agent_run_id.slice(0, 8)}` : ""
                }`,
              ),
            ),
        ],
      };
    },
  },

  watch: {
    run: async (args, ctx) => {
      const symbols = args.map((s) => s.trim().toUpperCase()).filter((s) => s.length > 0);
      if (symbols.length === 0) {
        const watching = ctx.watching();
        return {
          lines:
            watching.length === 0
              ? [note("watching nothing — try `watch NVDA`")]
              : [out(`watching ${watching.join(" ")}`)],
        };
      }

      // Refused rather than silently watching a symbol that will never tick:
      // an empty slot in the ticker is indistinguishable from a broken feed.
      const unknown: string[] = [];
      const found = await api.quotes(symbols);
      if (found.ok) unknown.push(...found.data.unknown);

      const known = symbols.filter((s) => !unknown.includes(s));
      if (known.length > 0) ctx.watch(known);

      return {
        lines: [
          ...(known.length > 0 ? [out(`watching ${ctx.watching().join(" ")}`)] : []),
          ...unknown.map((s) => err(`unknown symbol: ${s}`)),
        ],
      };
    },
  },

  unwatch: {
    run: async (args, ctx) => {
      const symbols = args.map((s) => s.trim().toUpperCase()).filter((s) => s.length > 0);
      if (symbols.length === 0) return { lines: [err("usage: unwatch NVDA")] };

      ctx.unwatch(symbols);
      const left = ctx.watching();
      return {
        lines: [out(left.length === 0 ? "watching nothing" : `watching ${left.join(" ")}`)],
      };
    },
  },

  markets: {
    run: async () => {
      const res = await api.markets();
      if (!res.ok) return { lines: [err(res.reason ?? res.error)] };
      return { lines: res.data.markets.flatMap((m) => marketLines(m)) };
    },
  },

  market: {
    run: async (args) => {
      const id = args[0]?.trim();
      if (!id) return { lines: [err("usage: market US")] };

      const res = await api.market(id);
      if (!res.ok) {
        return {
          lines: [
            err(res.reason ?? res.error),
            ...(res.status === 404 ? [note("`markets` lists them")] : []),
          ],
        };
      }
      return { lines: marketLines(res.data, { verbose: true }) };
    },
  },

  status: {
    run: async (args, ctx) => {
      const what = args[0];
      if (what !== undefined && what !== "system" && what !== "incidents") {
        return { lines: [err(`usage: ${usageOf(CATALOG.status)}`), note("  help status")] };
      }
      if (what === "incidents") {
        const listed = await api.incidents();
        if (!listed.ok) {
          return {
            lines: [
              listed.code === "not_an_operator"
                ? err("`status incidents` is an operator control")
                : refusal(listed),
            ],
          };
        }
        const events = listed.data.incidents;
        if (events.length === 0) return { lines: [out("no incident has ever been recorded")] };
        return {
          lines: [
            rowNote(`  ${pad("when", 22)}${pad("event", 10)}${pad("rule", 16)}emailed`),
            ...events.map((e) =>
              row(`  ${pad(e.at, 22)}${pad(e.event, 10)}${pad(e.rule, 16)}${e.emailed}`),
            ),
          ],
        };
      }

      const got = await api.status(ctx.session.accountId ?? undefined);
      if (!got.ok) return { lines: [refusal(got)] };
      const { you, system } = got.data;

      if (what === "system") {
        if (!system) {
          return { lines: [err("`status system` is an operator control — `status` shows yours")] };
        }
        const failing = system.rules.filter((r) => r.verdict === "fail");
        const age = system.sweep_age_s;
        const lines: Line[] = [
          age === null
            ? err("the sweep has never run")
            : age > 120
              ? err(`the last sweep was ${age}s ago — the cron may have stopped`)
              : out(`swept ${age}s ago · ${failing.length} rule(s) failing`),
          rowNote(`  ${pad("rule", 16)}${pad("verdict", 9)}${pad("branch", 22)}observed`),
          ...system.rules.map((r) => {
            const line = `  ${pad(r.rule, 16)}${pad(r.verdict, 9)}${pad(r.branch, 22)}${r.observed ?? ""}`;
            return r.verdict === "fail"
              ? rowErr(line)
              : r.verdict === "skip"
                ? rowNote(line)
                : row(line);
          }),
        ];
        const t = system.traffic;
        lines.push(
          "unavailable" in t
            ? note(`  traffic: unavailable (${t.unavailable})`)
            : out(
                `  last ${t.minutes}m: ${t.requests} requests, ${t.errors_5xx} 5xx, ` +
                  `${t.failed_invocations} failed invocations · p50 ${t.p50_ms}ms · p99 ${t.p99_ms}ms`,
              ),
        );
        if (!("unavailable" in t)) {
          for (const e of t.by_entrypoint) {
            lines.push(rowNote(`    ${pad(e.entrypoint, 22)}${pad(e.outcome, 14)}${e.count}`));
          }
        }
        for (const i of system.incidents_open) {
          lines.push(
            rowErr(
              `  open since ${i.opened_at}: ${i.rule}${i.subject ? ` (${i.subject})` : ""} — emailed ${i.emailed}`,
            ),
          );
        }
        lines.push(
          note(
            `  quote_tape ~${system.quote_tape.rows_estimate} rows, ` +
              `${Math.round(system.quote_tape.bytes / 1024)} KiB`,
          ),
        );
        return { lines };
      }

      // The trader's own slice.
      const feed = you.feed as Record<string, unknown>;
      const overdue = you.schedules.filter((s) => s.overdue);
      const running = you.schedules.filter((s) => s.status === "enabled");
      const lines: Line[] = [
        out(
          `feed ${String(feed.feed ?? "none")} (${String(feed.kind ?? "—")}) · ` +
            `${String(feed.market ?? "—")} ${String(feed.market_state ?? "—")}`,
        ),
      ];
      if (typeof feed.last_error === "string" && feed.last_error) {
        lines.push(err(`  the feed last failed with: ${feed.last_error}`));
      }
      lines.push(
        overdue.length > 0
          ? err(
              `${overdue.length} of ${running.length} running schedule(s) overdue: ` +
                overdue.map((s) => `${s.kind} ${s.symbol}`).join(", "),
            )
          : out(
              running.length === 0
                ? "no schedule is running"
                : `${running.length} schedule(s) running, all ticking`,
            ),
      );
      if (you.questions_waiting > 0) {
        lines.push(
          err(`${you.questions_waiting} agent question(s) waiting — run \`agent approvals\``),
        );
      }
      if (you.mirror) {
        lines.push(
          out(
            `mirrored to ${you.mirror.provider}${you.mirror.account_number ? ` ${you.mirror.account_number}` : ""}` +
              `${you.mirror.next_sync_at ? ` · next sync ${you.mirror.next_sync_at}` : ""}`,
          ),
        );
      }
      if (system) {
        const failing = system.rules.filter((r) => r.verdict === "fail").length;
        lines.push(
          failing === 0 && system.incidents_open.length === 0
            ? note("system: every rule passing — `status system`")
            : err(
                `system: ${failing} rule(s) failing, ${system.incidents_open.length} incident(s) open — \`status system\``,
              ),
        );
      }
      return { lines };
    },
  },

  schedules: {
    run: async () => {
      const [listed, rebalanced] = await Promise.all([api.intradaySchedules(), api.rebalances()]);
      if (!listed.ok) return { lines: [refusal(listed)] };
      const all = listed.data.schedules;
      // Rebalances trade too (M9/I2): listed here so "nothing is running" stays true.
      const rebalances = rebalanced.ok ? (rebalanced.data.rebalances ?? []) : [];
      const rebalanceLines = rebalances.map((r) => {
        // The symbol column names the setting and its share (M10/I5): `b 40%`.
        const what = r.name === undefined ? "top" : `${r.name} ${r.share ?? 100}%`;
        const line = `  ${pad(r.status === "unreachable" ? "unknown" : r.status, 10)}${pad("rebal", 6)}${pad(what, 8)}${pad(r.account ?? r.account_id.slice(0, 8), 16)}${r.next?.at ?? "—"}${r.error ? `  skipped: ${r.error}` : ""}`;
        return r.status === "enabled"
          ? row(line)
          : r.status === "unreachable"
            ? rowErr(line)
            : rowNote(line);
      });
      if (all.length === 0 && rebalances.length === 0) {
        return {
          lines: [out("nothing is running — no schedule has been configured on your accounts")],
        };
      }
      // The server orders them, running first. Whether "nothing is running" is
      // true is the one question this answers, so it is said before the table.
      const live =
        all.filter((s) => s.status !== "disabled").length +
        rebalances.filter((r) => r.status !== "disabled").length;
      const where = (s: (typeof all)[number]) => s.account ?? s.account_id.slice(0, 8);
      return {
        lines: [
          live === 0
            ? out("nothing is running")
            : err(`${live} schedule${live === 1 ? "" : "s"} may be trading`),
          rowNote(
            `  ${pad("status", 10)}${pad("kind", 6)}${pad("symbol", 8)}${pad("account", 16)}last tick`,
          ),
          ...all.map((s) => {
            const line = `  ${pad(s.status, 10)}${pad(s.kind, 6)}${pad(s.symbol, 8)}${pad(`${s.market} ${where(s)}`, 16)}${s.last_tick_at ?? "never"}`;
            if (s.status === "enabled") return row(line);
            return s.status === "unknown" ? rowErr(line) : rowNote(line);
          }),
          ...rebalanceLines,
          ...all
            .filter((s) => s.error)
            .map((s) => rowNote(`  ${s.kind} ${s.symbol} on ${where(s)}: ${s.error}`)),
          ...(!rebalanced.ok ? [note(`  rebalances could not be read: ${rebalanced.error}`)] : []),
        ],
      };
    },
  },

  curve: {
    // The server's points, printed as they came (M7/E11). Nothing here is
    // derived from them — not a return, not a drawdown.
    run: async (args, ctx) => {
      const accountId = ctx.session.accountId;
      if (!accountId) return { lines: [err("open an account first — run `account`")] };
      const parsed = parseFlags(args, CATALOG.curve.flags, "curve");
      if ("error" in parsed) return { lines: [err(parsed.error), note("  help curve")] };
      if (parsed.positional.length > 0) {
        return { lines: [err(`usage: ${usageOf(CATALOG.curve)}`), note("  help curve")] };
      }
      const days = parsed.values["--days"];
      const res = await api.accountValuations(accountId, days ? Number(days) : undefined);
      if (!res.ok) return { lines: [refusal(res)] };
      const points = res.data.points;
      if (points.length === 0) {
        return {
          lines: [note(`no valuation points on this account in the last ${days ?? 30} days`)],
        };
      }
      return {
        lines: [
          out(
            `${points.length} valuation point${points.length === 1 ? "" : "s"}, oldest first${
              res.data.next_cursor ? " — more than one page; narrow --days" : ""
            }`,
          ),
          rowNote(
            `  ${pad("as of", 26)}${pad("equity", 16)}${pad("cash", 16)}${pad("holdings", 14)}${pad("buy & hold", 14)}feed`,
          ),
          ...points.map((p) =>
            row(
              `  ${pad(p.as_of, 26)}${pad(p.equity, 16)}${pad(p.cash, 16)}${pad(p.market_value, 14)}${pad(p.benchmark_value ?? "—", 14)}${p.feed}`,
            ),
          ),
        ],
      };
    },
  },

  record: {
    // The trader's own recorder (M7/E19). The tape lives on the server; this
    // starts and stops it, prints its status, and saves a day as a file.
    run: async (args) => {
      const parsed = parseFlags(args, CATALOG.record.flags, "record");
      if ("error" in parsed) return { lines: [err(parsed.error), note("  help record")] };
      const [verb, list, ...extra] = parsed.positional;
      const usage = {
        lines: [err(`usage: ${usageOf(CATALOG.record)}`), note("  help record")],
      };
      const date = parsed.values["--date"];
      const action = verb?.toLowerCase();
      if (date && action !== "export") return usage;

      if (action === undefined) {
        const res = await api.recorder();
        if (!res.ok) return { lines: [recorderRefusal(res)] };
        return { lines: recorderLines(res.data) };
      }
      if (action === "on") {
        if (!list || extra.length > 0) return usage;
        const symbols = list
          .split(",")
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean);
        const res = await api.startRecorder(symbols);
        if (!res.ok) return { lines: [recorderRefusal(res)] };
        return {
          lines: [
            out(
              `recording ${res.data.symbols.join(", ")} once a minute through the US session and its delayed tail`,
            ),
            ...recorderLines(res.data),
          ],
        };
      }
      if (action === "off") {
        if (list) return usage;
        const res = await api.stopRecorder();
        if (!res.ok) return { lines: [recorderRefusal(res)] };
        return { lines: recorderLines(res.data) };
      }
      if (action === "export") {
        if (list) return usage;
        const res = await api.recordedTape(date);
        if (!res.ok) return { lines: [recorderRefusal(res)] };
        const { quotes } = res.data;
        const day = res.data.date;
        if (quotes.length === 0) return { lines: [note(`nothing recorded on ${day} (UTC)`)] };
        const saved = saveJson(`${day}.json`, res.data);
        return {
          lines: [
            out(
              `${quotes.length} print${quotes.length === 1 ? "" : "s"} on ${day}: ${quotes[0]?.as_of} → ${quotes.at(-1)?.as_of}`,
            ),
            saved
              ? note(`saved ${day}.json — commit it as intraday/${day}.json`)
              : err("this terminal could not save a file"),
          ],
        };
      }
      return usage;
    },
  },

  privacy: {
    run: async () => ({
      lines: [
        out(`the privacy policy: ${pageUrl("/privacy")}`),
        note("what is collected, why, and how to have it sent to you or deleted"),
      ],
    }),
  },

  setup: {
    run: async (args, ctx) => chooseLook(args[0], ctx.setups(), ctx.setSetup, "setup"),
  },

  theme: {
    run: async (args, ctx) => chooseLook(args[0], ctx.themes(), ctx.setTheme, "theme"),
  },

  history: {
    run: async (args, ctx) => {
      const verb = args[0]?.toLowerCase();
      if (args.length > 1 || (verb !== undefined && verb !== "clear"))
        return { lines: [err(`usage: ${usageOf(CATALOG.history)}`), note("  help history")] };
      if (!ctx.history || !ctx.clearHistory)
        return { lines: [err("history is kept by the terminal, and this one keeps none")] };
      if (verb === "clear") {
        const n = ctx.history().filter((c) => !/^history\s+clear$/i.test(c.trim())).length;
        ctx.clearHistory();
        return {
          lines: [
            out(
              n === 0
                ? "history was already empty"
                : `history cleared — ${n} command${n === 1 ? "" : "s"} forgotten`,
            ),
          ],
        };
      }
      // Oldest first and numbered, as bash prints it; this `history` is the last line.
      const all = [...ctx.history()].reverse();
      if (all.length === 0) return { lines: [note("no history yet")] };
      const width = String(all.length).length;
      return {
        lines: all.map((c, i) => row(`  ${String(i + 1).padStart(width)}  ${c}`)),
      };
    },
  },

  replay: {
    run: async (args, ctx) => {
      if (args[0]?.toLowerCase() === "stop") {
        return {
          lines: [out(ctx.stopReplay() ? "replay stopped" : "nothing was replaying")],
        };
      }

      // No date: say what there is. A command whose only failure mode is
      // "guess a date correctly" is a command nobody uses twice.
      if (args.length === 0) {
        const listed = await api.recordings();
        if (!listed.ok) return { lines: [err(listed.error)] };
        if (listed.data.recordings.length === 0) {
          return { lines: [note("no recorded days yet")] };
        }
        return {
          lines: [
            out(`recorded days: ${listed.data.recordings.join(" ")}`),
            note("try `replay <date> --speed 60`"),
          ],
        };
      }

      const parsed = parseReplayArgs(args);
      if ("error" in parsed) return { lines: [err(parsed.error)] };

      // elapsed 0: the opening frame. Everything after this is the shell
      // ticking, and every number in every frame is the server's.
      const first = await api.replay(parsed.date, 0, parsed.speed, parsed.symbols);
      if (!first.ok) {
        return {
          lines: [
            err(first.error),
            ...(first.status === 404 ? [note("`replay` with no date lists what exists")] : []),
          ],
        };
      }

      ctx.startReplay({ date: parsed.date, speed: parsed.speed, symbols: parsed.symbols });

      const frame = first.data;
      const span =
        (Date.parse(frame.session.end) - Date.parse(frame.session.start)) / 1000 / parsed.speed;

      return {
        lines: [
          out(`replaying ${frame.date} at ${parsed.speed}× — about ${Math.round(span)}s`),
          out(`  ${frame.symbols.join(" ")}  ${frame.timeframe}  ${frame.source} (${frame.kind})`),
          // Said out loud, every time. A modeled spread that nobody mentions is
          // a number people start trusting as an observation.
          note(
            `spread is modeled at ${Object.values(frame.modeled_half_spread_bps)
              .map((b) => `${b}bps`)
              .join("/")} half — recorded bars carry no bid/ask`,
          ),
          note("`replay stop` ends it"),
        ],
      };
    },
  },

  logout: {
    run: async (_args, ctx) => {
      await api.signOut();
      ctx.setSession({
        email: undefined,
        userId: undefined,
        accountId: undefined,
        accountName: undefined,
        agreementRequired: undefined,
      });
      // The socket authenticated once, at its upgrade, and the server's
      // 15-minute cap is a backstop rather than a prompt response. Closing it
      // here is what makes signing out take effect now.
      ctx.disconnect();
      ctx.refresh();
      return { lines: [out("signed out")] };
    },
  },

  clear: {
    run: async () => ({ lines: [] }),
  },

  /**
   * `strategy` — whether historical evidence supports an M8 investment review.
   *
   * A separate noun from `agent` because it is a separate question. `agent`
   * runs and schedules a trader; `strategy` asks whether one has earned a
   * nomination, which is `ADR-0017`'s subject and the vocabulary it uses:
   * *"`strategy qualify <version>` returns exactly one of
   * `qualified_for_m8_review`, `rejected`, or `insufficient_evidence`"*.
   *
   * Candidates are accepted by id or by version — `momentum-det` and
   * `momentum-det@1` both work — and the resolution happens in the API. The
   * terminal has no copy of the registry and must not grow one.
   */
  rebalance: {
    run: async (args, ctx) => {
      // `tune` takes a list where every other form takes one value (M9/E6).
      if (args[0]?.toLowerCase() === "tune") return rebalanceTuneCommand(args.slice(1), ctx);
      const parsed = parseFlags(args, CATALOG.rebalance.flags, "rebalance");
      if ("error" in parsed) return { lines: [err(parsed.error), note("  help rebalance")] };
      const [verb, ...extra] = parsed.positional;
      const usage = {
        lines: [err(`usage: ${usageOf(CATALOG.rebalance)}`), note("  help rebalance")],
      };
      const action = verb?.toLowerCase() ?? "status";
      // Which of the account's settings (M10/I5): not a rule field, so never sent as one.
      const { "--name": name, ...rule } = parsed.values;
      const flagged = Object.keys(rule).length > 0;
      if (
        ![
          "backtest",
          "list",
          "preview",
          "on",
          "off",
          "now",
          "status",
          "log",
          "compare",
          "tune",
        ].includes(action)
      )
        return usage;
      if (action === "backtest") return extra.length > 0 ? usage : rebalanceBacktest(parsed.values);
      if (action === "tune") return rebalanceTuneCommand([...extra], ctx, parsed.values);
      if (action === "list") return extra.length > 0 || flagged ? usage : rebalanceList();
      if (action === "compare")
        return flagged || extra.length < 1 || extra.length > 2
          ? usage
          : rebalanceCompare(ctx.session.accountId, extra);
      const accountId = ctx.session.accountId;
      if (!accountId) return { lines: [err("open an account first — run `account`")] };
      if (action === "preview")
        return extra.length > 0 ? usage : rebalancePreview(accountId, rule, name);
      if (action === "on") return extra.length > 0 ? usage : rebalanceOn(accountId, rule, name);
      if (flagged) return usage;
      if (action === "status" && extra.length === 0) return rebalanceShow(accountId, 1, name);
      if (action === "now" && extra.length === 0) {
        const res = await api.rebalanceNow(accountId, name);
        if (!res.ok) return { lines: [refusal(res)] };
        return {
          lines: [
            out(name ? `rebalanced ${name} now` : "rebalanced now"),
            ...decisionLines(res.data.decision),
          ],
        };
      }
      if (action === "log" && extra.length <= 1) {
        const n = extra[0] === undefined ? 5 : Number(extra[0]);
        if (!Number.isInteger(n) || n < 1 || n > 60)
          return { lines: [err("rebalance log takes 1–60")] };
        return rebalanceShow(accountId, n, name);
      }
      if (action === "off") {
        // `sell` sells its book; `forget` then removes a named setting (M10/I5).
        const words = extra.map((w) => w.toLowerCase());
        if (words.some((w) => w !== "sell" && w !== "forget") || new Set(words).size < words.length)
          return usage;
        const forget = words.includes("forget");
        if (forget && (name === undefined || name === "default"))
          return {
            lines: [err("`forget` removes a named setting — rebalance off forget --name <name>")],
          };
        return rebalanceOff(accountId, words.includes("sell"), name, forget);
      }
      return usage;
    },
  },

  strategy: {
    run: async (args) => {
      const verb = args[0]?.toLowerCase();
      if (verb === "qualify") return args[1] ? strategyQualify(args[1]) : strategyField();
      if (verb === "dossier") return strategyDossier(args[1]);
      if (verb === "holdout") return strategyHoldout();
      if (verb === "nominate") return strategyNominate(args[1]);
      if (verb === "field" || verb === undefined) return strategyField();
      return { lines: [err(`strategy: unknown argument '${args[0]}'`)] };
    },
  },

  agent: {
    run: async (args, ctx) => {
      const verb = args[0]?.toLowerCase();
      if (verb === "run") return startAgent(args[1], ctx);
      if (verb === "status") return agentStatus(args[1]);
      if (verb === "approve") return answerAgent(args[1], true, args.slice(2).join(" "), ctx);
      if (verb === "reject") return answerAgent(args[1], false, args.slice(2).join(" "), ctx);
      if (verb === "perf") return agentPerformance(ctx);
      if (verb === "evals") return agentEvals(args[1]);
      if (verb === "backtest") return agentBacktest(args[1]);
      if (verb === "config") return agentConfig(args.slice(1), ctx);
      /*
       * `agent qualify` was renamed to `strategy qualify` in `M6/I4/T3` — the
       * noun ADR-0017 uses, and the right one: a STRATEGY is qualified for
       * investment review, not an agent. Renamed outright rather than aliased,
       * so there is one name for it; the pointer below is what a reader who
       * types the old one gets, and it costs one line.
       */
      if (verb === "qualify") {
        return { lines: [err("agent qualify is now `strategy qualify` — try `strategy`")] };
      }
      if (verb) return { lines: [err(`agent: unknown argument '${args[0]}'`)] };
      return agentList();
    },
  },

  spend: {
    run: async () => agentSpend(),
  },

  insights: {
    run: async (_args, ctx) => startAgent("portfolio", ctx),
  },

  policy: {
    run: async (args) => {
      const verb = args[0]?.toLowerCase();
      if (verb === "halt" || verb === "resume") return haltOrResume(verb, args.slice(1).join(" "));
      if (verb === "set") return setLimit(args[1], args.slice(2));
      if (verb) return { lines: [err(`policy: unknown argument '${args[0]}'`)] };

      const state = await api.policy();
      if (!state.ok) return { lines: [err(`could not read your limits: ${state.error}`)] };
      return { lines: policyLines(state.data) };
    },
  },

  feed: {
    run: async (args, ctx) => {
      const verb = args[0]?.toLowerCase();
      if (verb === "connect") return connectFeed(args[1], ctx);
      if (verb === "disconnect") return disconnectFeed(args[1]);

      const state = await api.feeds();
      if (!state.ok) return { lines: [feedError(state)] };
      if (!verb) return { lines: feedLines(state.data) };

      const chosen = await api.selectFeed(verb);
      if (!chosen.ok) {
        // The refusal carries the offer, so the list below is the one the
        // server refused against rather than a second, staler read.
        return {
          lines: [err(chosen.error), ...feedLines(state.data)],
        };
      }
      ctx.refreshFeed();
      const account = ctx.session.accountName ?? "the selected account";
      return {
        lines: [
          out(`${account} is now priced by ${feedName(selectedOf(chosen.data))}.`),
          ...switchLines(chosen.data),
          ...feedLines(chosen.data),
        ],
      };
    },
  },

  terms: {
    run: async (_args, ctx) => {
      // Readable at any time, signed in or not. Terms you can only see once, at
      // the moment you are being asked to accept them, are terms nobody reads.
      const terms = await api.agreement();
      if (!terms.ok) return { lines: [err(`could not load the terms: ${terms.error}`)] };

      const lines: Line[] = terms.data.lines.map((text) => note(text));

      // If they are outstanding, reading them is also a chance to accept.
      if (ctx.session.userId) {
        const gate = await agreementGate(ctx);
        if (gate) return gate;
      }

      return {
        lines: [...lines, note(""), note(`version ${terms.data.version} — ${pageUrl("/terms")}`)],
      };
    },
  },
} satisfies Record<CommandName, CommandSpec>;

/** A page this deployment serves beside the terminal, as an address someone can open. */
const pageUrl = (path: string) => `${globalThis.location?.origin ?? ""}${path}`;

/** Lookups by a typed word, which may be anything at all. */
const COMMAND_TABLE: Readonly<Record<string, CommandSpec>> = COMMANDS;
const DOCS: Readonly<Record<string, CommandDoc>> = CATALOG;

/**
 * Ask the server to send a code, then ask the user for it.
 *
 * One function for both entry points, so the interactive flow cannot drift from
 * the argument form — including the two refusals that are not failures. A 403
 * or a 429 ends the flow with no follow-up prompt: asking for a code that was
 * never sent would be the terminal pretending the server said yes.
 */
async function sendCode(email: string, ctx: CommandContext): Promise<CommandResult> {
  if (!email) return { lines: [err("no email given — nothing sent")] };

  const sent = await api.sendOtp(email);
  if (!sent.ok) {
    if (sent.status === 403) {
      return {
        lines: [
          err(`${email} cannot sign in here`),
          note(sent.reason ?? "this deployment only accepts a configured list of addresses"),
        ],
      };
    }
    if (sent.status === 429) {
      return {
        lines: [
          err("too many codes requested for that address"),
          note(sent.reason ?? "wait a few minutes and try again"),
          note("if a code already arrived, it still works — run: login <email> <code>"),
        ],
        // A code that already arrived is still good, so this one DOES go on to
        // ask: the refusal is about sending another, not about the sign-in.
        prompt: {
          label: "code:",
          onAnswer: (answer) => verifyCode(email, answer.trim(), ctx),
        },
      };
    }
    return { lines: [err(`could not send a code: ${sent.reason ?? sent.error}`)] };
  }

  ctx.setSession({ pendingEmail: email });

  // Staging returns the code from a dev endpoint because its test addresses can
  // never receive mail. In production that endpoint is a 404 and the branch
  // below is the real path: the code arrives by email. See M2/I7.
  const peek = await api.peekOtp(email);
  const lines = peek.ok
    ? [out(`code sent to ${email}`), note(`dev mode — your code is ${peek.data.otp}`)]
    : [
        out(`code sent to ${email}`),
        note("check your email — the code lasts 10 minutes, and we never send a sign-in link"),
      ];

  return {
    lines,
    prompt: { label: "code:", onAnswer: (answer) => verifyCode(email, answer.trim(), ctx) },
  };
}

/** The second half: exchange the code for a session. */
async function verifyCode(
  email: string,
  code: string,
  ctx: CommandContext,
): Promise<CommandResult> {
  const verified = await api.verifyOtp(email, code);
  if (!verified.ok) {
    return {
      lines: [err(`sign-in failed: ${verified.reason ?? verified.error}`)],
      // Re-asked rather than dropped. A mistyped digit is the likeliest reason
      // to be here, and making the user start the whole flow again over one
      // character is how a sign-in becomes something people avoid.
      prompt: { label: "code:", onAnswer: (answer) => verifyCode(email, answer.trim(), ctx) },
    };
  }

  ctx.setSession({ email, userId: verified.data.user.id, pendingEmail: undefined });
  ctx.refresh();

  // The terms come immediately after signing in, before anything else is
  // suggested. Not because the terminal enforces them — the API refuses every
  // route until they are accepted — but because running into a `403` you were
  // never told about is a worse way to find out.
  const gate = await agreementGate(ctx, [out(`signed in as ${email}`)]);
  if (gate) return gate;

  return {
    lines: [
      out(`signed in as ${email}`),
      note("run `account` to open or inspect your paper account"),
    ],
  };
}

/**
 * Print the terms and ask for the word, if this user still owes us one.
 *
 * Returns `undefined` when there is nothing to ask, so a caller can carry on
 * with whatever it was going to say. The text is fetched, never bundled: the
 * server words the agreement, and a copy in the browser is a copy that can be
 * out of date with what was actually accepted.
 */
async function agreementGate(
  ctx: CommandContext,
  before: Line[] = [],
): Promise<CommandResult | undefined> {
  const who = await api.whoami();
  if (!who.ok || !who.data.agreement_required) {
    if (who.ok) ctx.setSession({ agreementRequired: false });
    return undefined;
  }

  ctx.setSession({ agreementRequired: true });
  const terms = await api.agreement();
  if (!terms.ok) {
    return {
      lines: [
        ...before,
        err("could not load the terms of use — nothing works until they are accepted"),
        note("try `terms` again in a moment"),
      ],
    };
  }

  return {
    lines: [
      ...before,
      ...terms.data.lines.map((text) => (text === "" ? note("") : note(text))),
      note(""),
      out(`type \`${CONSENT}\` to accept, or \`logout\` to leave.`),
    ],
    prompt: { label: `${CONSENT}?`, onAnswer: (answer) => acceptAgreement(answer, ctx) },
  };
}

/** What a switch ended, said before the picker so it is not scrolled past. */
function switchLines(outcome: Partial<FeedSwitch>): Line[] {
  // Partial: a deployment from before `M7/E33` answers a switch without them.
  const lines: Line[] = [];
  const canceled = outcome.canceled_orders?.length ?? 0;
  if (canceled > 0)
    lines.push(
      note(
        `canceled ${canceled} resting order${canceled === 1 ? "" : "s"} priced by the old feed.`,
      ),
    );
  for (const s of outcome.stopped ?? [])
    lines.push(
      note(
        `stopped ${s.kind}${s.symbol ? ` ${s.symbol}` : ""} — it was set up for another market.`,
      ),
    );
  if (outcome.incomplete?.length)
    lines.push(
      err(
        `could not finish: ${outcome.incomplete.join(", ")} — check \`orders\` and \`schedules\``,
      ),
    );
  return lines;
}

/**
 * The feed picker, printed.
 *
 * **It names the vendor.** Every other surface says `live` or `simulated`,
 * because which company sells the prices is a commercial fact that changes
 * without the product changing — and a picker is the exception that proves the
 * rule, because a list you cannot name cannot be chosen from. Both travel: the
 * provider id, and the kind beside it. See `M4/E30`.
 */
function feedLines(state: Feeds): Line[] {
  const lines: Line[] = [];

  // Said first, and said plainly. A trader whose key stopped verifying is on
  // the simulator without having asked to be, and the only thing worse than
  // that is not being told.
  if (state.lapsed) {
    lines.push(
      err(`your ${state.lapsed} feed is no longer available — you are on ${state.selected}.`),
      note(`  ${howToGetItBack(state, state.lapsed)}`),
      note(""),
    );
  }

  lines.push(rowNote(`  ${pad("", 2)}${pad("feed", 12)}${pad("prices", 12)}why`));
  for (const feed of state.feeds) {
    const line = `  ${pad(feed.selected ? "*" : "", 2)}${pad(feed.provider, 12)}${pad(
      feed.kind ?? "unknown",
      12,
    )}${why(feed)}`;
    lines.push(feed.selected ? row(line) : rowNote(line));
  }

  lines.push(note(""));
  // `ADR-0039`: said, because it is why the header says real time.
  if (state.realtime)
    lines.push(
      note(
        `${state.selected} prices you in real time — your own key's plan includes real-time SIP.`,
      ),
    );
  if (!state.chosen) {
    // The difference between choosing and defaulting, which the API publishes
    // precisely so this line can exist.
    lines.push(note(`you have not chosen — ${state.selected} is the default.`));
  }
  lines.push(note("`feed <name>` to switch. `feed connect <name>` to use a key of your own."));
  return lines;
}

/**
 * What to tell a trader whose feed went away, given what could bring it back.
 *
 * **The advice used to be `feed connect <x>` unconditionally**, which is right
 * for a revoked key and wrong for a plan: you cannot paste a key to restore a
 * feed the platform holds the licence for, and telling somebody to try costs
 * them a failed attempt and a support message.
 *
 * `lapsed` is only a provider id — it cannot say WHY the grant went, because by
 * the time it is reported the grant is gone and there is nothing left to read a
 * `because` off. So the question asked is the one that has an answer:
 * `accepts_keys` lists the providers a trader may connect a key for, and a
 * provider that is not in it cannot be recovered by connecting one. That field
 * is already published for the `connect` flow, so this needs nothing new from
 * the API — which is why it is preferred to a `lapsed_because` nobody else
 * would read.
 */
function howToGetItBack(state: Feeds, lapsed: string): string {
  return state.accepts_keys.includes(lapsed)
    ? `\`feed connect ${lapsed}\` to reconnect a key for it.`
    : `${lapsed} came with your plan, and your plan no longer includes it.`;
}

/** Why a feed is on the list, in the words a trader would use. */
function why(feed: Feed): string {
  if (feed.because === "everyone") return "always available";
  // A feed the platform is licensed to redistribute, included in this account's
  // plan. There is no key behind it and nothing for the trader to revoke, so it
  // says where it came from rather than naming a credential it does not have.
  if (feed.because === "your_plan") return "included in your plan";
  const when = feed.verified_at?.slice(0, 10);
  return `your key ${feed.key_id ?? ""}${when ? ` · verified ${when}` : ""}`.trim();
}

function selectedOf(state: Feeds): Feed | undefined {
  return state.feeds.find((f) => f.provider === state.selected);
}

/** A recorder's status as the server sent it. Nothing is inferred between ticks. */
function recorderLines(s: {
  enabled: boolean;
  symbols: string[];
  next_tick_at: string | null;
  last: { at: string; code: string; printed: number; unknown: string[] } | null;
  printed: number;
}): Line[] {
  const lines: Line[] = s.enabled
    ? [out(`recording ${s.symbols.join(", ")}`), row(`  next tick   ${s.next_tick_at ?? "—"}`)]
    : [
        note(
          s.symbols.length > 0 ? `not recording (was ${s.symbols.join(", ")})` : "not recording",
        ),
      ];
  if (s.last) {
    const missing = s.last.unknown.length > 0 ? `; no quote for ${s.last.unknown.join(", ")}` : "";
    lines.push(
      row(
        `  last minute ${s.last.at}: ${s.last.code}, ${s.last.printed} new print${s.last.printed === 1 ? "" : "s"}${missing}`,
      ),
    );
  }
  lines.push(row(`  recorded    ${s.printed} print${s.printed === 1 ? "" : "s"} in all`));
  return lines;
}

function recorderRefusal(res: { error: string; code?: string; reason?: string }): Line {
  if (res.code === "live_entitlement_required")
    return err(
      "your prices are not live, so there is nothing to record — `feed` shows what prices you",
    );
  if (res.code === "invalid_symbols")
    return err("record on wants 1 to 20 comma-separated tickers, e.g. SPY,QQQ,NVDA,AAPL");
  if (res.code === "status_unavailable")
    return err("the recorder did not answer — it may still be running; ask again");
  return refusal(res);
}

/**
 * Hand the browser a file, byte for byte what `record-session.mjs` wrote.
 * False where there is no document to save from — a test, or a worker.
 */
function saveJson(name: string, body: unknown): boolean {
  const doc = globalThis.document;
  if (!doc || typeof URL.createObjectURL !== "function") return false;
  const url = URL.createObjectURL(
    new Blob([`${JSON.stringify(body, null, 2)}\n`], { type: "application/json" }),
  );
  const link = doc.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

function feedError(result: { error: string; status: number }): Line {
  if (result.status === 401) return err("sign in first — `login`");
  return err(`could not read your feeds: ${result.error}`);
}

/**
 * Connect a broker key, one question at a time.
 *
 * The secret arrives through a prompt rather than as an argument, which is what
 * keeps it out of ArrowUp — the shell drops a prompt answer before the history
 * push, for exactly this reason and for the sign-in code before it. It is still
 * visible in the input while it is typed; see `M4/E36`.
 */
async function connectFeed(
  provider: string | undefined,
  ctx: CommandContext,
): Promise<CommandResult> {
  if (!provider) {
    const state = await api.feeds();
    const accepts = state.ok ? state.data.accepts_keys : [];
    return {
      lines: [
        err("say which broker: `feed connect <name>`"),
        ...(accepts.length > 0 ? [note(`  keys can be connected for: ${accepts.join(", ")}`)] : []),
      ],
    };
  }

  const name = provider.toLowerCase();
  return {
    lines: [
      note(
        `connecting a ${name} key — it must be a PAPER key, and it is checked before anything is stored.`,
      ),
      note("  neither value is echoed or kept in history."),
    ],
    prompt: {
      label: "key id:",
      onAnswer: async (keyId) => askSecret(name, keyId.trim(), ctx),
    },
  };
}

function askSecret(provider: string, keyId: string, ctx: CommandContext): CommandResult {
  if (keyId === "") {
    return {
      lines: [note(`nothing entered — \`feed connect ${provider}\` to try again.`)],
    };
  }
  return {
    lines: [],
    prompt: {
      label: "secret:",
      onAnswer: async (secret) => storeKey(provider, keyId, secret.trim(), ctx),
    },
  };
}

async function storeKey(
  provider: string,
  keyId: string,
  secret: string,
  ctx: CommandContext,
): Promise<CommandResult> {
  if (secret === "") {
    return { lines: [note(`nothing entered — \`feed connect ${provider}\` to try again.`)] };
  }

  const result = await api.connectFeedKey(provider, keyId, secret);
  if (!result.ok) {
    // The server's sentence, not ours. It knows whether the key was refused as
    // a live key, refused by the broker, or refused because this deployment
    // holds no encryption key — and each of those is a different thing to do
    // next. See M2/E8.
    return { lines: [err(result.error)] };
  }

  ctx.refreshFeed();
  return {
    lines: [
      out(
        `connected — ${provider} account ${result.data.account.number} (${result.data.account.status}).`,
      ),
      ...feedLines(result.data),
    ],
  };
}

/**
 * `account mirror` — the broker paper account the selected account mirrors into (`M7/E34`).
 *
 * Deliberately not `feed connect`: that key prices every account and receives no
 * orders, while this one belongs to one account and receives only its orders.
 * The prompts are the same two, and neither value is echoed.
 */
/** `accounts closed` — the accounts `account close` hid, which only `account delete` reaches. */
async function closedAccountList(): Promise<CommandResult> {
  const closed = await api.listClosedAccounts();
  if (!closed.ok) return { lines: [err(`error: ${closed.error}`)] };
  if (closed.data.accounts.length === 0) return { lines: [note("no closed accounts")] };
  const width = Math.max(...closed.data.accounts.map((a) => a.name.length)) + 2;
  return {
    lines: closed.data.accounts.map((a) =>
      out(`  ${pad(a.name, width)}${a.id.slice(0, 8)}  closed ${a.closed_at?.slice(0, 10) ?? "?"}`),
    ),
  };
}

type AccountListing = {
  selected_account_id: string | null;
  accounts: { id: string; name: string }[];
};

/**
 * `account delete <name>` — erase an account for good (`ADR-0038`).
 *
 * Open accounts are looked up first, then closed ones; a closed name several
 * accounts share is asked for by the id `accounts closed` prints. Everything it
 * says comes from `GET …/deletion`, and both confirmations are the server's
 * too: typing the name here only decides whether the request is sent.
 */
async function accountDelete(
  rest: string[],
  list: AccountListing,
  ctx: CommandContext,
): Promise<CommandResult> {
  const [wanted] = rest;
  if (!wanted || rest.length !== 1) return { lines: [err("usage: account delete <name>")] };
  const matches = (a: { id: string; name: string }) =>
    a.name.toLowerCase() === wanted.toLowerCase() ||
    (wanted.length >= 8 && a.id.startsWith(wanted.toLowerCase()));
  let target = list.accounts.find(matches);
  if (!target) {
    const closed = await api.listClosedAccounts();
    if (!closed.ok) return { lines: [err(`error: ${closed.error}`)] };
    const found = closed.data.accounts.filter(matches);
    if (found.length > 1)
      return {
        lines: [
          err(`${found.length} closed accounts are named ${wanted} — delete one by its id:`),
          ...found.map((a) =>
            out(
              `  account delete ${a.id.slice(0, 8)}   closed ${a.closed_at?.slice(0, 10) ?? "?"}`,
            ),
          ),
        ],
      };
    target = found[0];
  }
  if (!target)
    return {
      lines: [err(`no account named ${wanted} — \`accounts\` and \`accounts closed\` list them`)],
    };

  const preview = await api.accountDeletion(target.id);
  if (!preview.ok) return { lines: [err(`could not delete: ${preview.reason ?? preview.error}`)] };
  const p = preview.data;
  const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
  const lines: Line[] = [
    err(
      `delete ${p.name}${p.closed_at ? ` (closed ${p.closed_at.slice(0, 10)})` : ""} — this erases it for good:`,
    ),
  ];
  if (p.open_orders)
    lines.push(
      err(
        `  ${plural(p.open_orders, "open order")} — discarded${p.mirror_connected ? " here, not canceled at Alpaca" : ""}`,
      ),
    );
  if (p.positions) lines.push(out(`  ${plural(p.positions, "position")}, erased with it`));
  if (p.enabled_strategies)
    lines.push(out(`  ${plural(p.enabled_strategies, "running strategy")}, stopped`));
  if (p.enabled_schedules.length)
    lines.push(
      out(
        `  intraday schedules ${p.enabled_schedules.map((s) => `${s.kind} ${s.symbol}`).join(", ")}, stopped`,
      ),
    );
  if (p.mirror_connected)
    lines.push(
      out(
        `  its Alpaca paper connection${p.sync_every_minutes ? " and scheduled sync" : ""} — the paper account itself keeps its orders and positions`,
      ),
    );
  lines.push(
    out(
      `  its history: ${plural(p.fills, "fill")}, ${plural(p.execution_probes, "execution probe")}, the ledger, valuations and syncs`,
    ),
    note("nothing is recoverable."),
  );

  const erase = async (discard: boolean): Promise<CommandResult> => {
    const deleted = await api.deleteAccount(p.id, p.name, discard);
    if (!deleted.ok)
      return { lines: [err(`could not delete: ${deleted.reason ?? deleted.error}`)] };
    if (p.id === list.selected_account_id)
      ctx.setSession({ accountId: undefined, accountName: undefined });
    ctx.refresh();
    ctx.refreshFeed();
    return { lines: [out(`deleted ${p.name}`)] };
  };
  const refused = (why: string): CommandResult => ({
    lines: [note(`not deleted — ${why}`)],
  });
  const discardPhrase = `discard ${plural(p.open_orders, "order")}`;
  return {
    lines,
    prompt: {
      label: `type ${p.name} to delete it:`,
      onAnswer: async (answer) => {
        if (answer.trim() !== p.name) return refused("the name did not match");
        if (!p.open_orders) return erase(false);
        return {
          lines: [err(`${p.name} still has ${plural(p.open_orders, "open order")}.`)],
          prompt: {
            label: `type ${discardPhrase}:`,
            onAnswer: async (again) =>
              again.trim() === discardPhrase
                ? erase(true)
                : refused("the orders were not discarded"),
          },
        };
      },
    },
  };
}

async function accountMirror(
  rest: readonly string[],
  list: { selected_account_id: string | null; accounts: Array<{ id: string; name: string }> },
  ctx: CommandContext,
): Promise<CommandResult> {
  const [verb, broker, kind, ...extra] = rest;
  const selected = list.accounts.find((a) => a.id === list.selected_account_id);
  if (!selected)
    return { lines: [note("no account selected — run `accounts` or `account create <name>`")] };
  const usage = {
    lines: [err("usage: account mirror [connect <broker> [paper|live]|disconnect]")],
  };

  if (!verb) {
    const shown = await api.mirror(selected.id);
    if (!shown.ok) return { lines: [err(`could not read the mirror: ${shown.error}`)] };
    const m = shown.data.mirror;
    return m
      ? {
          lines: [
            m.trading_account === "live"
              ? warn(
                  `${selected.name} trades through LIVE ${m.provider} account ${m.account_number ?? "?"} — real money.`,
                )
              : out(
                  `${selected.name} trades through ${m.provider} paper account ${m.account_number ?? "?"}.`,
                ),
            note(
              `  key ${m.key_id} · proved ${m.verified_at.slice(0, 10)}${m.linked_by ? ` · linked by ${m.linked_by}` : ""}`,
            ),
            note(
              m.sync_every
                ? `  syncs from it every ${m.sync_every} — next at ${m.next_sync_at ?? "?"}`
                : "  `sync preview` shows what a sync from it would change",
            ),
          ],
        }
      : {
          lines: [
            note(
              `${selected.name} has no mirror — \`account mirror connect alpaca\` gives it one.`,
            ),
          ],
        };
  }

  if (verb === "disconnect") {
    if (broker !== undefined || kind !== undefined) return usage;
    const revoked = await api.revokeMirrorKey(selected.id);
    if (!revoked.ok)
      return {
        lines: [
          revoked.status === 404
            ? note(`${selected.name} has no mirror to disconnect.`)
            : err(`could not disconnect: ${revoked.error}`),
        ],
      };
    return { lines: [out(`${selected.name} no longer mirrors its orders.`)] };
  }

  if (verb !== "connect" || !broker || extra.length > 0) return usage;
  const live = kind?.toLowerCase() === "live";
  if (kind !== undefined && !live && kind.toLowerCase() !== "paper") return usage;
  const name = broker.toLowerCase();
  const again = `\`account mirror connect ${name}${live ? " live" : ""}\` to try again.`;
  return {
    lines: live
      ? [
          warn(
            `connecting your LIVE ${name} account as ${selected.name}'s one venue — this key moves real money.`,
          ),
          note(
            "  from the moment it is connected, every order on this account is placed there too, same quantity, no cap — there is no off switch, only `account mirror disconnect`",
          ),
          note(
            "  checked against Alpaca's live API before anything is stored; neither value is echoed.",
          ),
        ]
      : [
          note(
            `connecting ${selected.name}'s own ${name} paper account — its orders go there and nowhere else.`,
          ),
          note(
            "  it must be a PAPER key, checked before anything is stored; neither value is echoed.",
          ),
        ],
    prompt: {
      label: live ? "live key id:" : "key id:",
      onAnswer: async (rawKeyId) => {
        const keyId = rawKeyId.trim();
        if (keyId === "") return { lines: [note(`nothing entered — ${again}`)] };
        return {
          lines: [],
          prompt: {
            label: live ? "live secret:" : "secret:",
            onAnswer: async (rawSecret) => {
              const secret = rawSecret.trim();
              if (secret === "") return { lines: [note(`nothing entered — ${again}`)] };
              const connected = await api.connectMirrorKey(selected.id, name, keyId, secret, live);
              if (!connected.ok) return { lines: [err(connected.error)] };
              const feedLinesAfter = await feedForConnected(selected.name, name);
              ctx.refresh();
              ctx.refreshFeed();
              const m = connected.data.mirror;
              const margin = connected.data.multiplier && connected.data.multiplier !== "1";
              return {
                lines: [
                  m.trading_account === "live"
                    ? warn(
                        `${selected.name} now trades through LIVE ${m.provider} account ${m.account_number ?? "?"} — real money.`,
                      )
                    : out(
                        `${selected.name} now trades through ${m.provider} paper account ${m.account_number ?? "?"}.`,
                      ),
                  ...(margin
                    ? [note(`  that is a margin account (multiplier ${connected.data.multiplier})`)]
                    : []),
                  // M7/I11: the account mirrors, not a strategy — say what that means.
                  note(
                    "  from now on every order on this account — yours, an agent's, a schedule's — is placed there too, and its cancels follow",
                  ),
                  ...feedLinesAfter,
                  note(
                    "  `sync preview` shows what making this account match it would change; `sync` does it, so the two start equal",
                  ),
                ],
              };
            },
          },
        };
      },
    },
  };
}

/**
 * After connecting an Alpaca account: an account still on the simulator is put
 * on the trader's Alpaca feed, because it cannot sync — or price what Alpaca
 * holds — otherwise (`M7/E40`, `M7/E41`). Any other feed is the trader's
 * choice and is left alone. Every word of the outcome is the server's.
 */
async function feedForConnected(account: string, broker: string): Promise<Line[]> {
  const state = await api.feeds();
  if (!state.ok || state.data.selected !== "synthetic") return [];
  if (!state.data.feeds.some((f) => f.provider === broker)) {
    return [
      note(
        `  ${account} is priced by the simulator, so it cannot sync yet — \`feed connect ${broker}\` then \`feed ${broker}\``,
      ),
    ];
  }
  const chosen = await api.selectFeed(broker);
  if (!chosen.ok)
    return [
      note(
        `  ${account} stays on the simulator: ${chosen.error} — \`feed ${broker}\` to try again`,
      ),
    ];
  return [
    out(`  ${account} is now priced by ${feedName(selectedOf(chosen.data))}, so it can sync.`),
    ...switchLines(chosen.data),
  ];
}

/** Disconnect the key behind a provider, named the way the trader picked it. */
async function disconnectFeed(provider: string | undefined): Promise<CommandResult> {
  const state = await api.feeds();
  if (!state.ok) return { lines: [feedError(state)] };

  const connected = state.data.feeds.filter((f) => f.because === "your_key" && f.id);
  if (connected.length === 0) {
    return { lines: [note("no broker key is connected.")] };
  }

  const target = provider
    ? connected.find((f) => f.provider === provider.toLowerCase())
    : connected.length === 1
      ? connected[0]
      : undefined;

  if (!target?.id) {
    return {
      lines: [
        err(provider ? `no ${provider} key is connected.` : "say which: `feed disconnect <name>`"),
        ...connected.map((f) => rowNote(`  ${f.provider}`)),
      ],
    };
  }

  const revoked = await api.revokeFeedKey(target.id);
  if (!revoked.ok) return { lines: [err(`could not disconnect: ${revoked.error}`)] };

  return {
    lines: [
      out(`disconnected ${target.provider}.`),
      // The selection is left alone on purpose and the entitlement is derived,
      // so the state that comes back is what will actually price them next —
      // which is the simulator if that key was what they were on.
      ...feedLines(revoked.data),
    ],
  };
}

/** The one word that accepts. Anything else re-asks rather than assuming. */
/**
 * The limits, and — when it differs — what is actually being enforced.
 *
 * The second half is printed only when there is something to say. An account
 * running on the defaults because it could not reach the database is a state a
 * trader needs to know about; an account quietly agreeing with the row is not
 * worth four lines of a terminal.
 */
function policyLines(state: PolicyState): Line[] {
  const p = state.policy;
  const lines: Line[] = [
    note("your limits"),
    row(`  ${pad("kill_switch", 24)} ${p.kill_switch ? "ON — nothing will be accepted" : "off"}`),
    row(`  ${pad("max_notional_per_order", 24)} ${formatUsd(p.max_notional_per_order)}`),
    row(
      `  ${pad("max_intraday_notional_per_order", 24)} ${formatUsd(p.max_intraday_notional_per_order)}`,
    ),
    row(`  ${pad("max_position_notional", 24)} ${formatUsd(p.max_position_notional)}`),
    row(`  ${pad("max_orders_per_day", 24)} ${p.max_orders_per_day}`),
    row(`  ${pad("symbol_allowlist", 24)} ${p.symbol_allowlist.join(", ") || "(any symbol)"}`),
    row(`  ${pad("symbol_denylist", 24)} ${p.symbol_denylist.join(", ") || "(none)"}`),
    row(`  ${pad("agents_may_trade", 24)} ${p.agents_may_trade ? "yes" : "no"}`),
  ];

  const today = state.enforcing.find((e) => e.orders_today !== null)?.orders_today;
  if (today !== undefined) lines.push(note(`${today} order(s) submitted today`));

  // An account not enforcing YOUR limits says so, loudly. Silence would be the
  // worst kind: an order refused, or allowed, by a number nobody chose.
  //
  // `unavailable` is the state since `M5/E6` — the account could not read the
  // row, so it refuses rather than falling back. `defaults` is the behaviour
  // that replaced, kept here because an object deployed before the change can
  // still be holding the word.
  const stale = state.enforcing.filter(
    (e) => e.source === "unavailable" || e.source === "defaults" || e.source === "unreachable",
  );
  for (const account of stale) {
    const id = account.account_id.slice(0, 8);
    lines.push(
      err(
        account.source === "unavailable"
          ? `account ${id} could not read your limits, so it is refusing orders rather than ` +
              "guessing — this usually clears on its own"
          : account.source === "defaults"
            ? `account ${id} could not read your limits and is on the platform ` +
              `defaults — ${wholeUsd(POLICY_DEFAULTS.max_notional_per_order)} per order, ` +
              `${wholeUsd(POLICY_DEFAULTS.max_position_notional)} per position, ` +
              `${POLICY_DEFAULTS.max_orders_per_day} a day`
            : `account ${id} did not answer; what it is enforcing is unknown`,
      ),
    );
  }

  return lines;
}

/** `25,000`, not `25,000.00`: a limit reads as a round number. */
const wholeUsd = (decimal: string) => formatUsd(decimal).replace(/\.00$/, "");

/**
 * `policy set <limit> <value>`.
 *
 * The parsing here is only about *shape* — which JSON type a field takes — and
 * every judgement about the value is the server's. A terminal that decided
 * "1000 is a reasonable limit" would be the UI holding logic the API does not
 * expose, which is the one rule this slice is not allowed to break.
 */
async function setLimit(name: string | undefined, rest: string[]): Promise<CommandResult> {
  if (!name) {
    return {
      lines: [err("usage: policy set <limit> <value>"), note(`  ${LIMIT_NAMES.join(", ")}`)],
    };
  }
  const limit = name.toLowerCase();
  if (!(LIMIT_NAMES as readonly string[]).includes(limit)) {
    return { lines: [err(`policy: unknown limit '${name}'`), note(`  ${LIMIT_NAMES.join(", ")}`)] };
  }

  const raw = rest.join(" ").trim();
  if (!raw) return { lines: [err(`usage: policy set ${limit} <value>`)] };

  let value: unknown;
  const kind = policyLimitKind(limit);
  if (kind === "switch") {
    const yes = ["on", "true", "yes"].includes(raw.toLowerCase());
    const no = ["off", "false", "no"].includes(raw.toLowerCase());
    if (!yes && !no) return { lines: [err(`${limit} takes on or off`)] };
    value = yes;
  } else if (kind === "symbols") {
    // `none` clears it. Without a word for "empty" the only way back from a
    // denylist is a request this terminal cannot express.
    value = raw.toLowerCase() === "none" ? [] : raw.split(/[\s,]+/).filter(Boolean);
  } else if (kind === "money") {
    // Sent as the string it was typed as. Passing it through Number() here is
    // exactly the float the API refuses, and refuses for good reason.
    value = raw;
  } else {
    const n = Number(raw);
    if (!Number.isInteger(n)) return { lines: [err(`${limit} takes a whole number`)] };
    value = n;
  }

  const updated = await api.setPolicy({ [limit]: value });
  if (!updated.ok) return { lines: [err(updated.error)] };

  const state = await api.policy();
  return {
    lines: state.ok
      ? [out(`${limit} is now ${describe(limit, updated.data.policy)}`), ...policyLines(state.data)]
      : [out(`${limit} is now ${describe(limit, updated.data.policy)}`)],
  };
}

/** One limit's new value, in the words the list above prints it in. */
function describe(limit: string, p: Policy): string {
  const value = (p as unknown as Record<string, unknown>)[limit];
  if (Array.isArray(value)) return value.join(", ") || "(none)";
  if (typeof value === "boolean") return value ? "on" : "off";
  if (policyLimitKind(limit) === "money") return formatUsd(String(value));
  return String(value);
}

/**
 * The platform halt, for whoever is allowed to touch it.
 *
 * It is on the same command as a trader's own limits deliberately: they are the
 * same question asked at two scales, and a separate `admin` command would be a
 * surface implying more than one control exists. A non-operator sees the
 * server's `403` here, which is the honest answer rather than a hidden verb.
 */
async function haltOrResume(verb: "halt" | "resume", reason: string): Promise<CommandResult> {
  const on = verb === "halt";
  const result = await api.setKillSwitch(on, reason || undefined);
  if (!result.ok) return { lines: [err(result.error)] };

  return {
    lines: [
      on
        ? err(`TRADING HALTED platform-wide${result.data.reason ? ` — ${result.data.reason}` : ""}`)
        : out("trading resumed platform-wide"),
      note(`by ${result.data.changedBy ?? "unknown"} at ${result.data.changedAt ?? "unknown"}`),
    ],
  };
}

/*
 * ── agents ───────────────────────────────────────────────────────────────────
 *
 * **Every line printed below either came from the server or is a label.** The
 * terminal must never compute something the API does not expose (`PRODUCT.md`),
 * and that rule is load-bearing here in a way it is not elsewhere: a refusal
 * reason is a *sentence the server wrote*, and reformatting one means parsing
 * it. So `blocked_reason` is printed verbatim — which is exactly why `M5/E1`
 * fixed the money formatting on the server rather than here.
 */

/**
 * How long `agent run` waits before handing over to `agent status`.
 *
 * A run is a Workflow and the route answers `202` immediately, so something has
 * to wait. Waiting *here* is what makes `agent run momentum` a single
 * instruction that reports what happened, which is the whole shape of this
 * terminal — and the shell shows `aria-busy` throughout, so the wait is visible
 * rather than mysterious.
 *
 * The timeout is a real possibility rather than a safety net: a cold Workflow
 * and a cold model can exceed it. So it is not an error — the run is still
 * going, and the message says how to pick it back up.
 */
const AGENT_POLL_MS = 1_000;
const AGENT_WAIT_MS = 90_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function startAgent(
  variant: string | undefined,
  ctx: CommandContext,
): Promise<CommandResult> {
  if (!variant) {
    return {
      lines: [err(`usage: agent run <strategy> — ${AGENT_STRATEGIES.join(" or ")}`)],
    };
  }

  const started = await api.startAgentRun(variant, ctx.session.accountId);
  if (!started.ok) return { lines: [refusal(started)] };

  const lines: Line[] = [note(`${started.data.variant} started — run ${started.data.run_id}`)];

  const deadline = Date.now() + AGENT_WAIT_MS;
  let detail = await api.agentRun(started.data.run_id);

  while (detail.ok && !agentRunSettled(detail.data.run) && Date.now() < deadline) {
    await sleep(AGENT_POLL_MS);
    detail = await api.agentRun(started.data.run_id);
  }

  if (!detail.ok) return { lines: [...lines, refusal(detail)] };

  // Stopped to ask, rather than finished. `runLines` prints the question and
  // how to answer it — polling on would burn the rest of the wait and then
  // report a healthy run as one that timed out.
  if (detail.data.run.status === "awaiting_approval") {
    return { lines: [...lines, ...runLines(detail.data)] };
  }

  if (!agentRunFinished(detail.data.run)) {
    // Not an error. The run is still going and the row is where it lives.
    return {
      lines: [
        ...lines,
        note(`still running after ${Math.round(AGENT_WAIT_MS / 1000)}s`),
        note(`  agent status ${started.data.run_id}`),
      ],
    };
  }

  // The account moved if anything was applied, so the header is stale.
  if (detail.data.run.effects_applied > 0) ctx.refresh();

  return { lines: [...lines, ...runLines(detail.data)] };
}

async function agentStatus(runId: string | undefined): Promise<CommandResult> {
  if (!runId) {
    const recent = await api.agentRuns();
    if (!recent.ok) return { lines: [refusal(recent)] };
    const latest = recent.data.runs[0];
    if (!latest) return { lines: [note("no agent has run on this account yet")] };
    runId = latest.id;
  }

  const detail = await api.agentRun(runId);
  if (!detail.ok) return { lines: [refusal(detail)] };
  return { lines: runLines(detail.data) };
}

/**
 * Answer a run that stopped to ask.
 *
 * **Non-blocking, and it does not prompt.** A modal question would make the
 * whole feature depend on the tab staying open, which is the exact failure this
 * slice exists to remove: the point of a durable wait is that the answer can
 * come days later, from a session that has never seen the run before.
 *
 * With no run id it answers the most recent waiting run, because "the thing you
 * were just asked" is what somebody typing `agent approve` means — and it says
 * which run it answered rather than leaving that to be inferred.
 */
async function answerAgent(
  runId: string | undefined,
  approved: boolean,
  reason: string,
  ctx: CommandContext,
): Promise<CommandResult> {
  if (!runId) {
    const recent = await api.agentRuns();
    if (!recent.ok) return { lines: [refusal(recent)] };
    const waiting = recent.data.runs.find((run) => run.status === "awaiting_approval");
    if (!waiting) {
      return { lines: [note("no agent run is waiting for an answer")] };
    }
    runId = waiting.id;
  }

  const answered = await api.answerAgentRun(runId, approved, reason.trim() || undefined);
  if (!answered.ok) return { lines: [refusal(answered)] };

  const lines: Line[] = [note(`${approved ? "approved" : "rejected"} — run ${runId}`)];

  // Approving releases durable work that has not happened yet, so this waits
  // the same way `agent run` does rather than reporting a status it has not
  // seen. A rejection has nothing left to watch: the run is already ending.
  const deadline = Date.now() + AGENT_WAIT_MS;
  let detail = await api.agentRun(runId);
  while (detail.ok && !agentRunFinished(detail.data.run) && Date.now() < deadline) {
    await sleep(AGENT_POLL_MS);
    detail = await api.agentRun(runId);
  }
  if (!detail.ok) return { lines: [...lines, refusal(detail)] };

  if (!agentRunFinished(detail.data.run)) {
    return {
      lines: [...lines, note("still running"), note(`  agent status ${runId}`)],
    };
  }

  if (detail.data.run.effects_applied > 0) ctx.refresh();
  return { lines: [...lines, ...runLines(detail.data)] };
}

/**
 * The day's model spend, and the ceiling it is measured against.
 *
 * **Nothing here is computed from anything else here.** `remaining_usd` is the
 * server's, not `cap - spent`; the two agree today and the moment they do not
 * is the moment a client-side subtraction starts lying about a refusal that has
 * already happened. Same rule the whole terminal follows — see PRODUCT.md.
 *
 * The platform line is printed only when there is spend on it, because a
 * trader stopped by a ceiling they cannot act on at least deserves to see the
 * total behind it, and one that has never been near it does not need the row.
 */
async function intradayCommand(
  kind: IntradayKind,
  args: string[],
  ctx: CommandContext,
): Promise<CommandResult> {
  const stop = (response: { error: string; reason?: string; code?: string }): Line[] => [
    err(`${response.code ? `${response.code}: ` : ""}${response.reason ?? response.error}`),
  ];
  const accountId = ctx.session.accountId;
  if (!accountId)
    return { lines: [err("open an account before configuring a strategy — run `account`")] };
  const [action, rawSymbol, ...rest] = args;
  const symbol = rawSymbol?.toUpperCase();
  const usage = () => ({
    lines: [err(`usage: ${usageOf(CATALOG[kind])}`), note(`  help ${kind}`)],
  });
  if (!symbol || symbol.startsWith("-") || !["on", "off", "log"].includes(action ?? "")) {
    return usage();
  }
  if (action !== "on" && rest.length) return usage();
  if (action === "off") {
    const response = await api.disableIntraday(accountId, kind, symbol);
    if (!response.ok) return { lines: stop(response) };
    return {
      lines: [
        out(`${kind} ${symbol} disabled`),
        // A schedule that was failing and one that was running look the same
        // from a log; the server says which, once, as it clears it (M7/E15).
        ...(response.data.last_error
          ? [err(`its last tick had failed: ${response.data.last_error}`)]
          : []),
      ],
    };
  }
  if (action === "log") {
    const response = await api.intradayLog(accountId, kind, symbol);
    if (!response.ok) return { lines: stop(response) };
    const { config, error, pending } = response.data;
    return {
      lines: [
        // The schedule leads. Whether it is running is the question a log is
        // opened to answer, and a page of old ticks under a schedule that
        // failed to arm reads as healthy (N137).
        config
          ? (config.enabled ? out : err)(
              `${kind} ${symbol} ${config.enabled ? "enabled" : "disabled"} — ${describeIntraday(config)}`,
            )
          : note(`${kind} ${symbol} has never been configured on this account`),
        ...(config ? decidesLine(response.data.decides_on) : []),
        ...(config ? restingLine(config, response.data.exit_at_broker) : []),
        ...exitLine(response.data.exit),
        ...(response.data.hold_until && Date.parse(response.data.hold_until.at) > Date.now()
          ? [
              note(
                `  buying nothing until ${response.data.hold_until.at} (${response.data.hold_until.code})`,
              ),
            ]
          : []),
        ...(response.data.history_error
          ? [
              err(
                `  could not read the feed's minute history (${response.data.history_error}) — deciding on its own window`,
              ),
            ]
          : []),
        ...(error
          ? [err(pending ? `${error} — evidence is still pending` : error)]
          : pending
            ? [err("evidence_pending")]
            : []),
        ...(response.data.probes.length
          ? response.data.probes.flatMap((probe) => [
              row(
                `${probe.created_at}  ${probe.symbol}  ${probe.action}  ${probe.code}  qty ${probe.qty ?? "—"}`,
              ),
              rowNote(
                `  arrival ${probe.arrival_mid ?? "—"} at ${probe.arrival_as_of ?? "—"}${probe.arrival_source ? ` (${probe.arrival_source})` : ""}  fill ${probe.fill_price ?? "—"}  shortfall ${probe.shortfall_bps ?? "—"} bps`,
              ),
              ...(PROBE_CODE_HINTS[probe.code]
                ? [rowNote(`  ${PROBE_CODE_HINTS[probe.code]}`)]
                : []),
              ...(probe.sip_bid && probe.sip_ask
                ? [
                    rowNote(
                      `  sip ${probe.sip_bid} / ${probe.sip_ask} at ${probe.sip_as_of ?? "—"}`,
                    ),
                  ]
                : probe.sip_code
                  ? [rowNote(`  sip ${probe.sip_code}`)]
                  : []),
              ...(probe.feed_error ? [rowNote(`  feed ${probe.feed_error}`)] : []),
              ...(probe.order_id ? [rowNote(`  order ${probe.order_id}`)] : []),
            ])
          : [note(`no ${kind} probes for ${symbol}`)]),
      ],
    };
  }

  if (rest.some((t) => t === "--price" || t.startsWith("--price="))) {
    // Retired in M7/E44: a schedule decides on what prices its account.
    return {
      lines: [
        err("--price is gone: a schedule decides on the price its account is priced by"),
        note(
          "  real-time SIP when your Alpaca key's plan includes it, the delayed tape otherwise — `feed` shows which",
        ),
      ],
    };
  }
  if (rest.some((t) => t === "--mirror" || t.startsWith("--mirror="))) {
    // Retired in M7/I11: said plainly, because a trader who used it expects it.
    return {
      lines: [
        err("--mirror is gone: a connected account mirrors every order, a schedule's included"),
        note(
          "  `account mirror connect alpaca` connects the selected account; `account mirror` shows it",
        ),
      ],
    };
  }
  const parsed = parseFlags(rest, CATALOG[kind].flags, `${kind} on`);
  if ("error" in parsed) return { lines: [err(parsed.error), note(`  help ${kind}`)] };
  const [notional, ...extra] = parsed.positional;
  if (kind === "band" && notional !== undefined) {
    return {
      lines: [
        err(
          `band on takes no notional — its size is --tranche (default ${INTRADAY_DEFAULTS.tranche})`,
        ),
        note("  help band"),
      ],
    };
  }
  if (extra.length > 0) return usage();
  const options: IntradayOptions = {};
  if (notional !== undefined) {
    if (!/^\d+(?:\.\d+)?$/.test(notional)) {
      // A `shares` size is shares, not dollars (M8/I10).
      return {
        lines: [
          err(
            kind === "shares"
              ? `"${notional}" is not a number of shares — try \`shares on ${symbol} 0.1\``
              : `"${notional}" is not an amount — try \`dca on ${symbol} 100\``,
          ),
        ],
      };
    }
    if (kind === "shares") options.qty = notional;
    else options.notional = notional;
  }
  for (const [flag, value] of Object.entries(parsed.values)) {
    const field = Object.hasOwn(INTRADAY_FLAG_FIELDS, flag)
      ? INTRADAY_FLAG_FIELDS[flag]
      : undefined;
    if (!field) return { lines: [err(`${flag} is declared for ${kind} but not wired to a field`)] };
    if (
      field === "band_bps" ||
      field === "lookback" ||
      field === "interval_minutes" ||
      field === "max_spread_bps" ||
      field === "below_bps" ||
      field === "cooldown_minutes"
    ) {
      options[field] = Number(value);
    } else if (field === "flat_weekend") {
      options.flat_weekend = value === "on";
    } else if (field === "exit_action") {
      // Typed with dashes like every other word here; the API's names use underscores.
      options.exit_action = value.replace(/-/g, "_") as IntradayExitAction;
    } else options[field] = value;
  }
  const response = await api.putIntraday(accountId, kind, symbol, options);
  if (!response.ok) return { lines: stop(response) };
  const config = response.data.config;
  return {
    lines: [
      // Restated from what the API stored, not echoed from what was typed: a
      // default the trader did not know about is exactly what this line exists
      // to show them.
      out(
        `${kind} ${symbol} ${config.enabled ? "enabled" : "disabled"} — ${describeIntraday(config)}`,
      ),
      ...decidesLine(response.data.decides_on),
      note("valuation cadence: half-hourly while open"),
    ],
  };
}

/**
 * What a probe code means, for the ones a trader cannot reasonably guess.
 *
 * The grid prints the code itself, which is the right default — it is what the
 * database holds and what a bug report should quote. These are the ones where
 * the code alone sends somebody to look in the wrong place. `account_live` was
 * reported as `account_not_active` until `M8/I1`, which said the account was
 * disabled when it was working perfectly and simply real-money.
 */
const PROBE_CODE_HINTS: Record<string, string> = {
  account_live: "this is a real-money account — schedules cannot run on one yet",
  account_not_found: "that account no longer exists, or is not yours",
  account_not_active: "the account is closed or restricted — `accounts` shows its status",
  above_dip: "no buy: the price is not yet --below bps under the --lookback mean",
  exit_cancel_pending:
    "no buy: the exit resting at Alpaca is not confirmed cancelled yet, and Alpaca refuses a buy beside it",
  exit_filling:
    "no buy: the exit at Alpaca filled while it was being cancelled — the next tick books it",
  cooldown: "no buy: still cooling down after the last exit (--cooldown)",
  weekend: "no buy: flat for the weekend until the next session",
  flat_weekend: "sold everything before the week's last close (--flat-weekend)",
  take_profit: "the take profit was hit — this schedule's exit, `dca log` says what it did next",
  stop_loss: "the stop loss was hit — this schedule's exit, `dca log` says what it did next",
};

/** Which request field each declared flag sets. A declared flag missing here is refused, never ignored. */
const INTRADAY_FLAG_FIELDS: Readonly<Record<string, keyof IntradayOptions>> = {
  "--interval": "interval_minutes",
  "--max": "max_position",
  "--band": "band_bps",
  "--tranche": "tranche",
  "--core": "core",
  "--lookback": "lookback",
  "--max-spread": "max_spread_bps",
  "--below": "below_bps",
  "--flat-weekend": "flat_weekend",
  "--cooldown": "cooldown_minutes",
  "--tp-pct": "take_profit_pct",
  "--tp-usd": "take_profit_usd",
  "--sl-pct": "stop_loss_pct",
  "--sl-usd": "stop_loss_usd",
  "--on-exit": "exit_action",
};

/** A schedule in the words it was set in, from the configuration the server returned. */
function describeIntraday(c: IntradayConfig): string {
  const described =
    c.kind === "shares"
      ? `buys ${c.qty} shares every ${c.interval_minutes} min, holding at most ${c.max_position} shares`
      : c.kind === "dca"
        ? `buys ${c.notional} every ${c.interval_minutes} min, holding at most ${c.max_position} shares`
        : `trades ${c.tranche} at ±${c.band_bps} bps around a ${c.lookback}-minute mean, holding ${c.core}–${c.max_position} shares`;
  return `${described}${describeExits(c)}${describeBuying(c)}`;
}

/** A DCA schedule's exits, as the server stored them (M8/E4); nothing when none is set. */
function describeExits(c: IntradayConfig): string {
  const either = (pct?: string | null, usd?: string | null) =>
    [pct ? `${pct}%` : "", usd ? `$${usd}` : ""].filter(Boolean).join(" or ");
  const profit = either(c.take_profit_pct, c.take_profit_usd);
  const loss = either(c.stop_loss_pct, c.stop_loss_usd);
  if (!profit && !loss) return "";
  const then = {
    sell_stop: "sells everything and stops",
    sell_continue: "sells everything and keeps buying",
    stop: "stops and keeps the shares",
  }[c.exit_action ?? "sell_stop"];
  const when = [profit && `up ${profit}`, loss && `down ${loss}`].filter(Boolean).join(" or ");
  return `; once ${when}, ${then}`;
}

/**
 * Where a DCA schedule's exit lives (M8/I9): resting at Alpaca on a mirrored
 * account — with its order, or why there is none — or checked each tick here.
 */
function restingLine(c: IntradayConfig, at: ExitAtBroker | null | undefined): Line[] {
  const hasExit = c.take_profit_pct || c.take_profit_usd || c.stop_loss_pct || c.stop_loss_usd;
  if (c.kind === "band" || !hasExit) return [];
  if (!at) return [note("  exits are checked each tick on this account's feed")];
  const lines: Line[] = [];
  if (at.resting) {
    const r = at.resting;
    const legs = [
      r.take_profit ? `take profit ${r.take_profit}` : "",
      r.stop_loss ? `stop ${r.stop_loss}` : "",
    ]
      .filter(Boolean)
      .join(", ");
    lines.push(
      note(`  resting at Alpaca: sell ${r.qty} shares — ${legs} (order ${r.venue_order_id})`),
    );
  }
  if (at.fraction) {
    // Alpaca holds a fraction only as one plain day order (M8/E6).
    const f = at.fraction;
    const what = `sell ${f.qty} shares — ${f.kind === "stop" ? "stop" : "take profit"} ${f.price}, a day order (order ${f.venue_order_id})`;
    lines.push(note(at.resting ? `  and the fraction: ${what}` : `  resting at Alpaca: ${what}`));
  }
  if (at.resting || at.fraction) {
    // Something rests; nothing more to say about it.
  } else if (at.note === "no_position")
    lines.push(note("  no position at Alpaca yet — nothing to protect until the first buy fills"));
  else if (at.note === "under_one_share")
    lines.push(
      err(
        "  nothing rests at Alpaca yet: under one share, and the fraction's day order is not placed yet — the next tick places it",
      ),
    );
  else lines.push(note("  nothing rests at Alpaca yet — the next tick places it"));
  if (at.finishing) lines.push(note("  an exit filled at Alpaca; selling the leftover fraction"));
  if (at.error) lines.push(err(`  the last attempt at Alpaca failed: ${at.error}`));
  return [...lines, ...liveExitLines(at.live)];
}

/** The exit's copies on the LIVE account (M8/I13), in the real-money colour. */
function liveExitLines(live: ExitAtBroker["live"]): Line[] {
  if (!live) return [];
  const lines: Line[] = [];
  const copy = (c: LiveExitCopy): string => {
    const terms =
      c.kind === "oco"
        ? [
            c.take_profit ? `take profit ${c.take_profit}` : "",
            c.stop_loss ? `stop ${c.stop_loss}` : "",
          ]
            .filter(Boolean)
            .join(", ")
        : c.kind === "market"
          ? "at market"
          : `${c.kind === "stop" ? "stop" : "take profit"} ${c.price}`;
    const where = c.venue_order_id
      ? `${c.status ?? "sent"}, order ${c.venue_order_id}`
      : (c.status ?? "not sent");
    return `sell ${c.qty} shares — ${terms} (${where})${c.note ? ` — ${c.note}` : ""}`;
  };
  for (const c of [live.resting, live.fraction, live.rest])
    if (c) lines.push(warn(`  LIVE: ${copy(c)}`));
  for (const c of live.kept)
    lines.push(warn(`  LIVE, filled at paper, resting at live: ${copy(c)}`));
  if (live.error) lines.push(err(`  the live copy did nothing this tick: ${live.error}`));
  return lines;
}

/** The buying options, as stored (M8/I11); nothing when none is set. */
function describeBuying(c: IntradayConfig): string {
  const parts = [
    c.below_bps ? `buys only ${c.below_bps} bps under its ${c.lookback}-minute mean` : "",
    c.flat_weekend ? "goes flat for the weekend" : "",
    c.cooldown_minutes ? `waits ${c.cooldown_minutes} min after an exit` : "",
  ].filter(Boolean);
  return parts.length ? `; ${parts.join(", ")}` : "";
}

/** The exit that stopped a schedule, or the last one it sold through (M8/E4). */
function exitLine(exit: IntradayExit | null | undefined): Line[] {
  if (!exit) return [];
  const what =
    exit.reason === "take_profit"
      ? "took profit"
      : exit.reason === "flat_weekend"
        ? "went flat for the weekend"
        : "stopped the loss";
  return [
    note(
      `  ${what} at ${exit.at}${exit.stopped ? " and stopped" : ", then kept buying"} — probe ${exit.probe_id}`,
    ),
  ];
}

/** Which price the ticks decide on, as the server said (M7/E44). */
function decidesLine(on: DecidesOn | null | undefined): Line[] {
  if (on === "sip_realtime")
    return [note("  deciding on real-time SIP — your account is priced in real time")];
  if (on === "delayed_sip")
    return [note("  deciding on the 15-minute-delayed tape — your account is priced delayed")];
  return [];
}

/**
 * `sync` — reset the selected account to its connected Alpaca paper account (`ADR-0037`).
 *
 * Every figure is the server's plan; the terminal only lays it out. A refusal
 * says what to do about it, because each one is something the trader can fix.
 */
async function syncCommand(args: string[], ctx: CommandContext): Promise<CommandResult> {
  const accountId = ctx.session.accountId;
  if (!accountId) return { lines: [err("open an account first — run `account`")] };
  const usage = { lines: [err(`usage: ${usageOf(CATALOG.sync)}`), note("  help sync")] };
  const [verb, value, ...extra] = args;

  if (verb === undefined || verb === "preview") {
    if (value !== undefined) return usage;
    const preview = verb === "preview";
    const res = await api.sync(accountId, preview);
    if (!res.ok) return { lines: syncRefusal(res) };
    return { lines: describeSync(res.data.sync) };
  }

  if (verb === "every") {
    if (value === undefined || extra.length > 0) return usage;
    const off = value.toLowerCase() === "off";
    const res = await api.syncSchedule(accountId, off ? null : value);
    if (!res.ok) return { lines: syncRefusal(res) };
    const m = res.data.mirror;
    return {
      lines: [
        m.sync_every
          ? out(`syncing every ${m.sync_every} — next at ${m.next_sync_at ?? "?"}`)
          : out("the scheduled sync is off"),
      ],
    };
  }

  if (verb === "log") {
    if (value !== undefined) return usage;
    const res = await api.syncs(accountId);
    if (!res.ok) return { lines: [refusal(res)] };
    if (res.data.syncs.length === 0)
      return { lines: [note("no syncs yet — `sync preview` shows what one would do")] };
    return {
      lines: [
        rowNote(`${pad("id", 10)}${pad("when", 18)}${pad("by", 10)}${pad("outcome", 26)}flow`),
        ...res.data.syncs.map((s) =>
          row(
            `${pad(s.id.slice(0, 8), 10)}${pad(s.created_at.slice(0, 16).replace("T", " "), 18)}${pad(
              s.trigger,
              10,
            )}${pad(s.code, 26)}${s.flow ?? "—"}`,
          ),
        ),
      ],
    };
  }

  if (verb === "show") {
    if (value === undefined || extra.length > 0) return usage;
    const listed = await api.syncs(accountId, 100);
    if (!listed.ok) return { lines: [refusal(listed)] };
    const matches = listed.data.syncs.filter((s) => s.id.startsWith(value.toLowerCase()));
    if (matches.length !== 1)
      return {
        lines: [
          err(
            matches.length === 0
              ? `no sync starts with ${value} — \`sync log\` lists them`
              : `${value} matches ${matches.length} syncs — type more of the id`,
          ),
        ],
      };
    const shown = await api.syncShow(accountId, (matches[0] as { id: string }).id);
    if (!shown.ok) return { lines: [refusal(shown)] };
    const s = shown.data.sync;
    return {
      lines: [
        out(
          `sync ${s.id.slice(0, 8)} · ${s.trigger} · ${s.code} · ${s.created_at.slice(0, 19).replace("T", " ")}`,
        ),
        ...(s.detail ? [note(`  ${s.detail}`)] : []),
        ...(s.account_number
          ? [note(`  Alpaca paper account ${s.account_number}, read at ${s.snapshot_as_of ?? "?"}`)]
          : []),
        ...(s.plan ? planLines(s.plan) : []),
        ...(s.flow === null ? [] : [note(`  flow ${s.flow} — an external flow, never scored`)]),
      ],
    };
  }

  return usage;
}

/** What each refusal asks the trader to do. */
const SYNC_HINTS: Record<string, string> = {
  sync_no_key:
    "`account mirror connect alpaca` connects this account to its own Alpaca account (`… alpaca live` for your live one)",
  sync_wrong_market: "`feed alpaca` prices this account from your Alpaca feed",
  sync_key_refused:
    "the key stopped working at Alpaca — connect it again with `account mirror connect alpaca`",
  sync_unavailable: "Alpaca did not answer; nothing changed — try again in a minute",
  sync_mirror_pending:
    "a mirrored order is still on its way to Alpaca or settling there — `orders` shows it; sync once it has",
  sync_unsupported_position:
    "only long US stock positions can be synced — a short or a non-stock asset at Alpaca stops the whole sync",
  sync_unsupported_account: "this Alpaca account cannot be represented here",
};

function syncRefusal(res: { error: string; reason?: string; code?: string }): Line[] {
  const said = res.reason ?? res.error;
  const hint = res.code ? SYNC_HINTS[res.code] : undefined;
  // M7/E37: the server's own words often already name the command; say it once.
  const command = hint ? /`[^`]+`/.exec(hint)?.[0] : undefined;
  const repeats = command !== undefined && said.includes(command);
  return [err(`nothing changed — ${said}`), ...(hint && !repeats ? [note(`  ${hint}`)] : [])];
}

function describeSync(s: SyncResult): Line[] {
  const p = s.plan;
  const positions = p.target.positions.length;
  const head =
    s.status === "no_change"
      ? out("already matches its Alpaca account — nothing to change")
      : out(
          `${s.status === "dry_run" ? "would sync" : "synced"} from Alpaca — cash ${p.target.cash}, ${positions} position${
            positions === 1 ? "" : "s"
          }, ${p.created.length} open order${p.created.length === 1 ? "" : "s"} re-created${
            s.idempotent ? " (already applied)" : ""
          }`,
        );
  return [
    head,
    ...(s.status === "no_change" ? [] : planLines(p)),
    ...(s.status === "no_change"
      ? []
      : [
          note(
            `  equity ${s.equity_before} → ${s.equity_after}: a flow of ${s.flow}, never scored${
              s.flow_priced ? "" : " (a symbol had no price; valued at cost)"
            }`,
          ),
        ]),
    ...(s.status === "dry_run" ? [note("  nothing changed — `sync` applies it")] : []),
    ...(s.status === "applied" && !s.recorded
      ? [note("  applied; the record is still on its way to the database")]
      : []),
  ];
}

function planLines(p: SyncPlan): Line[] {
  const lines: Line[] = [];
  const bySymbol = new Map<string, string[]>();
  for (const f of p.fills) {
    const words = bySymbol.get(f.symbol) ?? [];
    words.push(`${f.role} ${f.qty.replace(/\.?0+$/, "")} @ ${f.price.replace(/0{1,4}$/, "")}`);
    bySymbol.set(f.symbol, words);
  }
  for (const [symbol, words] of bySymbol)
    lines.push(row(`  ${pad(symbol, 7)}${words.join(" · ")}`));
  if (p.cash_transfer !== "0.0000") lines.push(note(`  cash moved ${p.cash_transfer}`));
  for (const d of p.qty_dust ?? [])
    lines.push(
      note(
        `  ${d.symbol}: Alpaca holds ${d.broker}; kept ${d.kept.replace(/\.?0+$/, "")} here — 8 places is the most this account holds`,
      ),
    );
  if (p.cash_shortfall)
    lines.push(
      warn(
        `  Alpaca's cash is ${p.cash_shortfall} below zero (margin) — cash here stops at 0.0000, and margin is not modelled`,
      ),
    );
  if (p.canceled.length > 0)
    lines.push(note(`  ${p.canceled.length} open order(s) of ours cancelled`));
  for (const o of p.created)
    lines.push(
      note(
        `  re-created ${o.side} ${o.qty.replace(/\.?0+$/, "")} ${o.symbol} ${o.type} ${o.limit_price ?? o.stop_price ?? ""} ${o.time_in_force} (${o.client_order_id})`,
      ),
    );
  for (const n of p.not_recreated)
    lines.push(note(`  not re-created: ${n.symbol} ${n.client_order_id} — ${n.reason}`));
  for (const r of p.residue)
    lines.push(
      note(`  ${r.symbol} cost basis ${r.ours} here, ${r.broker} at Alpaca (${r.difference})`),
    );
  return lines;
}

/**
 * `gap dca|band <symbol> [--date]` — planned against real, as the server computed it.
 *
 * Every figure is the API's. The two gaps stay in two columns and are never
 * added together, because a partial fill at Alpaca is a quantity, not a price.
 */
async function gapCommand(args: string[], ctx: CommandContext): Promise<CommandResult> {
  const accountId = ctx.session.accountId;
  if (!accountId) return { lines: [err("open an account first — run `account`")] };
  const usage = { lines: [err(`usage: ${usageOf(CATALOG.gap)}`), note("  help gap")] };
  const parsed = parseFlags(args, CATALOG.gap.flags, "gap");
  if ("error" in parsed) return { lines: [err(parsed.error), note("  help gap")] };
  const [kind, rawSymbol, ...extra] = parsed.positional;
  const date = parsed.values["--date"];
  if (date !== undefined && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return { lines: [err(`--date takes a date like 2026-09-11, not ${date}`)] };
  }
  if (kind === undefined) return accountGapLines(accountId, date);
  if ((kind !== "dca" && kind !== "band" && kind !== "shares") || !rawSymbol || extra.length > 0)
    return usage;
  const response = await api.intradayGap(accountId, kind, rawSymbol.toUpperCase(), date);
  if (!response.ok) return { lines: [refusal(response)] };
  const g = response.data;
  const s = g.session;
  if (s.orders === 0) {
    return { lines: [note(`no filled ${kind} orders on ${g.symbol} for ${g.date}`)] };
  }
  return {
    lines: [
      out(
        `${kind} ${g.symbol} ${g.date} — ${s.orders} filled here, ${s.mirrored} mirrored, ${s.filled_real} filled at Alpaca${
          g.pending_mirrors ? `, ${g.pending_mirrors} awaiting Alpaca` : ""
        }`,
      ),
      g.mark
        ? note(`marked at ${g.mark.price}, the session's last arrival mid (${g.mark.as_of ?? "—"})`)
        : note("no arrival price in this session, so no P&L"),
      row(
        `  planned P&L ${s.planned_pnl ?? "—"}   on mirrored orders ${s.planned_pnl_mirrored ?? "—"}   real P&L ${s.real_pnl ?? "—"}   quantity gap ${s.quantity_gap}`,
      ),
      rowNote(
        `  ${pad("time", 10)}${pad("side", 5)}${pad("qty", 12)}${pad("ours", 12)}${pad("alpaca", 16)}${pad("gap bps", 11)}${pad("qty gap", 12)}sip ref (ours / alpaca bps)`,
      ),
      ...g.orders.map((o) =>
        row(
          `  ${pad(o.created_at.slice(11, 19), 10)}${pad(o.side, 5)}${pad(o.planned.qty, 12)}${pad(o.planned.fill, 12)}${pad(
            o.mirror ? (o.mirror.fill ?? o.mirror.code) : "not mirrored",
            16,
          )}${pad(o.execution_gap_bps ?? "—", 11)}${pad(o.quantity_gap ?? "—", 12)}${
            o.sip
              ? `${o.sip.reference} (${o.sip.planned_gap_bps} / ${o.sip.real_gap_bps ?? "—"})`
              : (o.sip_code ?? "—")
          }`,
        ),
      ),
    ],
  };
}

/**
 * `gap` with no strategy: every order on the account this session beside its
 * Alpaca mirror (`M7/I11`). Every figure is the server's.
 */
async function accountGapLines(accountId: string, date?: string): Promise<CommandResult> {
  const response = await api.accountGap(accountId, date);
  if (!response.ok) return { lines: [refusal(response)] };
  const g = response.data;
  const s = g.session;
  const pending = g.pending_mirrors ? `, ${g.pending_mirrors} still with Alpaca` : "";
  if (s.orders === 0) return { lines: [note(`no orders on ${g.date}${pending}`)] };
  return {
    lines: [
      out(
        `${g.date} — ${s.orders} orders here, ${s.mirrored} mirrored, ${s.filled_real} filled at Alpaca${pending}`,
      ),
      rowNote(
        `  ${pad("time", 10)}${pad("side", 5)}${pad("symbol", 8)}${pad("by", 7)}${pad("filled here", 22)}${pad("alpaca", 22)}${pad("gap bps", 11)}qty gap`,
      ),
      ...g.orders.map((o) =>
        row(
          `  ${pad(o.submitted_at.slice(11, 19), 10)}${pad(o.side, 5)}${pad(o.symbol, 8)}${pad(o.submitted_by, 7)}${pad(
            o.planned.fill ? `${trimQty(o.planned.qty)} @ ${o.planned.fill}` : o.planned.status,
            22,
          )}${pad(
            o.mirror
              ? o.mirror.fill
                ? `${trimQty(o.mirror.qty)} @ ${o.mirror.fill}`
                : o.mirror.code
              : "not mirrored",
            22,
          )}${pad(o.execution_gap_bps ?? "—", 11)}${o.quantity_gap ?? "—"}`,
        ),
      ),
    ],
  };
}

/** One order's mirror in a word or two, for the `orders` grid (`M7/E39`). */
function mirrorCell(o: Order): string {
  const m = o.mirror;
  if (!m) return "—";
  if (m.state === "pending") return "sending";
  if (m.state === "open") return m.status ?? "open";
  if (m.code === "filled" && m.fill_price) return `filled ${m.fill_price}`;
  return m.code ?? "decided";
}

/** Why an order did not reach Alpaca, when it did not — the line `M7/E39` asked for. */
function mirrorRefusalLines(orders: readonly Order[]): Line[] {
  return orders
    .filter((o) => o.mirror?.state === "decided" && o.mirror.code && isRefusal(o.mirror.code))
    .map((o) =>
      rowNote(
        `  ${orderTag(o)}  not at Alpaca: ${o.mirror?.code}${o.mirror?.detail ? ` — ${o.mirror.detail}` : ""}`,
      ),
    );
}

const isRefusal = (code: string) =>
  code.startsWith("mirror_") || code.startsWith("kill_switch") || code === "policy_unavailable";

/** What a just-placed order's mirrors are doing, under the order's own line. */
function mirrorNote(o: Order): Line[] {
  return [...paperMirrorNote(o), ...liveMirrorNote(o)];
}

/** The live copy (`ADR-0050`), said in the real-money colour so it is never missed. */
function liveMirrorNote(o: Order): Line[] {
  const m = o.live_mirror;
  if (!m) return [];
  if (m.state === "decided" && m.code && isRefusal(m.code))
    return [note(`  not sent to your LIVE account: ${m.code}${m.detail ? ` — ${m.detail}` : ""}`)];
  return [warn("  also placing it on your LIVE Alpaca account — real money")];
}

function paperMirrorNote(o: Order): Line[] {
  const m = o.mirror;
  if (!m) return [];
  if (m.state === "decided" && m.code && isRefusal(m.code))
    return [note(`  not sent to Alpaca: ${m.code}${m.detail ? ` — ${m.detail}` : ""}`)];
  return [note("  also placing it on your Alpaca paper account — `orders` shows how it went")];
}

async function agentConfig(args: string[], ctx: CommandContext): Promise<CommandResult> {
  const accountId = ctx.session.accountId;
  if (!accountId)
    return { lines: [err("open an account before configuring an agent — run `account`")] };

  const variant = args[0]?.toLowerCase();
  const action = args[1]?.toLowerCase();
  if (!variant) {
    const listed = await api.agentConfigs(accountId);
    if (!listed.ok) return { lines: [refusal(listed)] };
    if (listed.data.configs.length === 0) {
      return { lines: [note("no scheduled agent config"), note("  agent config momentum weekly")] };
    }
    return { lines: listed.data.configs.flatMap(configLines) };
  }
  if (variant !== "momentum")
    return { lines: [err("only momentum can be scheduled in this release")] };
  if (action === "daily" || action === "weekly") {
    const saved = await api.putAgentConfig(accountId, action);
    if (!saved.ok) return { lines: [refusal(saved)] };
    return {
      lines: [out(`momentum cadence saved as ${action}`), ...configLines(saved.data.config)],
    };
  }
  if (action === "enable" || action === "disable") {
    const changed = await api.setAgentConfigEnabled(accountId, action === "enable");
    if (!changed.ok) return { lines: [refusal(changed)] };
    return {
      lines: [
        out(`momentum schedule ${action === "enable" ? "enabled" : "disabled"}`),
        ...configLines(changed.data.config),
      ],
    };
  }
  return { lines: [err("usage: agent config momentum daily|weekly|enable|disable")] };
}

function configLines(config: AgentConfig): Line[] {
  const state = config.enabled ? `enabled / ${config.schedule_status}` : "disabled";
  const lines: Line[] = [
    row(`  ${pad(config.variant, 14)} ${pad(state, 20)} ${config.cadence}`),
    rowNote(`  ${pad("version", 14)} ${config.strategy_version}  ${config.universe}`),
    rowNote(`  ${pad("model", 14)} ${config.effective_model}`),
  ];
  if (config.next_run_at) lines.push(rowNote(`  ${pad("next run", 14)} ${config.next_run_at}`));
  if (config.next_valuation_at)
    lines.push(rowNote(`  ${pad("next value", 14)} ${config.next_valuation_at}`));
  if (config.schedule_error) lines.push(rowErr(`  ${config.schedule_error}`));
  return lines;
}

async function agentSpend(): Promise<CommandResult> {
  const res = await api.agentSpend();
  if (!res.ok) return { lines: [refusal(res)] };
  const s = res.data;

  const lines: Line[] = [
    note(`model spend today (UTC ${s.day})`),
    row(`  ${pad("spent", 14)} $${formatSpendUsd(s.spent_today_usd)}`),
    row(`  ${pad("your cap", 14)} $${formatSpendUsd(s.cap_usd)} per day`),
    row(`  ${pad("remaining", 14)} $${formatSpendUsd(s.remaining_usd)}`),
    row(`  ${pad("per run", 14)} $${formatSpendUsd(s.per_run_cap_usd)}`),
    row(`  ${pad("runs", 14)} ${s.runs_today}`),
  ];

  if (s.platform_spent_today_usd !== "0.000000") {
    lines.push(note(`platform-wide today: $${formatSpendUsd(s.platform_spent_today_usd)}`));
  }
  if (s.remaining_usd === "0.000000") {
    lines.push(err("your daily ceiling is met — agent runs are refused until UTC midnight"));
  }
  return { lines };
}

/**
 * Forward account evidence since the first stored valuation.
 *
 * Dollar values and the benchmark verdict arrive fully formed from the API.
 * In particular, this command never parses or subtracts the decimal strings:
 * prefixing one with `$` is presentation, while deriving one here would create
 * a second performance implementation with different evidence rules.
 */
async function agentPerformance(ctx: CommandContext): Promise<CommandResult> {
  const res = await api.agentPerformance(ctx.session.accountId);
  if (!res.ok) return { lines: [refusal(res)] };
  const performance = res.data;
  const exactUsd = (value: string | undefined) => (value === undefined ? "—" : `$${value}`);
  const window =
    performance.window_started_at === null || performance.window_ended_at === null
      ? "valuation window unavailable"
      : `${performance.window_started_at} → ${performance.window_ended_at}`;

  const lines: Line[] = [
    note(`forward performance, ${performance.samples} valuation sample(s)`),
    rowNote(`  ${window}`),
    row(`  ${pad("P&L", 18)} ${exactUsd(performance.pnl_usd)}`),
    row(`  ${pad("buy and hold", 18)} ${exactUsd(performance.buy_and_hold_pnl_usd)}`),
    row(`  ${pad("excess", 18)} ${exactUsd(performance.excess_pnl_usd)}`),
    row(`  ${pad("policy blocks", 18)} ${performance.policy_blocks}`),
    row(`  ${pad("model cost", 18)} $${performance.cost_usd}`),
    row(`  ${pad("feed", 18)} ${performance.feeds.join(", ") || "—"}`),
  ];

  if (performance.beat_buy_and_hold === undefined) {
    lines.push(note("  no supported benchmark comparison yet"));
  } else if (performance.beat_buy_and_hold) {
    lines.push(out(`  beat buy-and-hold by ${exactUsd(performance.excess_pnl_usd)} of CASH`));
  } else {
    lines.push(
      err(`  did NOT beat buy-and-hold — excess ${exactUsd(performance.excess_pnl_usd)} of CASH`),
    );
  }

  if (performance.attribution.status === "unique") {
    lines.push(
      row(
        `  ${pad("attributed to", 18)} ${performance.attribution.agent_key} ${performance.attribution.versions.join(", ")}`,
      ),
    );
  } else {
    lines.push(err(`  ${pad("attribution", 18)} unavailable`));
  }
  if (performance.observed_strategies.length > 0) {
    lines.push(
      rowNote(
        `  ${pad("observed", 18)} ${performance.observed_strategies
          .map((strategy) => `${strategy.agent_key} ${strategy.versions.join(", ")}`)
          .join("; ")}`,
      ),
    );
  }

  lines.push(row(""), note(`recent runs (newest first, limit ${performance.recent_runs_limit})`));
  if (performance.recent_runs.length === 0) {
    lines.push(note("  no agent runs in this valuation window"));
  } else {
    for (const run of performance.recent_runs) {
      lines.push(
        row(
          `  ${pad(run.agent_version, 15)}${pad(run.status, 11)}` +
            `${pad(`${run.policy_blocks} blocked`, 13)}` +
            `${pad(run.cost_usd === null ? "—" : `$${run.cost_usd}`, 12)}${run.id}`,
        ),
      );
      for (const outcome of run.outcomes) lines.push(performanceOutcomeLine(outcome));
    }
  }

  for (const caveat of performance.caveats) lines.push(note(`  ${caveat}`));
  return { lines };
}

function performanceOutcomeLine(
  outcome: AgentPerformance["recent_runs"][number]["outcomes"][number],
): Line {
  const detail = outcome.detail ?? {};
  const symbol = typeof detail.symbol === "string" ? ` ${detail.symbol}` : "";
  const result = typeof detail.result === "string" ? ` — ${detail.result}` : "";
  if (outcome.status === "blocked") {
    return rowErr(
      `    blocked ${outcome.name}${symbol}: ${outcome.reason ?? outcome.code ?? "refused"}`,
    );
  }
  if (outcome.impact === "none") {
    const rationale = typeof detail.rationale === "string" ? ` — ${detail.rationale}` : "";
    return rowNote(`    held${symbol}${rationale}`);
  }
  return row(`    applied ${outcome.name}${symbol}${result}`);
}

/**
 * How the strategy is doing, and how much to trust the answer.
 *
 * **An absent figure prints "—", never 0.** The server omits what the evidence
 * cannot support, and substituting a zero here would put back exactly the
 * confident claim it went to the trouble of not making. `pct` is the only
 * formatting this function does, and it formats — it computes nothing the API
 * did not send, which is PRODUCT.md's rule for this whole surface.
 *
 * The caveats are printed rather than summarised. They are the difference
 * between a number worth acting on and one worth waiting for, and a reader who
 * skips them will read a three-point drawdown as a fact about the strategy.
 */
async function agentEvals(arg: string | undefined): Promise<CommandResult> {
  const days = arg === undefined ? undefined : Number(arg);
  if (days !== undefined && (!Number.isFinite(days) || days <= 0)) {
    return { lines: [err(`agent evals: '${arg}' is not a number of days`)] };
  }

  const res = await api.agentEvals(days);
  if (!res.ok) return { lines: [refusal(res)] };
  const e = res.data;

  if (e.points === 0) {
    return {
      lines: [
        note("nothing has been observed on this account yet"),
        note("  a point is recorded at the start and end of every `agent run`"),
      ],
    };
  }

  const pct = (v: number | undefined) => (v === undefined ? "—" : `${(v * 100).toFixed(2)}%`);
  const num = (v: number | undefined) => (v === undefined ? "—" : v.toFixed(2));

  const lines: Line[] = [
    note(`strategy score over ${e.window_days} day(s), ${e.points} observation(s)`),
    row(`  ${pad("return", 16)} ${pct(e.total_return)}`),
    row(`  ${pad("buy and hold", 16)} ${pct(e.benchmark_return)}`),
    row(`  ${pad("excess", 16)} ${pct(e.excess_return)}`),
    row(`  ${pad("max drawdown", 16)} ${pct(e.max_drawdown)}`),
    row(`  ${pad("sortino", 16)} ${num(e.sortino)}`),
    row(`  ${pad("turnover", 16)} ${num(e.turnover)}`),
    row(`  ${pad("model spend", 16)} $${formatSpendUsd(e.cost_usd)} over ${e.runs} run(s)`),
    row(`  ${pad("feed", 16)} ${e.feeds.join(", ") || "—"}`),
  ];

  if (e.variants.length > 0) {
    lines.push(row(`  ${pad("strategies", 16)} ${e.variants.join(", ")}`));
  }
  // More than one thing placing orders means the curve belongs to neither of
  // them. Said loudly, because the number above it looks equally confident
  // either way.
  if (e.variants.filter((v) => !v.includes("portfolio-insight")).length > 1) {
    lines.push(err("more than one strategy traded this account — this score belongs to neither"));
  }
  for (const caveat of e.caveats) lines.push(note(`  ${caveat}`));

  return { lines };
}

/**
 * `agent backtest` — what the strategy did against history, and whether it beat
 * simply holding.
 *
 * **The one line that matters is the verdict**, printed as a sentence rather
 * than left to be worked out from two percentages. A return of 14% reads as
 * good until you notice the index did 19% over the same window, and a report
 * that makes a reader do that subtraction is a report that will be misread.
 *
 * `cost` is printed even though it is always zero, because zero is the claim:
 * this asks a question that has already been paid for, so it can be asked as
 * often as you like. What the recording cost is beside it, since that is the
 * number a person deciding whether to re-record actually needs.
 */
/**
 * `strategy qualify` — `ADR-0017`'s verdict, with the folds it was reached over.
 *
 * **Beside `agent backtest`, and they answer different questions.** That one
 * replays `momentum` over recent consolidated bars and says whether a prompt
 * change broke something. This one is investment qualification over six years
 * of point-in-time history with walk-forward folds, and passing one implies
 * nothing about the other.
 *
 * **The per-fold rows are printed, not summarised**, for the reason
 * `DESIGN-qualification-scorecard` gives about withholding figures: a strong
 * aggregate carrying two losing regimes is the commonest way a backtest is
 * oversold, and a reader who cannot see the folds cannot see it.
 */
/**
 * `strategy` with no verb — the whole field, ranked where it earned it.
 *
 * **Only qualifiers carry a rank** (`ADR-0023`), and the column is blank rather
 * than numbered for everything else: a rejected candidate can easily have a
 * better excess return than a qualifier, and numbering it would read as an
 * endorsement. What a non-qualifier gets instead is the list of hurdles it
 * failed, which is the thing a reader actually needs.
 */
/**
 * `strategy dossier <candidate>` — the evidence package, summarised for a screen.
 *
 * **The full document is ~180 KB and this prints its shape, not its bytes.** A
 * dossier holds every trade and every session's equity precisely so a figure can
 * be audited, and a terminal that dumped it would be unreadable. What it shows
 * is what a person checks by eye: the digest they can verify, the locked inputs,
 * whether the plan still validates, the drawdown as an episode, what survives
 * removing the best fold and the best symbol, and every failed hurdle.
 *
 * Nothing here is computed. Every number arrives from the API — `ADR-0026`'s
 * digest included, which the terminal shows and never recalculates.
 */
async function strategyDossier(candidate = "momentum-det"): Promise<CommandResult> {
  const res = await api.strategyDossier(candidate);
  if (!res.ok) return { lines: [refusal(res)] };
  const d = res.data;

  const pct = (v: number | undefined) => (v === undefined ? "—" : `${(v * 100).toFixed(2)}%`);
  const lines: Line[] = [
    note(`${d.candidate}  ${d.verdict.toUpperCase().replace(/_/g, " ")}`),
    note(`  dossier ${d.dossier_digest.slice(0, 16)}…  (SHA-256 of this document's own bytes)`),
    note(
      `  seal    ${d.holdout_seal.slice(0, 16)}…   result ${(d.score_digest ?? "—").slice(0, 16)}…`,
    ),
    row(""),
    row("  LOCKED"),
    row(
      `    code    ${d.locked.code_digest.slice(0, 16)}…   data ${d.locked.data_digest.slice(0, 16)}…`,
    ),
    row(`    universe ${d.locked.universe}   model ${d.locked.model ?? "none (deterministic)"}`),
    row(`    costs   ${d.locked.cost_model}`),
    row(""),
  ];

  /*
   * Stated rather than implied. An empty list means `foldPlanViolations` was
   * re-run against this plan and found nothing — seeing the line is seeing that
   * the question was asked, which a dossier that merely reprinted a plan could
   * not tell you.
   */
  lines.push(
    d.plan_violations.length === 0
      ? row("  PLAN     re-validated, no violations")
      : err(`  PLAN     ${d.plan_violations.length} violation(s): ${d.plan_violations.join("; ")}`),
  );

  if (d.folds.length > 0) {
    const trades = d.folds.reduce((n, f) => n + f.trades.length, 0);
    const points = d.folds.reduce((n, f) => n + f.series.length, 0);
    lines.push(
      row(`  EVIDENCE ${d.folds.length} fold(s), ${trades} trade(s), ${points} observation(s)`),
    );
  }

  if (d.drawdown) {
    const dd = d.drawdown;
    lines.push(row(""));
    lines.push(row("  WORST DRAWDOWN"));
    lines.push(
      row(
        `    ${pct(dd.depth)}  peak ${dd.peak_at.slice(0, 10)} → trough ${dd.trough_at.slice(0, 10)}`,
      ),
    );
    /*
     * Absent means it never recovered, and that is printed as a sentence rather
     * than left blank. A missing date reads as a rendering bug; "still under
     * water at the end of the evidence" is the more important of the two answers.
     */
    lines.push(
      dd.recovered_at
        ? row(
            `    recovered ${dd.recovered_at.slice(0, 10)} after ${dd.observations_to_recovery} observation(s)`,
          )
        : err("    never recovered inside the evidence"),
    );
  }

  const { by_fold: byFold, by_symbol: bySymbol } = d.concentration;
  if (byFold || bySymbol) {
    lines.push(row(""));
    lines.push(row("  WHAT SURVIVES LOSING ITS BEST BET"));
    if (byFold) {
      lines.push(
        row(
          `    without ${pad(byFold.dropped ?? "—", 10)} ${pad(pct(byFold.without_best), 10)} (all folds ${pct(byFold.total)})`,
        ),
      );
    }
    if (bySymbol) {
      lines.push(
        row(
          `    without ${pad(bySymbol.dropped ?? "—", 10)} ${pad(pct(bySymbol.without_best), 10)} (all symbols ${pct(bySymbol.total)})`,
        ),
      );
    }
  }

  const failed = d.hurdles.filter((h) => h.result === "fail");
  const unknown = d.hurdles.filter((h) => h.result === "unknown");
  if (failed.length > 0 || unknown.length > 0) {
    lines.push(row(""));
    lines.push(row("  HURDLES"));
    for (const h of failed) {
      lines.push(
        err(`    fail    ${pad(h.code, 28)} wanted ${h.threshold}, got ${h.observed ?? "—"}`),
      );
    }
    for (const h of unknown) {
      lines.push(note(`    unknown ${pad(h.code, 28)} nothing to judge it on`));
    }
  }

  lines.push(row(""));
  lines.push(
    note(
      `  ${d.concentration.symbols.length} symbol(s) attributed · full document at /api/v1/agents/qualify/dossier?strategy=${d.candidate}`,
    ),
  );
  lines.push(row(""));
  return { lines };
}

/**
 * `strategy nominate <candidate>` — open an M8 review, and nothing else.
 *
 * **The refusal is the ordinary outcome and is printed as information rather
 * than as a fault.** Nothing in the field clears `ADR-0017`'s hurdles, so a
 * caller will see `not qualified` with the codes it missed — which is the
 * evaluator working. Printing that as a red error would train somebody to
 * re-run it.
 */
async function strategyNominate(candidate?: string): Promise<CommandResult> {
  if (!candidate) {
    return { lines: [err("strategy nominate <candidate> — name the candidate to nominate")] };
  }
  const res = await api.strategyNominate(candidate);
  if (!res.ok) {
    /*
     * A `409` here carries the verdict and the hurdles, and reading them is the
     * whole value of the call. `refusal` would print the message alone.
     */
    const body = res.error as { error?: string; verdict?: string; failed?: readonly string[] };
    if (body?.error === "not_qualified") {
      return {
        lines: [
          note(`${candidate} is ${String(body.verdict).replace(/_/g, " ")} — no review opened`),
          note(`  did not clear: ${(body.failed ?? []).join(", ")}`),
          row(""),
        ],
      };
    }
    return { lines: [refusal(res)] };
  }

  const n = res.data;
  return {
    lines: [
      note(
        n.already_nominated
          ? `${n.version} was already nominated on this evidence`
          : `${n.version} nominated for M8 review`,
      ),
      row(`  dossier ${n.dossier_digest.slice(0, 16)}…`),
      row(""),
      note("  a review record and nothing else — approval, legal review, staged"),
      note("  allocation and the 501 guard on live accounts all still apply"),
      row(""),
    ],
  };
}

/**
 * `strategy holdout` — which sealed windows have been opened.
 *
 * **The interesting answer is zero**, and it is printed as a claim rather than
 * as an empty table. `M6/I4` built the machinery that would open one and
 * deliberately did not: a holdout is spent once, a failed one cannot be reused,
 * and every candidate in the field is rejected — so there is nothing worth
 * spending it on yet.
 */
async function strategyHoldout(): Promise<CommandResult> {
  const res = await api.strategyHoldout();
  if (!res.ok) return { lines: [refusal(res)] };
  const h = res.data;

  const lines: Line[] = [
    note(
      h.opened === 0
        ? "no sealed holdout has been opened — the window is still sealed"
        : `${h.opened} holdout(s) opened`,
    ),
  ];
  for (const e of h.entries) {
    lines.push(
      row(`  ${e.opened_at.slice(0, 10)}  ${pad(e.candidate, 18)} ${e.seal.slice(0, 16)}…`),
    );
  }
  lines.push(
    note(
      h.m8_review_candidates === 0
        ? "no candidate has been nominated for M8 review"
        : `${h.m8_review_candidates} nominated for M8 review`,
    ),
  );
  for (const n of h.nominated) {
    lines.push(row(`  ${pad(n.candidate, 18)} ${n.dossier_digest.slice(0, 16)}…`));
  }
  lines.push(row(""));
  lines.push(
    note(
      "  a holdout is opened once, ever — the ledger's unique constraint is what makes that true",
    ),
  );
  lines.push(row(""));
  return { lines };
}

/** The query the rebalance routes take, from the flags the catalog declares. */
const REBALANCE_QUERY: Readonly<Record<string, string>> = {
  "--top": "top",
  "--lookback": "lookback",
  "--score": "score",
  "--weight": "weight",
  "--every": "every",
  "--at": "at",
  "--sell-at": "sell_at",
  "--buy-at": "buy_at",
  "--session": "session",
  "--stop-pct": "stop_pct",
  "--band": "resize_band_pct",
  "--invest-pct": "invest_pct",
  "--skip-spike": "skip_spike_pct",
  "--market": "market",
  "--market-action": "market_action",
  "--from": "from",
  "--universe": "universe",
  "--exclude": "exclude",
  "--capital": "capital",
  "--share": "share",
  "--cash": "cash",
  "--cost-bps": "cost_bps",
  "--start": "start",
  "--end": "end",
  "--split": "split",
  "--by": "by",
  "--tp-pct": "tp_pct",
  "--tp-usd": "tp_usd",
  "--rebuy-under": "rebuy_under_pct",
  "--rebuy-above": "rebuy_above_pct",
  "--ladder-scale": "ladder_scale",
  "--when": "when",
  "--then": "then_flags",
};

const signedPct = (x: number) => `${x >= 0 ? "+" : ""}${(x * 100).toFixed(1)}%`;

const ORDINAL = ["", "1st", "2nd", "3rd"];
/** ", on its 2nd session" — nothing for the first, which is what every week or month means. */
const sessionText = (n: number | undefined) =>
  n === undefined || n <= 1 ? "" : `, on its ${ORDINAL[n] ?? `${n}th`} session`;

/** "at the open", or when it sells and buys when minutes are named (`M10/I6`). */
const timesText = (c: Pick<RebalanceConfigView, "at" | "sell_at" | "buy_at">) => {
  const sell = c.sell_at ?? null;
  const buy = c.buy_at ?? null;
  if (sell === null && buy === null) return `at the ${c.at}`;
  if (sell === buy) return `at ${sell} ET`;
  const edge = `the ${c.at}`;
  return `selling at ${sell === null ? edge : `${sell} ET`}, buying at ${buy === null ? edge : `${buy} ET`}`;
};

const describeRebalance = (c: RebalanceConfigView) =>
  `top ${c.top} of ${c.universe}${excludeText(c.exclude)} by ${c.lookback}-session ${c.score === "return" ? "return" : "return over volatility"}, ${c.weight} weight, every ${c.every}${sessionText(c.session)} ${timesText(c)}` +
  `${c.stop_pct === null ? "" : `, stop ${c.stop_pct}%`}${c.capital === null ? "" : `, up to $${c.capital}`}` +
  filtersText(c) +
  ladderText(c.ladder) +
  whenText(c);

/** ", and when dispersion:20>p75:26, market=spy:200,tp-pct=1" — nothing without one (`M10/I9`). */
const whenText = (c: Pick<RebalanceConfigView, "when" | "then_flags">) =>
  c.when ? `, and when ${c.when}: ${c.then_flags}` : "";

/** What a decision's `--when` read, and which flags its week runs on (`M10/I9`). */
function whenLine(d: RebalanceDecisionView): Line | null {
  const w = d.when;
  if (!w) return null;
  const n = (x: number | null) => (x === null ? "—" : x.toFixed(2));
  const why =
    w.reason === "too_few_samples"
      ? `too few weekly samples to set its threshold (${w.samples})`
      : w.reason === "pick_unmeasured"
        ? "no pick to measure"
        : w.reason === "measure_unread"
          ? "too little history to measure it"
          : null;
  const against = `${n(w.value)} against ${n(w.threshold)}${w.samples > 0 ? ` (${w.samples} weekly samples)` : ""}`;
  return note(
    `    when ${w.spec}${w.pick ? ` on ${w.pick}` : ""}: ${why ?? against} — this week runs ${w.on ? "--then's flags" : "its own flags"}`,
  );
}

const MARKET_WHO: Readonly<Record<string, string>> = {
  spy: "SPY is",
  qqq: "QQQ is",
  either: "SPY or QQQ is",
  both: "SPY and QQQ are",
};

/**
 * ", skip a 5-session rise over 15%, cash while SPY is under its 200-session
 * average" — nothing for a filter that is off (M9/I7).
 */
const filtersText = (c: RebalanceFiltersView) => {
  const spike =
    c.skip_spike_pct === null || c.skip_spike_pct === undefined
      ? ""
      : `, skip a 5-session rise over ${c.skip_spike_pct}%`;
  if (!c.market) return spike;
  const [on = "", sma = ""] = c.market.split(":");
  const whose = on === "spy" || on === "qqq" ? "its" : "their";
  return `${spike}, ${c.market_action === "hold" ? "hold" : "cash"} while ${MARKET_WHO[on] ?? `${on} is`} under ${whose} ${sma}-session average`;
};

/** " without PLTR, TSLA" — nothing when nothing is excluded (M9/E4). */
const excludeText = (exclude: readonly string[] | undefined) =>
  exclude && exclude.length > 0 ? ` without ${exclude.join(", ")}` : "";

/** ", ladder: take 1%, rebuild 2% under or 3% above, × vol:40:0.75" — nothing without one. */
const ladderText = (l: RebalanceConfigView["ladder"]) =>
  l
    ? `, ladder: take ${l.tp_pct !== null ? `${l.tp_pct}%` : `$${l.tp_usd}`}, rebuild ${l.rebuy_under_pct}% under${l.rebuy_above_pct === null ? "" : ` or ${l.rebuy_above_pct}% above`}${l.scale && l.scale !== "off" ? `, × ${l.scale}` : ""}`
    : "";

/** The ladder's counters and where each position stands on it (`M9/I4`). */
function ladderLines(l: RebalanceLadderView | null | undefined): Line[] {
  if (!l) return [];
  const c = l.counters;
  const lines: Line[] = [
    row(
      `  ladder: ${c.take_profits} take profit${c.take_profits === 1 ? "" : "s"} · ${c.dip_rebuys} rebuilt on a dip · ${c.breakout_rebuys} on a breakout · ${c.stub_minutes} position-minutes as a stub`,
    ),
  ];
  if (l.error) lines.push(err(`  ladder skipped its last minute: ${l.error}`));
  if (l.when?.on)
    lines.push(
      rowNote(`  this week steps on --then's ladder, since the rebalance at ${l.when.at}`),
    );
  if (l.scales)
    lines.push(
      rowNote(
        `  scaled by ${l.scales.spec} at ${l.scales.at}${l.scales.unread.length > 0 ? ` · k 1 for ${l.scales.unread.join(", ")} (no volatility)` : ""}`,
      ),
    );
  for (const p of l.positions) {
    // " ×1.32": the position's k, its levels already scaled by it (`M10/I4`).
    const k = p.k === null || p.k === undefined ? "" : ` ×${p.k.toFixed(2)}`;
    lines.push(
      rowNote(
        p.mode === "stub"
          ? `    ${pad(p.symbol, 7)}stub${k} — took profit at ${p.take_profit_price}; rebuilds at ${p.rebuy_under_at}${p.rebuy_above_at === null ? "" : ` or ${p.rebuy_above_at}`} with $${p.bucket.toFixed(2)}`
          : `    ${pad(p.symbol, 7)}full${k} from ${p.reference}${p.take_profit_at === null ? "" : `; takes profit at ${p.take_profit_at}`}`,
      ),
    );
  }
  return lines;
}

const nextLine = (next: RebalanceNext) =>
  next
    ? `next: ${next.at}${next.leg === "buy" ? " — this rebalance's buy" : next.rebalance ? " — a rebalance" : " — a stop check"}`
    : "next: nothing armed";

/** " ×0.77": a pick's ladder k at this decision, nothing without a scale (`M10/I4`). */
const pickK = (d: RebalanceDecisionView, symbol: string) => {
  const k = d.ladder_scale?.by_symbol[symbol];
  return k === undefined ? "" : ` ×${k.toFixed(2)}`;
};

function decisionLines(d: RebalanceDecisionView): Line[] {
  const head = `${d.at}  ${d.kind}${d.leg ? ` (${d.leg})` : ""}${d.dry_run ? " (preview)" : ""}`;
  if (d.error) return [rowErr(`  ${head}: skipped — ${d.error}`)];
  const lines: Line[] = [row(`  ${head}`)];
  const when = whenLine(d);
  if (when) lines.push(when);
  if (d.market?.risk_off)
    lines.push(
      note(
        `    market: ${d.market.below.join(" and ")} under ${d.market.below.length > 1 ? "their" : "its"} ${d.market.sma}-session average — stepping aside`,
      ),
    );
  if (d.market && d.market.unread.length > 0)
    lines.push(
      note(
        `    · market: too little history to read ${d.market.unread.join(", ")}, so not counted as under its average`,
      ),
    );
  if (d.picks.length > 0)
    lines.push(
      note(
        `    picks: ${d.picks.map((p) => `${p.symbol} ${(p.weight * 100).toFixed(0)}%${pickK(d, p.symbol)}`).join("  ")}`,
      ),
    );
  if (d.skipped && d.skipped.length > 0)
    lines.push(
      note(`    passed over, up more than --skip-spike in 5 sessions: ${d.skipped.join(", ")}`),
    );
  if (d.intents.length === 0)
    lines.push(
      note(
        d.market?.risk_off
          ? "    no orders: --market-action hold keeps what it holds"
          : "    no orders: already where it should be",
      ),
    );
  for (const i of d.intents) {
    const line = `    ${pad(i.action, 5)}${pad(i.symbol, 7)}${pad(i.qty, 14)}${i.status}${i.price ? ` at ${i.price}` : ""}${i.code ? ` — ${i.code}` : ""}`;
    lines.push(i.status === "rejected" || i.status === "error" ? rowErr(line) : rowNote(line));
  }
  if (d.history.note) lines.push(note(`    · ${d.history.note}`));
  if (d.history.failed.length > 0)
    lines.push(note(`    · not extended: ${d.history.failed.join(", ")}`));
  return lines;
}

/** Fields that describe a study, not a schedule: `preview` and `on` never send them. */
const BACKTEST_ONLY = new Set(["cash", "cost_bps", "from", "start", "end", "split", "by"]);

/** The rule flags as the API's field names; the server checks the values too. */
function rebalanceFields(values: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [flag, value] of Object.entries(values)) {
    const key = REBALANCE_QUERY[flag];
    if (key && !BACKTEST_ONLY.has(key)) out[key] = value;
  }
  return out;
}

type RebalanceRecord = NonNullable<
  Extract<Awaited<ReturnType<typeof api.rebalanceStatus>>, { ok: true }>["data"]["rebalance"]
>["record"];

/** What it has done since it started, beside the universe and SPY — or why not yet. */
function recordLines(record: RebalanceRecord): Line[] {
  if (!record) return [note("  record: could not be read")];
  const pct = (x: number | null) =>
    x === null ? "—" : `${x >= 0 ? "+" : ""}${(x * 100).toFixed(2)}%`;
  const lines: Line[] = [
    record.points < 2
      ? note(
          `  record: ${record.points} valuation point${record.points === 1 ? "" : "s"} since ${record.since.slice(0, 10)} — one is taken after each close`,
        )
      : row(
          `  since ${record.since.slice(0, 10)}: this ${pct(record.return)} · equal weight ${pct(record.equal_weight_return)} · SPY ${pct(record.spy_return)} · ${record.points} closes`,
        ),
  ];
  if (record.holdings.length > 0)
    lines.push(
      note(
        `  holding: ${record.holdings
          .map(
            (h) =>
              `${h.symbol} ${(h.weight * 100).toFixed(0)}%${h.target === null ? " (not a pick)" : ` of ${(h.target * 100).toFixed(0)}%`}`,
          )
          .join("  ")}`,
      ),
    );
  return lines;
}

/** "b, 40% of the account — " for a named or shared setting; nothing for a lone `default`. */
const rebalanceSettingText = (name: string | undefined, share: number | undefined) =>
  (name && name !== "default") || (share !== undefined && share < 100)
    ? `${name ?? "default"}, ${share ?? 100}% of the account — `
    : "";

/** What a setting's book holds (`M10/I5`). */
const bookLine = (book: RebalanceBookView | null | undefined): Line[] =>
  book === undefined
    ? []
    : book === null
      ? [note("  book: takes what the account holds at its next decision")]
      : [
          note(
            `  book: ${book.length === 0 ? "nothing yet" : book.map((b) => `${b.symbol} ${Number(b.qty)}`).join("  ")}`,
          ),
        ];

async function rebalanceShow(accountId: string, n: number, name?: string): Promise<CommandResult> {
  const res = await api.rebalanceStatus(accountId, n, name);
  if (!res.ok) return { lines: [refusal(res)] };
  const r = res.data.rebalance;
  if (!r)
    return {
      lines: [
        out(
          name
            ? `no rebalance named ${name} on this account — \`rebalance on --name ${name} --share <pct>\``
            : "no rebalance on this account — `rebalance preview`, then `rebalance on`",
        ),
      ],
    };
  return {
    lines: [
      (r.config.enabled ? out : note)(
        `${r.config.enabled ? "running" : "stopped"} — ${rebalanceSettingText(r.name, r.share)}${describeRebalance(r.config)}`,
      ),
      note(`  ${nextLine(r.next)} · since ${r.started_at.slice(0, 10)}`),
      ...bookLine(r.book),
      ...(r.error ? [err(`  last decision skipped: ${r.error}`)] : []),
      ...recordLines(r.record),
      ...ladderLines(r.ladder),
      ...(r.log.length === 0 ? [note("  no decision yet")] : r.log.flatMap(decisionLines)),
    ],
  };
}

async function rebalancePreview(
  accountId: string,
  values: Readonly<Record<string, string>>,
  name?: string,
): Promise<CommandResult> {
  const res = await api.rebalancePreview(accountId, rebalanceFields(values), name);
  if (!res.ok) return { lines: [refusal(res)] };
  return {
    lines: [
      out(
        `${res.data.from === "current" ? `preview of this account's rebalance${name ? ` ${name}` : ""}, if it ran now` : "preview"} — ${rebalanceSettingText(res.data.config.name, res.data.config.share)}${describeRebalance(res.data.config)}; nothing is placed`,
      ),
      note(
        `  budget: $${centsOf(res.data.decision.budget.cash)} cash${res.data.decision.budget.capital === null ? "" : `, capital $${res.data.decision.budget.capital}`}`,
      ),
      ...decisionLines(res.data.decision),
      ...(res.data.decision.unscored.length > 0
        ? [note(`  not enough history to rank: ${res.data.decision.unscored.join(", ")}`)]
        : []),
    ],
  };
}

async function rebalanceOn(
  accountId: string,
  values: Readonly<Record<string, string>>,
  name?: string,
): Promise<CommandResult> {
  const res = await api.rebalanceOn(accountId, rebalanceFields(values), name);
  if (!res.ok) return { lines: [refusal(res)] };
  const r = res.data.rebalance;
  return {
    lines: [
      out(`rebalance on — ${rebalanceSettingText(r.name, r.share)}${describeRebalance(r.config)}`),
      note(`  ${nextLine(r.next)}`),
      note(
        "  it trades only its own book — the shares it bought; another setting's and any bought by hand are left alone",
      ),
      ...(res.data.warnings ?? []).map((w) => err(`  ${w}`)),
    ],
  };
}

async function rebalanceOff(
  accountId: string,
  sell: boolean,
  name?: string,
  forget = false,
): Promise<CommandResult> {
  const res = await api.rebalanceOff(accountId, sell, name, forget);
  if (!res.ok) return { lines: [refusal(res)] };
  const which = name && name !== "default" ? `rebalance ${name}` : "rebalance";
  return {
    lines: [
      out(
        sell
          ? `${which} off, and its book is being sold`
          : `${which} off — its book stays, and stays its own`,
      ),
      ...(res.data.sold ? decisionLines(res.data.sold) : []),
      ...(res.data.forgotten
        ? [note(`  ${name} is forgotten; its name and its share are free`)]
        : []),
    ],
  };
}

/**
 * Two rebalance accounts over the same days (`M9/I4`): this one and a named
 * one, or two named ones. Names are resolved from `accounts`, as `account` does.
 */
async function rebalanceCompare(
  current: string | undefined,
  names: readonly string[],
): Promise<CommandResult> {
  const list = await api.listAccounts();
  if (!list.ok) return { lines: [refusal(list)] };
  const find = (name: string) =>
    list.data.accounts.find((a) => a.name.toLowerCase() === name.toLowerCase() || a.id === name);
  // `<account>:<setting>` compares one setting of a shared account (M10/I5).
  const refs = names.map((n) => {
    const [account = "", setting] = n.split(":");
    return { account, setting };
  });
  const picked = refs.map((r) => find(r.account));
  const missing = refs.filter((_, i) => !picked[i]).map((r) => r.account);
  if (missing.length > 0)
    return { lines: [err(`no account named ${missing.join(", ")} — \`accounts\` lists them`)] };
  const ids = picked.map((a, i) => {
    const setting = refs[i]?.setting;
    return setting ? `${a?.id}:${setting}` : (a?.id as string);
  });
  if (ids.length === 1) {
    if (!current) return { lines: [err("open an account first — run `account`")] };
    ids.unshift(current);
  }
  const res = await api.rebalanceCompare(ids[0] as string, ids[1] as string);
  if (!res.ok) return { lines: [refusal(res)] };
  const nameOf = (id: string) =>
    list.data.accounts.find((a) => a.id === id)?.name ?? id.slice(0, 8);
  const sideName = (s: RebalanceCompareSide) =>
    s.name && s.name !== "default" ? `${nameOf(s.account_id)}:${s.name}` : nameOf(s.account_id);
  const pct = (x: number | null | undefined) =>
    x === null || x === undefined ? "—" : `${x >= 0 ? "+" : ""}${(x * 100).toFixed(2)}%`;
  const side = (s: RebalanceCompareSide): Line[] => [
    row(
      `  ${pad(sideName(s), 16)}${pad(pct(s.record?.return), 10)}${pad(s.record ? String(s.record.points) : "—", 8)}${s.enabled ? "" : "(stopped) "}${describeRebalance(s.config)}`,
    ),
    ...(s.ladder
      ? [
          note(
            `    ladder: ${s.ladder.counters.take_profits} take profits · ${s.ladder.counters.dip_rebuys} dip and ${s.ladder.counters.breakout_rebuys} breakout rebuilds`,
          ),
        ]
      : []),
  ];
  return {
    lines: [
      out(`since ${res.data.since.slice(0, 10)}, the later of the two starts`),
      rowNote(`  ${pad("account", 16)}${pad("return", 10)}${pad("closes", 8)}rule`),
      ...side(res.data.a),
      ...side(res.data.b),
      note(`  SPY over the same days: ${pct(res.data.a.record?.spy_return)}`),
    ],
  };
}

async function rebalanceList(): Promise<CommandResult> {
  const res = await api.rebalances();
  if (!res.ok) return { lines: [refusal(res)] };
  const all = res.data.rebalances;
  if (all.length === 0) return { lines: [out("no rebalance on any of your accounts")] };
  return {
    lines: all.flatMap((r) => [
      (r.status === "enabled" ? row : r.status === "unreachable" ? rowErr : rowNote)(
        `  ${pad(r.status, 12)}${pad(r.account ?? r.account_id.slice(0, 8), 16)}${pad(r.name ?? "", 10)}${pad(r.share === undefined ? "" : `${r.share}%`, 6)}${r.config ? describeRebalance(r.config) : ""}`,
      ),
      ...bookLine(r.book).map((l) => ({ ...l, text: `  ${l.text}` })),
      ...(r.next ? [note(`    ${nextLine(r.next)}`)] : []),
      ...(r.error ? [note(`    last decision skipped: ${r.error}`)] : []),
    ]),
  };
}

/**
 * `--with 40:top=3,every=month` as the query string of one more setting (`M10/I5`): its share,
 * then rule fields named as their flags without the dashes. Undefined when it can't be read.
 */
export function withSetting(spec: string): string | undefined {
  const [share, rest = ""] = spec.split(":", 2) as [string, string?];
  if (!/^\d{1,3}$/.test(share)) return undefined;
  const query = new URLSearchParams({ share });
  for (const part of rest.split(",").filter(Boolean)) {
    const [field, value] = part.split("=", 2) as [string, string?];
    const key = REBALANCE_QUERY[`--${field}`];
    if (!key || !value || BACKTEST_ONLY.has(key)) return undefined;
    query.set(key, value);
  }
  return query.toString();
}

async function rebalanceBacktest(values: Readonly<Record<string, string>>): Promise<CommandResult> {
  const query: Record<string, string> = {};
  const withs: string[] = [];
  for (const [flag, value] of Object.entries(values)) {
    if (flag === "--with") {
      const w = withSetting(value);
      if (!w)
        return {
          lines: [
            err(`--with takes <share>:<flag>=<value>,… — like 40:top=3,every=month; got ${value}`),
          ],
        };
      withs.push(w);
      continue;
    }
    const key = REBALANCE_QUERY[flag];
    if (key) query[key] = value;
  }
  const res = await api.rebalanceBacktest(query, withs);
  if (!res.ok) return { lines: [refusal(res)] };
  const b = res.data;
  const c = b.config;
  const line = (label: string, s: RebalanceStatsWire | null) =>
    s
      ? row(
          `  ${pad(label, 16)}${pad(signedPct(s.cagr), 12)}${pad(signedPct(s.total_return), 12)}${signedPct(s.max_drawdown)}`,
        )
      : rowNote(`  ${pad(label, 16)}—`);
  const lines: Line[] = [
    out(
      `top ${c.top} of ${b.universe.symbols}${excludeText(b.universe.exclude)} by ${c.lookback}-session ${c.score === "return" ? "return" : "return over volatility"}, ${c.weight} weight, every ${c.every}${sessionText(c.session)} at the ${c.at}` +
        `${c.stop_pct === null ? "" : `, stop ${c.stop_pct}%`}${filtersText(c)}`,
    ),
    note(
      `  ${b.data.source} daily bars through ${b.data.through}${b.data.through === b.data.artifact_end ? "" : ` (stored to ${b.data.artifact_end}, then your feed)`} · from ${b.first_trade_day} · $${b.cash} opening cash · ${b.cost_bps} bp a side`,
    ),
    ...(b.window
      ? [
          out(
            `  reported over ${b.window.start} → ${b.window.end} only: the same run, cut — every row below is the window's`,
          ),
        ]
      : []),
    rowNote(`  ${pad("", 16)}${pad("a year", 12)}${pad("in total", 12)}worst drop`),
    line("this rule", b.strategy),
    line("  first half", b.fit),
    line("  second half", b.test),
    ...(b.without_hindsight_winners ? [line("  w/o winners", b.without_hindsight_winners)] : []),
    line("SPY", b.benchmarks.spy),
    line("equal weight", b.benchmarks.equal_weight),
  ];
  if (b.strategy) {
    lines.push(
      note(
        `  by year: ${b.strategy.by_year.map((y) => `${y.year} ${signedPct(y.return)}`).join("  ")}`,
      ),
    );
  }
  // A shared run (M10/I5): each setting's part of the account, beside the whole.
  if (b.settings && b.settings.length > 0) {
    lines.push(
      rowNote(`  ${pad("each setting", 16)}${pad("a year", 12)}${pad("in total", 12)}worst drop`),
    );
    for (const s of b.settings) {
      lines.push(line(`  ${s.name} ${s.share}%`, s.strategy));
      if (s.config)
        lines.push(
          note(
            `    top ${s.config.top} by ${s.config.lookback}-session ${s.config.score === "return" ? "return" : "return over volatility"}, every ${s.config.every}${sessionText(s.config.session)} · ${s.rebalances} rebalances`,
          ),
        );
    }
  }
  lines.push(
    note(
      `  ${b.activity.rebalances} rebalances${b.activity.stop_checks ? `, ${b.activity.stop_checks} stop checks` : ""}, ${b.activity.fills} fills, ${b.activity.rejected} refused`,
    ),
  );
  if (b.data.failed.length > 0) {
    // The reason, once per distinct one: 53 symbols failing for one cause is one line.
    const reasons = [...new Set(b.data.failed.map((f) => f.reason))];
    lines.push(
      err(
        `  history not extended from your feed for ${b.data.failed.length} symbol${b.data.failed.length === 1 ? "" : "s"}: ${reasons.slice(0, 2).join(" | ")}`,
      ),
    );
  }
  for (const n of b.notes) lines.push(note(`  · ${n}`));
  const flow = b.cash_flow;
  if (flow) {
    const spy = new Map(flow.spy_weekly.map((w) => [w.week_ending, w.balance]));
    lines.push(
      out(
        `cash flow from ${flow.from}: $${flow.opening_cash} → $${flow.end_balance?.toFixed(2) ?? "—"} (${flow.return === null ? "—" : signedPct(flow.return)}), worst drop ${flow.max_drawdown === null ? "—" : signedPct(flow.max_drawdown)}`,
      ),
      rowNote(`  ${pad("week ending", 14)}${pad("balance", 12)}${pad("of it cash", 12)}SPY held`),
      ...flow.weekly.map((w) =>
        row(
          `  ${pad(w.week_ending, 14)}${pad(w.balance.toFixed(2), 12)}${pad(w.cash.toFixed(2), 12)}${spy.get(w.week_ending)?.toFixed(2) ?? "—"}`,
        ),
      ),
    );
  }
  return { lines };
}

// ---------------------------------------------------------------------------
// rebalance tune (M9/E6, ADR-0056): started here, run by a Workflow, followed by
// polling, with one progress line rewritten in place.
// ---------------------------------------------------------------------------

/** The four flags a tune takes as lists: the same flags, read as words. */
const TUNE_GRID_FLAGS = new Set(["--top", "--lookback", "--score", "--weight"]);
const TUNE_FLAGS: readonly FlagSpec[] = (CATALOG.rebalance.flags as readonly FlagSpec[]).map(
  (f): FlagSpec => {
    if (!TUNE_GRID_FLAGS.has(f.name)) return f;
    const { choices: _c, min: _min, max: _max, default: _d, ...rest } = f;
    return { ...rest, kind: "word", value: `${f.value},…` };
  },
);
const TUNE_POLL_MS = 3000;

async function rebalanceTuneCommand(
  args: readonly string[],
  ctx: CommandContext,
  already?: Readonly<Record<string, string>>,
): Promise<CommandResult> {
  const usage = {
    lines: [
      err("usage: rebalance tune [flags]  |  rebalance tune status [<id>]"),
      note("  help rebalance"),
    ],
  };
  if (args[0]?.toLowerCase() === "status") {
    if (args.length > 2 || args.slice(1).some((a) => a.startsWith("--"))) return usage;
    return followTune(args[1] ?? "latest", ctx);
  }
  const parsed = parseFlags(args, TUNE_FLAGS, "rebalance tune");
  if ("error" in parsed) return { lines: [err(parsed.error), note("  help rebalance")] };
  if (parsed.positional.length > 0) return usage;
  const values = { ...already, ...parsed.values };
  for (const flag of [
    "--from",
    "--tp-pct",
    "--tp-usd",
    "--rebuy-under",
    "--rebuy-above",
    "--ladder-scale",
    "--when",
    "--then",
  ])
    if (flag in values)
      return {
        lines: [
          err(
            flag === "--from"
              ? "rebalance tune does not take --from — it takes --start, --end and --split"
              : flag === "--when" || flag === "--then"
                ? `rebalance tune does not take ${flag} — it switches the ladder, which has no backtest`
                : `rebalance tune does not take ${flag} — the ladder has no backtest`,
          ),
        ],
      };
  const body: Record<string, string> = {};
  for (const [flag, value] of Object.entries(values)) {
    const key = REBALANCE_QUERY[flag];
    if (key) body[key] = value;
  }
  const res = await api.rebalanceTune(body);
  // A tune already running says so, and that `rebalance tune status` follows it.
  if (!res.ok) return { lines: [refusal(res)] };
  return followTune(res.data.tune.id, ctx, res.data.tune);
}

const tuneBar = (done: number, total: number, width = 24) => {
  const full = total > 0 ? Math.round((done / total) * width) : 0;
  return `[${"█".repeat(full)}${"░".repeat(width - full)}]`;
};

/** Wait, unless Escape is pressed first: true when it was. */
const waitOrAbort = (ms: number, signal: AbortSignal | undefined) =>
  new Promise<boolean>((resolve) => {
    if (signal?.aborted) return resolve(true);
    const done = (stopped: boolean) => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      resolve(stopped);
    };
    const onAbort = () => done(true);
    const timer = setTimeout(() => done(false), ms);
    signal?.addEventListener("abort", onAbort);
  });

async function followTune(
  id: string,
  ctx: CommandContext,
  first?: TuneRecordWire,
): Promise<CommandResult> {
  let record = first;
  for (;;) {
    if (!record) {
      const res = await api.rebalanceTuneStatus(id);
      if (!res.ok) return { lines: [refusal(res)] };
      record = res.data.tune;
    }
    const short = record.id.slice(0, 8);
    if (record.status !== "running") {
      ctx.progress?.(
        `tune ${short}: ${record.done} of ${record.total} settings ${tuneBar(record.done, record.total)}`,
      );
      return { lines: tuneReportLines(record) };
    }
    const progress = `tune ${short}: ${record.done} of ${record.total} settings ${tuneBar(record.done, record.total)} · Escape stops following`;
    // Where nothing can show progress, say how to follow it rather than wait.
    if (!ctx.progress)
      return {
        lines: [
          out(`tune ${record.id} running: ${record.done} of ${record.total} settings`),
          note(`  rebalance tune status ${record.id}`),
        ],
      };
    ctx.progress(progress);
    if (await waitOrAbort(TUNE_POLL_MS, ctx.signal))
      return {
        lines: [
          note(`stopped following — the tune keeps running: rebalance tune status ${record.id}`),
        ],
      };
    record = undefined;
  }
}

const SCORE_WORD: Record<string, string> = { return: "return", "return-vol": "return/vol" };
const settingText = (s: TuneRowWire["setting"]) =>
  `top ${s.top} · ${s.lookback} · ${SCORE_WORD[s.score] ?? s.score} · ${s.weight}`;
const pctOr = (x: number | null | undefined) =>
  x === null || x === undefined ? "—" : signedPct(x);
const ordinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

/** The report, best on the analysis period first, the winner and SPY spelled out. */
function tuneReportLines(r: TuneRecordWire): Line[] {
  const head = `tune ${r.id}`;
  if (r.status === "failed") return [err(`${head} failed: ${r.error ?? "no reason recorded"}`)];
  const report = r.report;
  if (!report) return [err(`${head} finished without a report`)];
  const p = report.periods;
  const calmar = report.by === "calmar";
  const stats = (s: TuneStatsWire | null) =>
    `${pad(pctOr(s?.cagr), 9)}${pad(pctOr(s?.max_drawdown), 9)}${calmar ? pad(s ? s.score.toFixed(2) : "—", 7) : ""}`;
  const lines: Line[] = [
    out(
      `${head} — ${r.total} settings, every ${r.request.base.every}${sessionText(r.request.base.session)} at the ${r.request.base.at}, ${r.request.universe}${excludeText(r.request.exclude)}, chosen by ${calmar ? "CAGR over the worst drop" : "CAGR"}`,
    ),
    note(
      `  analysis ${p.analysis.start} → ${p.analysis.end} · test ${p.test.start} → ${p.test.end}`,
    ),
    rowNote(
      `  ${pad("#", 4)}${pad("setting", 36)}${pad("analysis", calmar ? 25 : 18)}${pad("test", calmar ? 25 : 18)}test rank`,
    ),
    rowNote(
      `  ${pad("", 40)}${pad("a year", 9)}${pad("worst", 9)}${calmar ? pad("score", 7) : ""}${pad("a year", 9)}${pad("worst", 9)}${calmar ? pad("score", 7) : ""}`,
    ),
  ];
  const shown = report.rows.slice(0, 12);
  for (const row_ of shown)
    lines.push(
      row(
        `  ${pad(String(row_.analysis_rank), 4)}${pad(settingText(row_.setting), 36)}${stats(row_.analysis)}${stats(row_.test)}${row_.test_rank === null ? "—" : `${row_.test_rank} of ${report.rows.length}`}`,
      ),
    );
  if (report.rows.length > shown.length)
    lines.push(note(`  … ${report.rows.length - shown.length} more, lower on the analysis period`));
  lines.push(
    row(`  ${pad("", 4)}${pad("SPY", 36)}${stats(report.spy.analysis)}${stats(report.spy.test)}`),
  );
  const w = report.winner;
  if (w) {
    lines.push(
      out(
        `winner: ${settingText(w.setting)} — on the test period ${pctOr(w.test?.cagr)} a year against SPY's ${pctOr(report.spy.test?.cagr)}, ${w.test_rank === null ? "unranked" : `${ordinal(w.test_rank)} of ${report.rows.length}`} after the split`,
      ),
      note(`  ${tuneBacktestLine(r.request, w.setting)}  for its whole run`),
    );
  }
  if (report.data.failed > 0)
    lines.push(err(`  history not extended from your feed for ${report.data.failed} symbol(s)`));
  for (const n of report.notes) lines.push(note(`  · ${n}`));
  return lines;
}

async function strategyField(): Promise<CommandResult> {
  const res = await api.agentQualifyField();
  if (!res.ok) return { lines: [refusal(res)] };
  const f = res.data;

  const pct = (v: number | undefined) => (v === undefined ? "—" : `${(v * 100).toFixed(2)}%`);
  const lines: Line[] = [
    note(
      `${f.candidates.length} candidates over ${f.window.sessions} session(s), ` +
        `${f.window.from} → ${f.window.to}, ${f.universe}`,
    ),
    // It is precomputed, so when matters. `ADR-0025`.
    note(
      `  computed ${f.computed_at.slice(0, 19).replace("T", " ")}Z · ${f.candidate_digest_algorithm}`,
    ),
    row(""),
    rowNote(
      `  ${pad("", 4)}${pad("candidate", 17)}${pad("verdict", 23)}${pad("cagr", 9)}${pad("vs SPY", 10)}${pad("drawdown", 10)}`,
    ),
  ];

  for (const c of f.candidates) {
    const rank = c.rank === undefined ? "   " : `#${String(c.rank).padStart(2)}`;
    const line =
      `  ${rank} ${pad(c.candidate, 17)}${pad(c.verdict, 23)}` +
      `${pad(pct(c.score?.cagr), 9)}` +
      `${pad(c.score?.excess_cagr_pp === undefined ? "—" : `${c.score.excess_cagr_pp > 0 ? "+" : ""}${c.score.excess_cagr_pp.toFixed(2)}pp`, 10)}` +
      `${pad(pct(c.score?.max_drawdown), 10)}`;
    lines.push(row(line));
    const why = c.failed.length > 0 ? c.failed.join(", ") : c.causes.join(", ");
    if (why) lines.push(rowNote(`       ${why}`));
  }

  lines.push(row(""));
  if (f.candidates.every((c) => c.rank === undefined)) {
    /*
     * The ordinary outcome today, and it is worth saying rather than leaving as
     * an empty column: every candidate breaches ADR-0017's 15% drawdown
     * ceiling over these folds, SPY included. A reader who does not know that
     * reads a blank rank column as a bug.
     */
    lines.push(
      note("  nothing qualifies — every candidate breaches a hurdle, so nothing is ranked"),
    );
  }
  lines.push(note("  strategy qualify <candidate>  for the folds one verdict was reached over"));
  lines.push(row(""));
  return { lines };
}

async function strategyQualify(candidate = "spy-hold"): Promise<CommandResult> {
  const res = await api.agentQualify(candidate);
  if (!res.ok) return { lines: [refusal(res)] };
  const q = res.data;

  const pct = (v: number | undefined) => (v === undefined ? "—" : `${(v * 100).toFixed(2)}%`);
  const num = (v: number | undefined) => (v === undefined ? "—" : v.toFixed(2));

  const lines: Line[] = [
    note(
      `${q.candidate} over ${q.window.sessions} session(s), ${q.window.from} → ${q.window.to}, ${q.universe}`,
    ),
    note(
      `  ${q.plan.outer_folds} outer fold(s) of ${q.plan.test_sessions}, ` +
        `${q.plan.embargo_sessions}-session embargo, holdout ${q.plan.holdout.from} → ${q.plan.holdout.to} (sealed)`,
    ),
    note(`  holdout seal ${q.holdout_seal.slice(0, 16)}… (sealed, and M6/I4 left it sealed)`),
    row(""),
  ];

  /*
   * The refusal is printed as a refusal, and it is the ordinary outcome for an
   * LLM candidate rather than a fault. Nothing was computed, so nothing below
   * is shown — printing empty hurdles would suggest they had been judged.
   */
  if (!q.eligible) {
    lines.push(note("  NOT ELIGIBLE — nothing was run, and that costs nothing"));
    for (const sentence of q.eligibility_notes) lines.push(note(`    ${sentence}`));
    lines.push(row(""));
    lines.push(note(`  INSUFFICIENT EVIDENCE — ${q.causes.join(", ")}`));
    lines.push(row(""));
    return { lines };
  }

  for (const fold of q.folds) {
    const bad = (fold.score.total_return ?? 0) <= 0;
    /*
     * The fold's OWN seal is printed, not the run's. `ADR-0017` locks before
     * each fold is revealed, and a reader can only check that two folds were
     * sealed separately by seeing two digests — one repeated digest here would
     * be the tell that the narrowing had stopped happening.
     */
    const line =
      `  fold ${fold.index}  ${fold.from} → ${fold.to}  ` +
      `return ${pad(pct(fold.score.total_return), 9)} ` +
      `drawdown ${pad(pct(fold.score.max_drawdown), 9)} ` +
      `cagr ${pad(pct(fold.score.cagr), 9)} ` +
      `seal ${fold.seal.slice(0, 8)}…`;
    lines.push(bad ? err(line) : row(line));
  }
  lines.push(row(""));

  if (q.score) {
    lines.push(
      row(`  ${pad("aggregate", 16)} ${pct(q.score.total_return)} over ${num(q.score.years)}y`),
    );
    lines.push(
      row(`  ${pad("cagr", 16)} ${pct(q.score.cagr)} against SPY ${pct(q.score.benchmark_cagr)}`),
    );
    lines.push(row(`  ${pad("drawdown", 16)} ${pct(q.score.max_drawdown)}`));
    lines.push(row(`  ${pad("execution cost", 16)} $${q.score.execution_cost_usd}`));
    lines.push(row(""));
  }

  for (const h of q.hurdles) {
    const want =
      h.unit === "pp"
        ? `${h.threshold} points`
        : h.unit === "ratio"
          ? `${h.threshold}`
          : pct(h.threshold);
    const got = h.unit === "pp" || h.unit === "ratio" ? num(h.observed) : pct(h.observed);
    const line = `  ${pad(h.code.replace(/_/g, " "), 30)} ${pad(h.result, 8)} wanted ${want}, got ${got}`;
    lines.push(h.result === "pass" ? row(line) : h.result === "fail" ? err(line) : note(line));
  }
  lines.push(row(""));

  if (q.verdict === "qualified_for_m8_review") {
    lines.push(row("  QUALIFIED for M8 investment review — this moves no money"));
  } else if (q.verdict === "rejected") {
    lines.push(err("  REJECTED — see the failed hurdles above"));
  } else {
    lines.push(note(`  INSUFFICIENT EVIDENCE — ${q.causes.join(", ")}`));
  }
  lines.push(row(""));
  return { lines };
}

async function agentBacktest(strategy = "momentum"): Promise<CommandResult> {
  const res = await api.agentBacktest(strategy);
  if (!res.ok) return { lines: [refusal(res)] };
  const b = res.data;

  const pct = (v: number | undefined) => (v === undefined ? "—" : `${(v * 100).toFixed(2)}%`);
  const num = (v: number | undefined) => (v === undefined ? "—" : v.toFixed(2));

  const lines: Line[] = [
    note(
      `${b.strategy} ${b.version} over ${b.window.sessions} session(s), ${b.window.from} → ${b.window.to}`,
    ),
    note(`  ${b.decisions} decision(s), ${b.orders} order(s), ${b.fills} fill(s), ${b.universe}`),
    row(""),
    row(`  ${pad("return", 16)} ${pct(b.score.total_return)}`),
    row(`  ${pad("buy and hold", 16)} ${pct(b.score.benchmark_return)}`),
    row(`  ${pad("excess", 16)} ${pct(b.score.excess_return)}`),
    // Annual, and labelled as such. The figures above are over the whole
    // window; ADR-0017 judges the ones below, and confusing the two is how a
    // 30% total return reads as a 30% year.
    row(`  ${pad("cagr", 16)} ${pct(b.score.cagr)} a year over ${num(b.score.years)}y`),
    row(`  ${pad("spy cagr", 16)} ${pct(b.score.benchmark_cagr)}`),
    row(`  ${pad("excess cagr", 16)} ${num(b.score.excess_cagr_pp)} points a year`),
    row(`  ${pad("max drawdown", 16)} ${pct(b.score.max_drawdown)}`),
    // Two Sortinos differing by about sevenfold, so neither goes unlabelled.
    row(
      `  ${pad("sortino", 16)} ${num(b.score.sortino_annualized)} annualised (${num(b.score.sortino)} per period)`,
    ),
    row(`  ${pad("turnover", 16)} ${num(b.score.turnover)}`),
    row(""),
    row(
      `  ${pad("execution cost", 16)} $${b.score.execution_cost_usd} — spread $${b.score.spread_cost_usd}, slippage $${b.score.slippage_cost_usd}, fees $${b.score.fee_cost_usd}`,
    ),
    row(`  ${pad("under 2x costs", 16)} ${pct(b.score.stressed_total_return)}`),
    row(`  ${pad("model cost", 16)} $${b.cost_usd} (recording cost $${b.recorded_cost_usd}, once)`),
    row(""),
  ];

  // The hurdles, every one, with what it wanted and what it got. A scorecard
  // that printed only the verdict would be asking to be trusted.
  for (const h of b.hurdles) {
    const want =
      h.unit === "pp"
        ? `${h.threshold} points`
        : h.unit === "ratio"
          ? `${h.threshold}`
          : pct(h.threshold);
    const got = h.unit === "pp" || h.unit === "ratio" ? num(h.observed) : pct(h.observed);
    const line = `  ${pad(h.code.replace(/_/g, " "), 30)} ${pad(h.result, 8)} wanted ${want}, got ${got}`;
    lines.push(h.result === "pass" ? row(line) : h.result === "fail" ? err(line) : note(line));
  }
  lines.push(row(""));

  // The verdict last, after its evidence, and never dressed up. `qualified` is
  // the one that opens an M8 review; the other two open nothing.
  if (b.verdict === "qualified_for_m8_review") {
    lines.push(row("  QUALIFIED for M8 investment review — this moves no money"));
  } else if (b.verdict === "rejected") {
    lines.push(err("  REJECTED — see the failed hurdles above"));
  } else {
    lines.push(note(`  INSUFFICIENT EVIDENCE — ${b.causes.join(", ")}`));
    lines.push(
      note(
        "  this is not a rejection: the hurdles above were judged on evidence that is not enough",
      ),
    );
  }
  lines.push(row(""));

  // Said plainly, and said as a failure when it is one. A strategy that did not
  // beat holding is the normal result, and a report that phrased it neutrally
  // would let it slide past.
  if (b.beat_buy_and_hold === undefined) {
    lines.push(note("  no benchmark in this window — nothing to compare against"));
  } else if (b.beat_buy_and_hold) {
    lines.push(row(`  beat buy-and-hold by ${pct(b.score.excess_return)} of RETURN`));
  } else {
    lines.push(
      err(
        `  did NOT beat buy-and-hold — holding the index was ${pct(Math.abs(b.score.excess_return ?? 0))} better`,
      ),
    );
  }

  if (b.unused_answers > 0) {
    lines.push(
      err(
        `  ${b.unused_answers} recorded answer(s) went unused — this golden was made against a different prompt`,
      ),
    );
  }

  return { lines };
}

async function agentList(): Promise<CommandResult> {
  const recent = await api.agentRuns();
  if (!recent.ok) return { lines: [refusal(recent)] };
  if (recent.data.runs.length === 0) {
    return {
      lines: [
        note("no agent has run on this account yet"),
        note(`  agent run ${AGENT_STRATEGIES[0]}`),
      ],
    };
  }

  return {
    lines: [
      rowNote(`  ${pad("STRATEGY", 12)}${pad("STATUS", 11)}${pad("DID", 14)}${pad("COST", 10)}RUN`),
      ...recent.data.runs.map((run) =>
        row(
          `  ${pad(strategyOf(run), 12)}${pad(run.status, 11)}` +
            `${pad(didOf(run), 14)}${pad(costOf(run), 10)}${run.id.slice(0, 8)}`,
        ),
      ),
    ],
  };
}

/** `trading:momentum` → `momentum`. The pair identifies a competitor; the half names it. */
const strategyOf = (run: AgentRun): string => run.agent_key.split(":")[1] ?? run.agent_key;

/**
 * What the run did, in the vocabulary of what it was doing.
 *
 * The columns are `effects_applied` and `effects_blocked` because the table
 * holds the runtime's facts; rendering them as orders or as insights is this
 * function's job and nobody else's. Renaming the *columns* per domain is what
 * `M5/I2/T12` deliberately stopped doing.
 *
 * The ordered outcomes distinguish a material effect from a recorded no-op.
 * That makes "orders" true for trading without teaching the generic run store
 * any trading vocabulary, and keeps insight packs honest on the same fields.
 */
function didOf(run: AgentRun): string {
  const material = run.outcomes.filter(
    (outcome) => outcome.status === "applied" && outcome.impact === "material",
  ).length;
  const noop = run.outcomes.filter(
    (outcome) => outcome.status === "applied" && outcome.impact === "none",
  ).length;
  const noun = run.domain === "portfolio-insight" ? "insight" : "order";
  const parts = [`${material} ${noun}${material === 1 ? "" : "s"}`];
  if (noop > 0) parts.push(`${noop} held`);
  if (run.effects_blocked > 0) parts.push(`${run.effects_blocked} blocked`);
  return parts.join(", ");
}

/**
 * The bill, or a dash.
 *
 * A dash rather than `$0.00` when nothing was spent, because those are
 * different facts: a run refused before its model call has no model and no
 * cost, and printing a zero would make "it was halted" look like "it was free".
 */
const costOf = (run: AgentRun): string =>
  run.model === null ? "—" : `$${formatSpendUsd(run.cost_usd)}`;

function runLines(detail: AgentRunDetail): Line[] {
  const { run, insights: recorded } = detail;
  const lines: Line[] = [];

  lines.push(row(`  ${pad(strategyOf(run), 12)}${pad(run.status, 11)}${didOf(run)}`));

  /*
   * **`blocked_reason` is printed verbatim and `error` is not the same field.**
   * One is a sentence a person is meant to read and act on; the other is a
   * diagnostic. Putting both in one line makes the trader's message and the
   * operator's stack trace compete, which is why the columns are separate.
   */
  if (run.blocked_reason) lines.push(err(run.blocked_reason));
  if (run.error) lines.push(err(run.error));

  /*
   * The question, verbatim, and what to type.
   *
   * `question` is a sentence the server wrote — the same rule `blocked_reason`
   * follows one line above. Assembling one here from `detail` would mean
   * parsing the server's meaning out of its data, and the terminal's whole
   * discipline is that it prints what it is given.
   */
  if (run.approval) {
    lines.push(out(`  ${run.approval.question}`));
    lines.push(note(`  agent approve ${run.id}`));
    lines.push(note(`  agent reject ${run.id} [why]`));
    lines.push(note("  nothing is placed while it waits"));
  }

  for (const outcome of run.outcomes) {
    const detail = outcome.detail ?? {};
    const symbol = typeof detail.symbol === "string" ? ` ${detail.symbol}` : "";
    if (outcome.status === "blocked") {
      lines.push(
        err(`  blocked ${outcome.name}${symbol}: ${outcome.reason ?? outcome.code ?? "refused"}`),
      );
    } else if (outcome.impact === "none") {
      lines.push(
        note(
          `  held${symbol}${typeof detail.rationale === "string" ? ` — ${detail.rationale}` : ""}`,
        ),
      );
    } else {
      lines.push(out(`  applied ${outcome.name}${symbol}`));
    }
  }

  for (const insight of recorded) {
    lines.push(out(`  ${insight.title}`));
    lines.push(note(`    ${insight.body_md}`));
  }

  if (run.model) {
    lines.push(
      rowNote(
        `  ${run.model} · ${run.input_tokens} in / ${run.output_tokens} out · $${formatUsd(run.cost_usd)}`,
      ),
    );
  }

  // A finished run that applied nothing and blocked nothing did look and decide
  // not to act, which is a real answer and worth saying out loud — otherwise the
  // terminal prints one status line and reads as though something went wrong.
  if (run.status === "succeeded" && run.effects_applied === 0 && run.effects_blocked === 0) {
    lines.push(note("it looked, and decided to do nothing"));
  }

  return lines;
}

const CONSENT = "agree";

async function acceptAgreement(answer: string, ctx: CommandContext): Promise<CommandResult> {
  if (answer.trim().toLowerCase() !== CONSENT) {
    return {
      lines: [note(`not accepted — type \`${CONSENT}\` to accept, or \`logout\` to leave.`)],
      // Re-asked rather than dropped, exactly like a mistyped sign-in code. The
      // alternative is a session that can do nothing with no visible reason.
      prompt: { label: `${CONSENT}?`, onAnswer: (again) => acceptAgreement(again, ctx) },
    };
  }

  const accepted = await api.acceptAgreement();
  if (!accepted.ok) return { lines: [err(`could not record that: ${accepted.error}`)] };

  ctx.setSession({ agreementRequired: false });
  ctx.refresh();
  return {
    lines: [
      out("accepted — thank you."),
      note("run `account` to open or inspect your paper account"),
    ],
  };
}

/**
 * `setup` and `theme` are the same command over two catalogues.
 *
 * Written once because the difference between them is a noun. Listing marks the
 * current one with `*` rather than colour alone, so the answer to "which am I
 * wearing" does not depend on being able to see a hue.
 */
function chooseLook(
  wanted: string | undefined,
  options: readonly { id: string; title: string; current: boolean }[],
  choose: (id: string) => boolean,
  noun: string,
): CommandResult {
  const id = wanted?.trim().toLowerCase();

  if (!id) {
    return {
      lines: [
        note(`${noun}s`),
        ...options.map((o) =>
          (o.current ? row : rowNote)(`  ${o.current ? "*" : " "} ${pad(o.id, 12)}${o.title}`),
        ),
        note(""),
        note(`\`${noun} <id>\` to switch — the choice survives a reload`),
      ],
    };
  }

  if (!choose(id)) {
    return {
      lines: [
        err(`unknown ${noun}: ${id}`),
        note(`try one of: ${options.map((o) => o.id).join(", ")}`),
      ],
    };
  }

  return { lines: [out(`${noun} ${id}`)] };
}

export type ParsedOrder = {
  qty: string;
  symbol: string;
  type: "market" | "limit" | "stop" | "stop_limit";
  limitPrice?: string;
  stopPrice?: string;
  timeInForce?: string;
};

/**
 * One market, as lines.
 *
 * **Every word printed here came off the response.** The state, the reason, the
 * next transition — the terminal does not own a calendar, a timezone or an
 * opinion about what "closed" means, and if it did, PRODUCT.md's rule would be
 * broken in the one place it is hardest to notice: a UI that computes market
 * hours looks right until the day it disagrees with the engine that fills
 * orders.
 */
export function marketLines(
  m: MarketSummary,
  options: { verbose?: boolean } = {},
): ReturnType<typeof out>[] {
  const badge = m.state === "open" ? "●" : m.state === "closed" ? "○" : "?";
  const when =
    m.state === "open"
      ? m.closes_at
        ? `until ${venueDateTime(m.closes_at, m.time_zone)}`
        : ""
      : m.opens_at
        ? `opens ${venueDateTime(m.opens_at, m.time_zone)}`
        : "";

  const head = row(
    `${badge} ${m.id.padEnd(4)} ${m.state.padEnd(7)} ${when}${
      m.state === "closed" && m.reason ? `  (${m.reason.replace(/_/g, " ")})` : ""
    }`.trimEnd(),
  );

  if (!options.verbose) return [head];

  return [
    head,
    out(`  ${m.name} · ${m.currency} · ${m.time_zone} · local date ${m.local_date}`),
    // Said out loud rather than left to be discovered: a venue nothing can
    // price is a venue you should not be trading, and `unknown` past the
    // horizon is not the same answer as `closed`.
    // Names the feed AND says what sort of prices it sells, since `M4/E30`.
    // "live prices" alone stopped being enough the day a trader could choose
    // between two of them.
    m.priced_by
      ? note(
          `  ${m.priced_by} (${m.priced_by_kind ?? "unknown"}) prices, ${
            m.realtime ? "in real time" : `delayed ${m.quote_delay_minutes} minutes`
          }`,
        )
      : note("  nothing in this deployment can price it"),
    note(`  calendar runs through ${m.calendar_through}`),
  ];
}

export type ParsedReplay = { date: string; speed: number; symbols: string[] };

/**
 * `replay 2026-07-15 --speed 60 NVDA AAPL` → what to ask the replay API for.
 *
 * `--speed` is the first flag-shaped argument in the terminal; everything else
 * is positional. It earns the shape because a bare number after a date would be
 * ambiguous with a symbol, and because speed is genuinely optional in a way
 * `qty` never is.
 *
 * An unrecognised token is refused rather than dropped, matching
 * `parseOrderArgs`: silently ignoring a typo would replay a different day, at a
 * different speed, and report success.
 */
export function parseReplayArgs(args: string[]): ParsedReplay | { error: string } {
  const [date, ...rest] = args;
  if (!date) return { error: "usage: replay <date> [--speed <n>] [SYMBOL …]" };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return { error: `"${date}" is not a date — try \`replay 2026-07-15\`` };
  }

  let speed = 60;
  const symbols: string[] = [];

  for (let i = 0; i < rest.length; i++) {
    const token = rest[i] as string;

    if (token === "--speed") {
      const value = rest[i + 1];
      if (!value || !/^\d+(\.\d+)?$/.test(value)) {
        return { error: "--speed needs a number — try `--speed 60`" };
      }
      speed = Number(value);
      if (speed <= 0 || speed > MAX_REPLAY_SPEED) {
        return { error: `--speed must be between 0 and ${MAX_REPLAY_SPEED}` };
      }
      i++;
      continue;
    }

    if (/^[A-Za-z][A-Za-z.-]*$/.test(token)) {
      symbols.push(token.toUpperCase());
      continue;
    }

    return { error: `don't understand "${token}" — try \`--speed 60\` or a symbol` };
  }

  return { date, speed, symbols };
}

/**
 * `buy 10 AAPL limit 250 gtc` → an order request.
 *
 * Quantity first, because that is how a trader says it out loud, and the price
 * qualifiers read the same way: "limit two fifty". Nothing here decides what an
 * order costs or whether it can be afforded — this turns words into the request
 * body the API already accepts, and the API answers every other question.
 */
export function parseOrderArgs(args: string[]): ParsedOrder | { error: string } {
  const [rawQty, rawSymbol, ...rest] = args;
  if (!rawQty || !rawSymbol) return { error: "usage: <side> <qty> <symbol> [limit <price>]" };
  if (!/^\d+(\.\d+)?$/.test(rawQty)) {
    return { error: `"${rawQty}" is not a quantity — try \`10 ${rawSymbol.toUpperCase()}\`` };
  }

  let limitPrice: string | undefined;
  let stopPrice: string | undefined;
  let timeInForce: string | undefined;

  for (let i = 0; i < rest.length; i++) {
    const token = (rest[i] as string).toLowerCase();

    if (token === "limit" || token === "stop") {
      const value = rest[i + 1];
      if (!value || !/^\d+(\.\d+)?$/.test(value)) {
        return { error: `${token} needs a price — try \`${token} 250\`` };
      }
      if (token === "limit") limitPrice = value;
      else stopPrice = value;
      i++;
      continue;
    }

    if ((TIF as readonly string[]).includes(token)) {
      timeInForce = token;
      continue;
    }

    return { error: `don't understand "${rest[i]}" — try \`limit 250\`, \`stop 260\`, or \`gtc\`` };
  }

  const type =
    limitPrice && stopPrice ? "stop_limit" : limitPrice ? "limit" : stopPrice ? "stop" : "market";

  // The server refuses this too. Saying so here costs no round trip and names
  // the thing the user typed rather than a field in a request body they never
  // wrote: a market order has no price to rest against, so `gtc` on one would
  // mean "wait forever for nothing in particular".
  if (type === "market" && timeInForce === "gtc") {
    return { error: "a market order cannot be gtc — it fills now or not at all" };
  }

  return {
    qty: rawQty,
    symbol: rawSymbol.toUpperCase(),
    type,
    ...(limitPrice ? { limitPrice } : {}),
    ...(stopPrice ? { stopPrice } : {}),
    ...(timeInForce ? { timeInForce } : {}),
  };
}

/** `limit 250.00` / `stop 260 limit 261` / `market` — what an order actually is. */
/**
 * An order's short id, marked when an agent placed it. `M5/E10`.
 *
 * **A leading `*` rather than a column**, because the table is already at the
 * eighty columns the terminal is drawn in: `id` 10, `side` 6, `qty` 10,
 * `symbol` 8, `order` 22, `status` 11, then the price. A four-wide `by` column
 * takes it past the edge, and shrinking `order` starts truncating
 * `stop_limit 190.25/188.00 gtc`.
 *
 * The id column holds an eight-character slice in a ten-wide cell, so there is
 * exactly one spare character and this is what it is for. `pad` uses `padEnd`,
 * so a nine-character cell still aligns.
 *
 * The marker says *that* an agent placed it; the line printed underneath says
 * which run. Neither claims the other: `submitted_by` is not a synonym for
 * having a run id — `system` is neither a person nor a run.
 */
function orderTag(o: Order): string {
  return `${o.submitted_by === "agent" ? "*" : ""}${o.id.slice(0, 8)}`;
}

function describeTerms(o: Order): string {
  const parts: string[] = [];
  if (o.stop_price) parts.push(`stop ${formatUsd(o.stop_price)}`);
  if (o.limit_price) parts.push(`limit ${formatUsd(o.limit_price)}`);
  if (parts.length === 0) parts.push("market");
  if (o.time_in_force && o.time_in_force !== "day") parts.push(o.time_in_force);
  return parts.join(" ");
}

function describeOrder(o: Order): string {
  return `${o.side} ${trimQty(o.qty)} ${o.symbol} ${describeTerms(o)}`;
}

/**
 * Place an order: at the market, or resting until the market comes to it.
 *
 * The API decides which of those happened — a limit priced through the spread
 * fills at once, and one below it waits. The terminal reports what came back
 * rather than predicting it, because predicting it would mean holding a second
 * copy of the venue's rules here.
 */
async function trade(
  side: "buy" | "sell",
  args: string[],
  ctx: CommandContext,
): Promise<CommandResult> {
  const parsed = parseOrderArgs(args);
  if ("error" in parsed) {
    return { lines: [err(parsed.error.replace("<side>", side))] };
  }

  const accountId = await resolveAccount(ctx);
  if (typeof accountId !== "string") return accountId;

  const res = await api.placeOrder({
    account_id: accountId,
    symbol: parsed.symbol,
    side,
    qty: parsed.qty,
    type: parsed.type,
    ...(parsed.limitPrice ? { limit_price: parsed.limitPrice } : {}),
    ...(parsed.stopPrice ? { stop_price: parsed.stopPrice } : {}),
    ...(parsed.timeInForce ? { time_in_force: parsed.timeInForce } : {}),
  });

  if (!res.ok) {
    // A refusal is not an error in the terminal's sense — the system worked and
    // said no. It reads as one line with the reason, not a stack of noise.
    return { lines: [refusal(res)] };
  }

  ctx.refresh();
  const { order, fill } = res.data;
  const replay = res.data.idempotent ? [note("(replay of an order already placed)")] : [];

  if (fill) {
    return {
      lines: [
        out(
          `✓ ${order.id.slice(0, 8)} ${side === "buy" ? "bought" : "sold"} ${trimQty(fill.qty)} ${order.symbol} @ ${formatUsd(fill.price)}`,
        ),
        ...mirrorNote(order),
        ...replay,
      ],
    };
  }

  if (order.status === "canceled") {
    return {
      lines: [
        err(`✗ ${order.id.slice(0, 8)} ${order.time_in_force} order did not fill and was canceled`),
        ...(res.data.reason ? [note(`  ${res.data.reason}`)] : []),
        ...replay,
      ],
    };
  }

  return {
    lines: [
      out(`⏳ ${order.id.slice(0, 8)} resting — ${describeOrder(order)}`),
      note("it will fill by itself when the price crosses; `cancel` it to stop waiting"),
      ...mirrorNote(order),
      ...replay,
    ],
  };
}

/** The account to act on: the session's, or the user's first, opening one if needed. */
async function resolveAccount(ctx: CommandContext): Promise<string | CommandResult> {
  if (ctx.session.accountId) return ctx.session.accountId;

  const list = await api.listAccounts();
  if (!list.ok) {
    return {
      lines: [
        err(list.status === 401 ? "not signed in — run `login <email>`" : `error: ${list.error}`),
      ],
    };
  }

  const selected =
    list.data.selected_account_id ??
    (!("selected_account_id" in list.data) || list.data.accounts.length === 1
      ? list.data.accounts[0]?.id
      : undefined);
  if (!selected)
    return { lines: [err("select an account first — run `accounts` or `account <name>`")] };
  ctx.setSession({
    accountId: selected,
    accountName: list.data.accounts.find((a) => a.id === selected)?.name,
  });
  return selected;
}

/** `10.00000000` → `10`. Trailing zeros on a share count are noise. */
function trimQty(decimal: string): string {
  return decimal.includes(".") ? decimal.replace(/\.?0+$/, "") : decimal;
}

/** Decimal string subtraction, exact — the same reason `formatUsd` exists. */
function subtractDecimals(a: string, b: string): string {
  const scale = 4;
  const toInt = (s: string) => {
    const [whole = "0", frac = ""] = s.split(".");
    return BigInt(whole + frac.padEnd(scale, "0").slice(0, scale));
  };
  const diff = toInt(a) - toInt(b);
  const negative = diff < 0n;
  const magnitude = (negative ? -diff : diff).toString().padStart(scale + 1, "0");
  const whole = magnitude.slice(0, -scale);
  return `${negative ? "-" : ""}${whole}.${magnitude.slice(-scale)}`;
}

/** `+12.40` / `-3.10`, so a gain and a loss are distinguishable at a glance. */
function signed(decimal: string): string {
  return decimal.startsWith("-") ? `-${formatUsd(decimal.slice(1))}` : `+${formatUsd(decimal)}`;
}

function renderAccount(a: Account): Line[] {
  return [
    row(`  ${pad("cash", 10)}${formatUsd(a.cash)}`),
    row(`  ${pad("equity", 10)}${formatUsd(a.equity)}`),
    ...(a.market_value && a.market_value !== "0.0000"
      ? [row(`  ${pad("holdings", 10)}${formatUsd(a.market_value)} across ${a.positions} symbols`)]
      : []),
    rowNote(`  ${pad("mode", 10)}${a.mode}`),
    rowNote(`  ${pad("account", 10)}${a.name}`),
    rowNote(`  ${pad("id", 10)}${a.id}`),
    // Surfaced deliberately: it is the evidence that the balance above is
    // derived from a ledger rather than stored in a column.
    rowNote(`  ${pad("ledger", 10)}${a.ledger_entries} entries`),
    // The two figures differ for as long as a fill is in flight to Postgres.
    // Showing it is more honest than hiding a queue behind a spinner.
    ...(a.ledger_cash && a.ledger_cash !== a.cash
      ? [rowNote(`  ${pad("settling", 10)}ledger shows ${formatUsd(a.ledger_cash)}, catching up`)]
      : []),
  ];
}

/**
 * Every command, in the order a reader can use.
 *
 * Sorted rather than declared: `help` is a reference for twenty commands, and
 * source order is an order only the author can see. It also decides which
 * command a prefix resolves to — the "did you mean" suggestion and Tab both
 * read this list — so leaving it as source order made those depend on when a
 * command happened to be written, which no test pins and no user can predict.
 */
export const COMMAND_NAMES = Object.keys(COMMANDS).sort();

/** What Tab may offer after a command and the tokens typed so far — see `spec.ts`. */
export function optionsForCommand(
  command: string,
  typed: readonly string[],
  partial: string,
  shell: ShellSets,
): { words: readonly string[]; hint?: string } {
  const doc = DOCS[command.toLowerCase()];
  return doc ? optionsFor(doc, typed, partial, shell) : { words: [] };
}

function unknownCommand(name: string): CommandResult {
  const guesses = suggest(name, COMMAND_NAMES);
  return {
    lines: [
      err(`unknown command: ${name}`),
      guesses.length > 0
        ? note(`did you mean ${guesses.map((g) => `\`${g}\``).join(" or ")}?`)
        : note("run `help` for commands"),
    ],
  };
}

/** One command's page, rendered from its declaration. */
function helpFor(name: string): CommandResult {
  const key = name.toLowerCase();
  const doc = DOCS[key];
  return doc ? { lines: renderHelpPage(key, doc) } : unknownCommand(name);
}

export async function runCommand(input: string, ctx: CommandContext): Promise<CommandResult> {
  const trimmed = input.trim();
  if (!trimmed) return { lines: [] };

  const [name = "", ...args] = trimmed.split(/\s+/);
  const key = name.toLowerCase();
  const command = COMMAND_TABLE[key];
  if (!command) return unknownCommand(name);

  // `dca --help` and `dca help` are `help dca`: the page is where a trader
  // who half-remembers a command goes, and they should not need to know which
  // way round to ask.
  const first = args[0]?.toLowerCase();
  if (first === "--help" || (key !== "help" && first === "help" && args.length === 1)) {
    return helpFor(key);
  }
  return command.run(args, ctx);
}

/** `clear` is handled by the shell rather than by producing output. */
export const isClear = (input: string) => input.trim().toLowerCase() === "clear";
